import { count, sentence, whom } from './core/words.js';
import { createStage } from './core/stage.js';
import { createPost } from './core/post.js';
import { createLook } from './world/look/look.js';
import { createLoop } from './core/loop.js';
import { createInput } from './core/input.js';
import { behindBody, createCameraRig } from './core/camera.js';
import { createCharacter } from './core/character.js';
import { ft, toFeet } from './core/units.js';
import { penetrates, segmentClear } from './core/collision.js';
import { buildLevel, levelSolids } from './world/build.js';
import { createAvatar } from './world/avatar.js';
import { createEngine } from './audio/engine.js';
import { scoreFor } from './audio/music.js';
import { createSoundboard } from './audio/soundboard.js';
import { MINUTES_PER_DAY, createClock, formatSpan, formatTime, parseTime } from './sim/clock.js';
import { createShiftTracker } from './sim/shift.js';
import { createInteraction } from './sim/interaction.js';
import { CEILING, belowRung, createStanding, rungFor } from './sim/standing.js';
import { createBeoBoard, timesOf } from './sim/beo.js';
import { JOBS } from './data/jobs.js';
import { boardLoad } from './sim/beoGen.js';
import { createCarry } from './sim/carry.js';
import { createWheeled } from './sim/wheeled.js';
import { createClimbing } from './sim/climbing.js';
import { createPerception } from './sim/perception.js';
import { createConcealment } from './sim/concealment.js';
import { HOLLOWS } from './data/hollows.js';
import { CONSUMABLES } from './data/consumables.js';
import { createImpairment } from './sim/impairment.js';
import { createChatter } from './sim/chatter.js';
import { createClaims } from './sim/claims.js';
import { createAccess } from './sim/access.js';
import { createWildlife } from './sim/wildlife.js';
import { createWitnesses } from './sim/witnesses.js';
import { createActionKey } from './sim/actionKey.js';
import { createFrameInput } from './sim/frameInput.js';
import { createEscalation } from './sim/escalation.js';
import { createDeals } from './sim/deals.js';
import { createConversation } from './sim/conversation.js';
import { createManagerWarning } from './sim/managerWarning.js';
import { createCharm } from './sim/charm.js';
import { createBodyOffers } from './sim/bodyOffers.js';
import { createObjective } from './sim/objective.js';
import { createCartOffers } from './sim/cartOffers.js';
import { createPocket } from './sim/pocket.js';
import { createOpening } from './sim/opening.js';
import { createRiders } from './sim/riders.js';
import { cardFor as teardownCard, createTeardown } from './sim/teardown.js';
import { TEARDOWNS } from './data/teardowns.js';
import { createDinners } from './sim/dinner.js';
import { createSearches } from './sim/search.js';
import { createRescues } from './sim/rescue.js';
import { createShuttles } from './sim/shuttle.js';
import { createBlockades } from './sim/blockade.js';
import { createDeliveries } from './sim/delivery.js';
import { createPoolSets } from './sim/poolset.js';
import { createIndividuals } from './sim/individual.js';
import { createIndividualView } from './world/individualView.js';
import { WILDLIFE } from './data/wildlife.js';
import { ACCESS_IDS } from './data/access.js';
import { ACCUSING, CALM, createSuspicion } from './sim/suspicion.js';
import { createNoticed } from './sim/noticed.js';
import { createSetup } from './sim/setup.js';
import { resolvePlacement, resolveStrike } from './sim/placement.js';
import { awardFor, gradeSetup, letterFor, wantedAnchors } from './sim/grading.js';
import {
  REASONS,
  createWriteups,
  describe as describeWriteup,
  penaltyFor,
} from './sim/writeups.js';
import { createNpc } from './sim/npc.js';
import { createAutosave, createSave } from './sim/save.js';
import { generateDay } from './sim/beoGen.js';
import { createStores } from './sim/stores.js';
import { createTasks, ERRAND_REACH } from './sim/tasks.js';
import { plannedKeys, snapTarget } from './sim/snap.js';
import { roomNeeds } from './sim/roomNeeds.js';
import { createBreaks } from './sim/breaks.js';
import { createCrewTalk } from './sim/crewTalk.js';
import { JOB_SHARE, cardFor as jobCard, createJobs } from './sim/jobs.js';
import { createTutorial } from './sim/tutorial.js';
import { createRouter, pathLength } from './core/route.js';
import { createRouteView } from './world/routeView.js';
import { createHighlightView } from './world/highlightView.js';
import { createFavors } from './sim/favors.js';
import { createProcurement, errandFor, optionsFor } from './sim/procurement.js';
import {
  CART,
  FIXED,
  LITTER,
  NEAR,
  PLACING,
  POSSIBLE,
  TALKING,
  describe,
  line,
  AIM,
  perform,
  route,
} from './sim/prompts.js';
import { onDutyAt, weekdayOf, windowCovers } from './sim/npc.js';
import { createRng, hashSeed } from './sim/rng.js';
import { createBus, EVENT } from './sim/events.js';
import { createEntities } from './sim/entities.js';
import { createThrowing } from './sim/throwing.js';
import { scatterInto } from './sim/scatter.js';
import { FIRST_ACT_EASE, createGhosts } from './sim/ghosts.js';
import { createJournal } from './sim/journal.js';
import { createChapters } from './sim/chapters.js';
import { createStory } from './sim/story.js';
import { createClasses } from './sim/classes.js';
import { createPranks } from './sim/pranks.js';
import { byId as PRANKS_BY_ID } from './data/pranks.js';
import { createPrankOffers } from './sim/prankOffers.js';
import { createPerks } from './sim/perks.js';
import { CLASS_MINUTES } from './data/classes.js';
import { createSetupView } from './world/setupView.js';
import { createCartView } from './world/cartView.js';
import { createWildlifeView } from './world/wildlifeView.js';
import { createEntityView } from './world/entityView.js';
import { createNpcView } from './world/npcView.js';
import { createHud } from './ui/hud.js';
import { createMinimap } from './ui/minimap.js';
import { createControlsCard } from './ui/controlsCard.js';
import { createHelpCard } from './ui/helpCard.js';
import { createDebugPanel } from './ui/debugPanel.js';
import { createBeoPanel } from './ui/beoPanel.js';
import { createClassPanel } from './ui/classPanel.js';
import { createDialogue } from './ui/dialogue.js';
import { createSheet } from './ui/sheet.js';
import { createGradePanel } from './ui/gradePanel.js';
import { createShiftSummary } from './ui/shiftSummary.js';
import { createChapterPanel } from './ui/chapterPanel.js';
import { createChoicePanel } from './ui/choicePanel.js';
import { createVeil } from './ui/veil.js';
import { PAD_ROWS, createMenus } from './ui/menus.js';
import { createPadNav, scrollerOf } from './ui/padNav.js';
import { currentDevice, relabel, setDevice } from './ui/glyphs.js';
import { SCROLL_SPEED } from './core/gamepad.js';
import { createSettings } from './core/settings.js';
import { createCreation } from './ui/creation.js';
import bindings from './core/bindings.json';
import feel from './core/feel.json';
import { LEVEL } from './data/level.js';
import { INTERACTABLES } from './data/interactables.js';
import { shiftForDay } from './data/schedule.js';
import { DEPARTMENTS, byId as departmentsById } from './data/departments.js';
import { RIVALRY } from './data/rivalry.js';
import { FIRST_BEO, REFERENCE_BEO } from './data/beos.js';
import { WHEELED } from './data/wheeled.js';
import { FAVOR_WINDOW as FAVOR_MINUTES } from './sim/favors.js';
import { PLACES, SHORTFALLS, placeFor } from './data/resources.js';
import { DINNERS_BY_ID } from './data/dinners.js';
import { RESCUES_BY_ID } from './data/rescues.js';
import { BLOCKADES_BY_ID } from './data/blockades.js';
import { LINENS, OBJECTS } from './data/objects.js';
import { CREW, RENAMED_CREW, STAFF, STAFF_BY_ID, STARTING_STANDING, gateFor, gatekeepersFor } from './data/staff.js';
import { GUESTS } from './data/guests.js';
import { VISITORS } from './data/visitors.js';
import { ENDINGS, REPORTED, SAW_IT, SCENES, WIN, WRONGED } from './data/story.js';
import { BODIES } from './data/bodies.js';
import { createIdentity } from './sim/identity.js';
import { createClothing, describeClothing } from './sim/clothing.js';

const canvas = document.getElementById('viewport');
const ui = document.getElementById('ui');

// --- world -------------------------------------------------------------------

const level = buildLevel(LEVEL);
const rig = createCameraRig({ world: level.world });
const stage = createStage(canvas, rig.camera);
stage.scene.add(level.object3D);
/*
 * The look of the building: surfaces, light, sky. Handed a plain copy of the
 * level's description, and each drawn frame a reading of numbers; it holds
 * nothing it could change the game through. `?look=0` boots the greybox, which
 * is how `scripts/look.mjs` checks that a shift plays the same either way.
 */
const look = new URLSearchParams(window.location.search).get('look') === '0'
  ? null
  : createLook({ scene: stage.scene, renderer: stage.renderer, lights: stage.lights, levelObject: level.object3D, plan: structuredClone(LEVEL) });

/*
 * The finish on the frame: occlusion, glow, a grade per zone. Only over the
 * look (the greybox is a measuring instrument and stays flat), and on a
 * software rasteriser it starts without the occlusion, which is the costly
 * part. A real machine that cannot hold the frame drops it too (`draw`).
 */
const post = look
  ? (() => {
      try {
        const gl = stage.renderer.getContext();
        const info = gl.getExtension('WEBGL_debug_renderer_info');
        const name = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : '';
        const soft = /swiftshader|llvmpipe|software/i.test(name);
        const made = createPost({ renderer: stage.renderer, scene: stage.scene, camera: rig.camera, quality: soft ? 'low' : 'high' });
        const fit = () => made.setSize(window.innerWidth, window.innerHeight);
        fit();
        window.addEventListener('resize', fit);
        return made;
      } catch (error) {
        console.info(`[lion&crown] drawing without post: ${error?.message ?? error}`);
        return null;
      }
    })()
  : null;

/**
 * A fade to black and back, over everything: the scene, the HUD, the lot.
 * For the moments that should not be a cut: a new day, sleeping, a
 * suspension, a chapter beat. A reading of what already happened; nothing
 * waits on it and the world does not stop for it.
 */
const fader = (() => {
  const root = document.createElement('div');
  root.className = 'fader';
  ui.append(root);
  let level = 0;
  let holdFor = 0;
  let rate = 1.4;
  return {
    /** Black at once, held a moment, then up over `seconds`. */
    flash({ hold = 0.25, seconds = 0.9 } = {}) {
      level = 1;
      holdFor = hold;
      rate = 1 / Math.max(0.05, seconds);
    },
    /** Dims to `to` and stays, until `flash` or `clear`. Behind a panel. */
    frame(dt) {
      if (holdFor > 0) holdFor -= dt;
      else level = Math.max(0, level - rate * dt);
      const shown = level.toFixed(3);
      if (root.style.opacity !== shown) root.style.opacity = shown;
    },
    /** Straight to clear, for a screenshot that wants the frame and not the fade. */
    clear() {
      level = 0;
      holdFor = 0;
      root.style.opacity = '0';
    },
    get level() {
      return level;
    },
  };
})();

const player = createCharacter({
  world: level.world,
  // Spelled out rather than spread: the spawn also carries a yaw, and a body's
  // position is three numbers.
  position: { x: level.spawn.x, y: level.spawn.y, z: level.spawn.z },
});
player.yaw = level.spawn.yaw;
rig.yaw = behindBody(level.spawn.yaw);

const avatar = createAvatar();
stage.scene.add(avatar.object3D);

/**
 * Who you are and what you have got on.
 *
 * Two small modules rather than one, because they answer to different things:
 * the identity is set once at the hiring screen and never moves again, and the
 * clothing slot moves every time you stand at your locker. What they share is
 * the avatar — the body's colour is the one and only place the two meet, and
 * `applyLook` below is that place.
 */
const identity = createIdentity({ bodies: BODIES });
const clothing = createClothing();

/**
 * The look, which is the whole of what a preset buys.
 *
 * Blacks when you are in uniform — the spare included, because a spare that
 * looked different would be a fourth state the rules do not have — and your own
 * colour when you are not. The wedge stays the preset's accent either way: it
 * is the facing cue, and a facing cue that changes meaning with your trousers
 * is not a cue.
 */
function applyLook() {
  const body = identity.body;
  avatar.setColor(clothing.isUniform ? body.blacks : body.civvies);
  avatar.setAccent(body.accent);
}

identity.onChange(applyLook);
clothing.onChange(applyLook);
applyLook();

// --- systems -----------------------------------------------------------------

// Day 1 starts an hour and a half before the PM shift, in the break room. Early
// enough to walk the property, late enough that the clock is already a question.
const clock = createClock({ day: 1, minutes: 12 * 60 + 30, scale: 1 });
const shift = createShiftTracker({ schedule: shiftForDay });
const interaction = createInteraction({ level, interactables: INTERACTABLES });

/**
 * The middle of a prop, in metres. For anything that wants to stand near one.
 *
 * `interaction.js` already does this for the things that carry a prompt of
 * their own; a prank stock point does not, because the prompt it carries is
 * "take a handful of ice" and that belongs in the offer list with everything
 * else the key could do. So the geometry is looked up here instead, once.
 */
const PROP_CENTRES = new Map(
  LEVEL.props.map((prop) => {
    const [x0, z0, x1, z1] = prop.rect;
    return [
      prop.id,
      {
        x: ft((x0 + x1) / 2),
        y: ft(prop.base ?? 0),
        z: ft((z0 + z1) / 2),
        /*
         * And how big it is, because reach is measured from a prop's *edge*
         * and not from its middle — the same rule `interaction.js` keeps, and
         * for the same reason: a twelve-foot shelf is usable from anywhere
         * along it, and measuring to the centre makes half of it out of reach
         * while you are standing against it.
         */
        halfX: ft(Math.abs(x1 - x0) / 2),
        halfZ: ft(Math.abs(z1 - z0) / 2),
      },
    ];
  })
);
const propAt = (id) => PROP_CENTRES.get(id) ?? null;
/**
 * Where a new hire stands with all seven, which is not the same as nothing.
 *
 * Six of them have never heard of you. One has: Housekeeping's floor lead has
 * watched a hundred first weeks and starts you half way up, which is why the
 * shortest fetch in the property is short on day one. The number lives on her
 * row in `staff.js` beside the reason for it, and this line is the only place
 * that reads it.
 */
const standing = createStanding({
  departments: DEPARTMENTS,
  rivalry: RIVALRY,
  initial: STARTING_STANDING,
});
/**
 * The run seed.
 *
 * Every rolled thing in the game hangs off this, so it is generated once and
 * then saved. Reloading has to give back the same board, the same shortfalls
 * and the same called-in favours; a save that restores the day but re-rolls its
 * contents changes the game underneath the player.
 *
 * `?seed=` pins it. The headless scripts deliberately do *not* pin it by
 * default — they run a different arrangement of the world every time, and that
 * is how the prop that quietly ate the cart handle press was found at all. They
 * report the seed instead, so a failure can be replayed exactly.
 */
let runSeed = new URL(window.location.href).searchParams.get('seed')
  ?? `run-${Math.floor(Math.random() * 1e9)}`;

const journal = createJournal({ standing });
const ghosts = createGhosts({ standing, seed: 'lion-ghosts' });
/** The story, filled in once what it reads exists. See `createStory` below. */
let story = null;
/*
 * The beats, and the story's say over the three conditions a beat can put on
 * it (`needs`, `unless`, `follows`). Asked lazily: the story is built further
 * down, once everything it reads exists.
 */
const chapters = createChapters({ standing, has: (flag) => Boolean(story?.has(flag)) });

/**
 * The work classes, and the three numbers passing one is worth.
 *
 * Two objects rather than one, and the split is the design. `classes` is a
 * ledger of what has been passed and a timetable of what is posted; `perks`
 * turns that into three small bags of plain numbers, and those three bags are
 * the only thing `throwing.js`, `carry.js` and `grading.js` ever see. None of
 * those three has heard of a class, a level or a minigame, and taking the
 * feature out again would be deleting these two lines.
 */
const classes = createClasses();
const perks = createPerks({ classes });

/**
 * Standing, as the person who answers for a department actually reads it.
 *
 * Two facts, kept apart, because they expire differently. The ledger is what
 * you earned and only what you do moves it. The penalty is what the write-ups
 * against your name are doing to you *this week*, and it comes off on a date
 * printed on your card without anybody having to remember to give it back.
 *
 * One rule decides whose opinion applies: **a department reads your card
 * through its gate.** She is the face of it, hers is the row that says where a
 * new hire starts, and hers is therefore the row that says how much a bad
 * fortnight costs with them. Which is how the seven reactions come out
 * different without a single `if` anywhere naming anybody: Housekeeping weighs
 * it at nothing, Banquets at double, and the doors they each hold do the rest.
 *
 * Everything that asks a door a question goes through this — the negotiation
 * menu, the zones in `access.js`, where a released shortfall lands, and how big
 * a board you are handed. Everything that *moves* standing still goes to the
 * ledger, because a punishment you can be talked out of is not one.
 */
function recordPenalty(department) {
  return penaltyFor(gateFor(department), writeups.count);
}

function seenValue(department) {
  return standing.get(department) - recordPenalty(department);
}

/** The same object shape a door already reads, with the card subtracted. */
const asRead = { ids: standing.ids, get: seenValue };

/**
 * What each department made of you when a day's board went up.
 *
 * The board budgets a shift off the rungs, because the same shortfall is eighty
 * feet of fetch or none depending on who is holding it — so a player everybody
 * likes should be handed a day with more on it. But a day is supposed to be a
 * pure function of the seed and the day number, and standing moves *during* a
 * shift: reading it live would re-roll the board under the player the moment
 * they did somebody a favour.
 *
 * So it is read once, the first time a day is asked for, and then it is that
 * day's. Saved with everything else, or a reload would re-roll it too.
 */
const boardRungs = new Map();

function rungsForDay(day) {
  if (!boardRungs.has(day)) {
    boardRungs.set(
      day,
      Object.fromEntries(standing.ids.map((id) => [id, rungFor(seenValue(id)).id]))
    );
  }
  return boardRungs.get(day);
}

/**
 * A new starter's first afternoon (`sim/opening.js`). Declared here because the
 * board below asks it for day one's catalogue, and the afternoon itself cannot
 * be built until everything it reads exists — so this is the slot, filled once
 * that is true.
 */
let theOpening = null;
/**
 * The guided first days (`sim/tutorial.js`): day one held by the hand, and
 * everything it has not taught yet kept back until its turn. Only a week that
 * started at the hiring screen has one.
 */
const tutorial = createTutorial();
/** Whoever asks for a job: the roster row that says so (`jobs` in `data/staff.js`). */
const giverOf = (job) => STAFF.find((row) => row.jobs?.includes(job)) ?? null;
/** Whether a system is open yet: always, with no tutorial. */
const allows = (system) => tutorial.allows(system, clock.day);

/**
 * An event on the pool deck, put on tomorrow's board by a beat. Before the
 * board, because the board asks it for a day's card the way it asks the story
 * for the finale. See `sim/poolset.js`.
 */
const poolSets = createPoolSets({
  clock,
  grid: LEVEL.poolDeck,
  shiftFor: (day) => shiftForDay(day),
  rungFor: (department) => rungsForDay(clock.day)[department] ?? 'cold',
});

const beos = createBeoBoard({
  catalogue: (day) => {
    const first = theOpening?.boardFor(day);
    if (first) return first;
    /*
     * The finale's day is the finale. One room, and the man from the ownership
     * group standing in it; a board with five other cards on it is a board
     * where the one that matters is a choice, and it is not supposed to be.
     */
    const finale = story?.finaleFor(day);
    if (finale) return [finale];
    const rungs = rungsForDay(day);
    // From the second act on, the act's departments post more of their rooms.
    const act = story?.actOn(day) ?? 1;
    const generated = generateDay({
      day,
      shift: shiftForDay(day),
      grid: LEVEL.ballroom,
      seed: runSeed,
      rungFor: (department) => rungs[department] ?? 'cold',
      favour: act >= 2 ? story.departmentsOf(act) : [],
    });
    /*
     * And a lawn to strike, on the shifts a lawn is struck on. After the
     * generated events and not rolled with them, so a day's rooms are the same
     * rooms they were before there were lawns, and pure in the day like them.
     */
    const shift = shiftForDay(day);
    const strikes = TEARDOWNS.filter((row) => row.shifts.includes(shift.id)).map((row) => teardownCard(row, day, shift));
    // And the pool deck, on the day a beat put an event out there.
    const pool = poolSets.cardFor(day);
    // And the day's jobs (`data/jobs.js`), from the day a new starter is shown them.
    const jobs = tutorial?.allows('jobs', day) === false ? [] : JOBS.map((row) => jobCard(row, day, shift, giverOf(row.id)?.name ?? null));
    return [...generated, ...strikes, ...(pool ? [pool] : []), ...jobs];
  },
  resolveGhost: (context) => ghosts.resolve({ ...context, ease: firstActEase() }),
});
const stores = createStores();
/**
 * The bus, and everything that talks through it.
 *
 * Entities are every loose object in the world; throwing owns the ones in the
 * air. Both of them only ever emit — what a noise means is decided by whoever
 * is listening, which is the whole reason a thrown orange and a dropped tray
 * can have the same consequences without either being written down twice.
 */
const bus = createBus();
const entities = createEntities();
const throwing = createThrowing({
  world: level.world,
  entities,
  bus,
  // Everybody a flying object could hit. The player is not in this list yet;
  // nothing throws anything at them.
  bodies: () => npcs.filter((npc) => npc.present).map((npc) => ({
    id: npc.data.id,
    kind: 'staff',
    position: npc.position,
  })),
  // Trash basketball, as a cone and a multiplier. Identity until one is passed.
  perk: () => perks.throwing,
});

const carry = createCarry({ perk: () => perks.carry });
/*
 * `bodies` is everybody on their feet, for what a cart on a drawbar can run
 * into. Asked each frame; the roster is built a few lines further down.
 */
const wheeled = createWheeled({
  world: level.world,
  bus,
  entities,
  definitions: WHEELED,
  swipes: level.swipes,
  bodies: () => npcs.filter((npc) => npc.present && !npc.aboard).map((npc) => ({ id: npc.data.id, position: npc.position })),
});
const climbing = createClimbing({ world: level.world, bus, tuning: feel });
const cartView = createCartView({ wheeled });
stage.scene.add(cartView.object3D);
const entityView = createEntityView({ entities, throwing });
stage.scene.add(entityView.object3D);
/*
 * Where a room is set: the ballroom, and the pool deck on the day a beat put
 * an event out there. Each is its own anchor grid with its own tables, drawn
 * by its own view. `setup` is whichever the event you have taken is set in,
 * the ballroom when there is none, read fresh every time it is asked, so
 * everything handed it once (the objective, the scripts) follows the event
 * without being told.
 */
const setups = {
  ballroom: createSetup({ grid: LEVEL.ballroom }),
  pool: createSetup({ grid: LEVEL.poolDeck }),
};
const venueOf = (beo) => (beo?.venue && setups[beo.venue] ? beo.venue : 'ballroom');
const currentSetup = () => setups[venueOf(beos.active?.beo)];
const setup = new Proxy(
  {},
  {
    get: (_, key) => {
      const target = currentSetup();
      const value = target[key];
      return typeof value === 'function' ? value.bind(target) : value;
    },
  }
);
const setupViews = Object.fromEntries(Object.entries(setups).map(([id, one]) => [id, createSetupView({ setup: one })]));
for (const view of Object.values(setupViews)) stage.scene.add(view.object3D);
/** The guides are the current venue's; the other venue's are put away. */
const setupView = {
  setGuides(options) {
    const here = venueOf(beos.active?.beo);
    for (const [id, view] of Object.entries(setupViews)) view.setGuides(id === here ? options : { visible: false });
  },
};

const writeups = createWriteups();
/**
 * Everybody in the building, staff and guests alike.
 *
 * One list, one class, one set of eyes. What separates a guest from a member of
 * staff is a field in their data saying what they do when they have seen
 * enough — and nothing else in the game asks which kind anybody is.
 */
/*
 * `sight` is how much of their vision the world is currently letting them have.
 * One is everybody, all the time, until somebody hands one of them a napkin
 * with pepper in it — and `pranks` is the only thing that ever answers
 * otherwise.
 */
/*
 * And the one visitor, last, so everybody before him in the list is where they
 * were. `onProperty` is the story's say on top of anybody's hours: he is only
 * here while he is touring, and somebody walked out is not here at all.
 */
/**
 * Whether a room on today's board is about to open its doors: from forty
 * minutes before until twenty after, which is when its guests stand about in
 * the lobby outside. Per day, off the board, which is a pure function of it.
 */
const doorsByDay = new Map();
function eventOn(clock) {
  if (!doorsByDay.has(clock.day)) {
    const doors = (beos?.catalogueFor(clock.day) ?? []).filter((beo) => beo.setup && !beo.teardown && beo.room === 'ballroom').map((beo) => timesOf(beo).doors);
    doorsByDay.set(clock.day, doors);
  }
  return doorsByDay.get(clock.day).some((doors) => clock.minutes >= doors - 40 && clock.minutes < doors + 20);
}

const npcs = [...STAFF, ...GUESTS, ...VISITORS].map((data) =>
  createNpc({
    data,
    world: level.world,
    sight: () => pranks.sightOf(data.id),
    onProperty: (row, clock) => (row.attends ? eventOn(clock) : story ? story.onProperty(row, clock) : true),
  })
);

/*
 * Breaks (`sim/breaks.js`): a second route grid, over the building only, from
 * anywhere in it to the break room, with the main service run kept out of it
 * so nobody on a break ever walks the run (rule 2). Built a little a frame,
 * by count, like the player's.
 */
const breakRouter = createRouter({
  world: level.world,
  bounds: { minX: ft(-64), minZ: ft(-56), maxX: ft(198), maxZ: ft(104) },
  cell: 0.25,
  avoid: [[ft(-8), ft(10), ft(6), ft(102)]],
});
breakRouter.target({ x: ft(70), z: ft(-12) });
/**
 * Read in full, now, rather than a little a frame: on a reset as at boot, so
 * when somebody first can take a break never depends on what ran before
 * (`look.mjs` plays a seeded shift after other checks and on a fresh page, and
 * the two have to agree). About a tenth of a second, at boot and on a reset.
 */
function readBreakRoom() {
  breakRouter.invalidate();
  while (!breakRouter.ready) breakRouter.pump(1e6);
}
readBreakRoom();
/** Where people spend a break: on the benches first, then standing by the coffee and the vending machine. */
const BREAK_SPOTS = [
  ...[65, 67, 69, 71].map((x) => ({ x: ft(x), z: ft(-16.5), does: x % 4 === 1 ? 'phone' : 'food' })),
  { x: ft(81), z: ft(-27.5), does: 'coffee' },
  ...[65, 67, 69, 71].map((x) => ({ x: ft(x), z: ft(-29.4), via: [{ x: ft(61), z: ft(-16) }, { x: ft(61), z: ft(-29.4) }], does: x % 4 === 1 ? 'food' : 'phone' })),
  { x: ft(88.5), z: ft(-25.5), does: 'phone' },
];
const breaks = createBreaks({
  npcs,
  pathFrom: (point) => (breakRouter.ready ? breakRouter.path(point) : null),
  spots: BREAK_SPOTS,
  seed: () => runSeed,
});
/** Cells of the break room's grid built a frame: it is smaller and only built once. */
const BREAK_BUDGET = 3000;
/** Seconds the player has been within earshot of the visitor, for him to say something in passing. */
let passingFor = 0;
/** Colleagues talking to each other (`sim/crewTalk.js`): words over their heads, nothing more. */
const crewTalk = createCrewTalk({ npcs, seed: () => runSeed, onBreak: (id) => breaks.onBreak(id) });
/** The day's jobs (`sim/jobs.js`), on the ballroom. */
const jobs = createJobs({
  entities,
  setup: () => setups.ballroom,
  inRoom: (point) => level.roomAt(point.x, point.z)?.id === 'ballroom',
  carried: () => (String(hands().claim ?? '').startsWith('job:') ? hands().count : 0),
});
/** What somebody is doing that only shows: a break's phone or sandwich, a colleague they are talking to. */
function lifeOf(npc) {
  const spot = breaks.spotOf(npc.data.id);
  const facing = crewTalk.facingOf(npc.data.id);
  if (!spot && !facing) return null;
  const there = spot && Math.hypot(npc.position.x - spot.x, npc.position.z - spot.z) < 0.3;
  return { does: there ? spot.does : null, facing };
}
/**
 * The one set of eyes and ears in the game.
 *
 * Every `npc.sees` call in the building goes through this, which is the point:
 * before it there were two independent answers to "did anybody notice", one for
 * standing somewhere you shouldn't and one for helping yourself to a shelf, and
 * they disagreed about distance, about walls and about how long you had to get
 * away with it.
 */
/**
 * Everything with room in it, right now.
 *
 * Three sources and no cases. The fixed ones come out of the data. A table with
 * a linen over it counts and a bare one does not, which is a fact about covers
 * and not about banquets — the rule reads `cover`, so a linened round somebody
 * set in the corridor for a laugh works exactly as well as one on an anchor.
 * And a parked cart is a parked cart wherever it is parked.
 */
function hollowsNow() {
  const spots = HOLLOWS.map((hollow) => ({
    x: ft(hollow.at[0]),
    y: 0,
    z: ft(hollow.at[1]),
    radius: ft(hollow.radius),
    label: hollow.label,
  }));
  for (const one of Object.values(setups)) {
    for (const table of one.tables) {
      if (!table.cover) continue;
      const anchor = one.anchorAt(table.key);
      if (!anchor) continue;
      spots.push({
        x: anchor.x, y: 0, z: anchor.z,
        radius: ft(OBJECTS[table.kind].radius ?? 3),
        label: `under a ${OBJECTS[table.kind].name.toLowerCase()}`,
      });
    }
  }
  for (const cart of wheeled.all) {
    if (cart.state !== 'parked' || cart.tipped) continue;
    spots.push({
      x: cart.position.x, y: cart.position.y, z: cart.position.z,
      radius: wheeled.radius(cart) * 1.6,
      label: `behind a ${OBJECTS[cart.kind].name.toLowerCase()}`,
    });
  }
  return spots;
}

const concealment = createConcealment({
  world: level.world,
  bus,
  hollows: hollowsNow,
  tuning: feel,
});

/**
 * The bar, on the body's own clock.
 *
 * Deliberately not on the game clock. Skipping time forward to the next shift
 * does not sober you up and waiting for the board to refresh does not get you
 * high; being out of it is a thing that happens to a body over real seconds,
 * and it reads completely wrong if a time-scale key can do it to you.
 */
const impairment = createImpairment({
  catalogue: CONSUMABLES,
  bus,
  now: () => clock.day * 1440 + clock.minutes,
});

/**
 * What you hear, which is the other half of a perception system.
 *
 * Thirteen people have had ears since step six and the player has had none.
 * Everything already goes past on the bus with a position and a radius on it,
 * so listening is a subscription and a distance test; the only thing impairment
 * changes is how far the test reaches. A compass bearing rather than a marker,
 * because knowing something broke somewhere behind you is interesting and
 * knowing exactly where is not.
 */
const BEARINGS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];
let heard = null;

function bearingTo(position) {
  const angle = Math.atan2(position.x - player.position.x, position.z - player.position.z);
  const eighth = Math.round((((angle / (Math.PI * 2)) % 1) + 1) * 8) % 8;
  return BEARINGS[eighth];
}

/**
 * The building, out loud.
 *
 * Two objects and a promise. `audio` owns the context and the fader; `sound`
 * turns what is happening into noises. Neither of them can reach anything in
 * the simulation: the only things that cross are plain numbers, handed over by
 * `soundReading()` once a frame and by the listener below when a noise goes on
 * the bus. Nothing about a radius, a sightline or a write-up changes because
 * the game is now audible, and the corridor benchmark neither knows nor cares.
 */
const audio = createEngine();
const sound = createSoundboard({
  engine: audio,
  /*
   * The same segment test the roster's eyes use, so that the wall which stops
   * a manager seeing you is the wall that muffles the sound of you. Handed in
   * rather than worked out down there, because working it out needs the level
   * and the audio layer is not allowed to hold one.
   */
  blocked: (from, to) =>
    !segmentClear({ x: from.x, y: 1.2, z: from.z }, { x: to.x, y: to.y ?? 1, z: to.z }, level.world),
});

/**
 * Every noise the simulation makes, made audible at the radius it was given.
 *
 * Flattened to plain numbers on the way out — a cause, a place and a reach —
 * so there is no event object on the other side to reach back through. The
 * radius is passed through untouched and used as-is, which is the entire point
 * of the layer: these radii are exactly how far away somebody can hear you do
 * a thing, they have been invisible since the day they were written, and this
 * is the player finally being told.
 *
 * Unlike the line below it, this one does not skip the player. Hearing what
 * *you* just did, and how far it went, is most of what there is to learn.
 */
bus.on(EVENT.NOISE, (event) => {
  sound.heard({
    cause: event.cause,
    kind: event.kind ?? null,
    actor: event.actor ?? null,
    x: event.position?.x ?? 0,
    y: event.position?.y ?? 0,
    z: event.position?.z ?? 0,
    radius: event.radius ?? 0,
  });
});

/*
 * Breakage is its own event as well as a noise, and it is the loudest thing in
 * the building. The noise carries the reach; this carries the fact that it was
 * glass, which the catalogue has a different sound for.
 */
bus.on(EVENT.THROW, (event) => {
  sound.cue('throw', { x: event.position?.x, z: event.position?.z });
  spokeForHands = true;
});
bus.on(EVENT.DROP, (event) => {
  sound.cue('setdown', { x: event.position?.x, z: event.position?.z, radius: event.radius });
  spokeForHands = true;
});

/**
 * Your hands, watched rather than announced.
 *
 * Something arriving in them and something leaving them are the two sounds, and
 * there are a dozen verbs that do one or the other — picking a thing off the
 * floor, drawing from a station, loading a cart, unloading it, placing a round
 * on an anchor. Cueing each of those at its own call site would be a dozen
 * lines to keep in step and one of them wrong within a fortnight.
 *
 * So it is one line watching one number. Anything that changes what is in your
 * hands makes the right noise without knowing that it did.
 *
 * `spokeForHands` is the exception the rule needs: a throw and a drop both
 * empty your hands and both already have a sound of their own, at the place the
 * thing went rather than at your feet. Without it every throw would also be a
 * set-down, half a metre behind the bottle.
 */
let heldLast = 0;
let spokeForHands = false;

function runHands() {
  const now = hands().count;
  if (now > heldLast) sound.cue('lift');
  else if (now < heldLast && !spokeForHands) sound.cue('setdown');
  heldLast = now;
  spokeForHands = false;
}

bus.on(EVENT.NOISE, (event) => {
  if (event.actor === 'player') return;
  const gap = Math.hypot(event.position.x - player.position.x, event.position.z - player.position.z);
  if (gap > impairment.earshot) return;
  heard = { cause: event.cause, where: bearingTo(event.position), near: gap < ft(20), at: elapsed };
});

const perception = createPerception({
  npcs,
  bus,
  now: () => clock.day * 1440 + clock.minutes,
  concealed: () => Boolean(concealment.hidden),
  belief: () => impairment.belief,
  // Quick Hands, as one multiplier on one kind of claim. Identity until sat.
  quiet: () => perks.hands.quiet,
  // The ride a strike sends you on is not a thing anybody makes a note of.
  excused: (event) => Boolean(teardown?.excuses(event)),
  // Nobody is a witness to the cart they are sitting in.
  aboard: (event, npc) => {
    const cart = wheeled.byId(event.cart);
    return Boolean(cart) && wheeled.occupants(cart).some((seat) => seat.who === npc.data.id);
  },
});
perception.listen();
/**
 * And what they do about what they saw.
 *
 * The other half of the one path. Perception has opinions; this has states of
 * mind, and an ACCUSING state of mind is the only thing in the game that can
 * produce a write-up for conduct. Before this there were two: the restricted
 * zone ran its own timer per NPC, and taking from a station asked "was anybody
 * looking" and floored a department on the spot.
 */
/**
 * What each colleague thinks of you, which is not what their department thinks.
 *
 * The same module the seven departments run on, a second time, with one row per
 * person and **no rivalry matrix at all** — nobody in the section thinks worse
 * of you for somebody else thinking better. Which is the whole reason it is a
 * second instance rather than thirteen more rows in the first one: department
 * standing ripples by design and a personal opinion must not, and the two must
 * never be able to absorb each other.
 *
 * It moves on what this person witnessed and on what has passed between the two
 * of you. It never moves on a grade — a room that went well is Banquets'
 * business, and what Ryan makes of you is Ryan's.
 */
const STARTING_REGARD = Object.freeze(
  Object.fromEntries(CREW.map((person) => [person.id, person.starts ?? 0]))
);

const regard = createStanding({
  departments: CREW.map((person) => ({ id: person.id, name: person.name })),
  rivalry: {},
  initial: STARTING_REGARD,
});

/**
 * How much it takes to get one particular person going, in the units the bands
 * are written in.
 *
 * The one place personal standing is read by the simulation, and it is read as
 * a *bar* rather than as a decision: somebody who thinks well of you does not
 * behave differently when they see something, they need to see more of it.
 *
 * A tenth, at a full ladder of goodwill, and the size is not a taste. The bands
 * sit at 0.34 and 0.75; a bottle broken in front of somebody at conversational
 * distance is worth about 0.46. So a tenth is the largest number that leaves a
 * thing seen properly and at close range still landing on everybody, while
 * moving the *weaker* claims — the glimpse across a room, the noise through a
 * door — across the line for somebody who has no time for you and under it for
 * somebody who has. Which is what reporting sooner or later means.
 *
 * At a third, which is where this started, a person who liked you could not be
 * made suspicious by anything at all. That is not the benefit of the doubt, it
 * is blindness, and it took the one interesting thing at the top of the ladder
 * — the hesitation — off the table, because you cannot hesitate over something
 * you never noticed.
 */
const REGARD_BAR = 0.1;

/*
 * The section before it was the real crew. A save written then keeps each
 * slot's opinion of you under the old id; it belongs to whoever is in that
 * slot now, so it is read across rather than dropped. The map is the roster's.
 */
function renamedCrew(saved) {
  const out = { ...saved };
  for (const [old, now] of Object.entries(RENAMED_CREW)) {
    if (old in out && !(now in out)) out[now] = out[old];
    delete out[old];
  }
  return out;
}

function barFor(id) {
  if (!regard.ids.includes(id)) return 0;
  return (regard.get(id) / CEILING) * REGARD_BAR;
}

const suspicion = createSuspicion({ npcs, perception, bar: barFor });

/**
 * And the player's copy of both, which is the only one that lasts.
 *
 * A witness forgets after forty-five minutes and a suspicion level drains to
 * nothing, both on purpose, so by the time a shift ends there is nothing in the
 * simulation left to read. This keeps a line per thing anybody noticed and what
 * became of it — including, and mostly, the ones that became nothing.
 *
 * It is downstream of everything and upstream of nothing. Nobody in the
 * building can consult it and no rule reads it back.
 */
const noticed = createNoticed();

/**
 * The four acts, and the man from the ownership group. See `sim/story.js`.
 *
 * It reads the after-action record above, which nothing else in the building
 * does: his file on you is what the building noticed, read back at the end.
 * Declared near the top because the board, the beats and the roster all ask
 * it questions through closures that exist before it does.
 */
story = createStory({
  standing,
  noticed,
  writeups,
  npcs,
  roster: [...STAFF, ...VISITORS],
  shiftFor: shiftForDay,
  rungOf: (department) => rungFor(seenValue(department)).id,
  played: (id) => chapters.played.includes(id),
});

/** The name to write on the record, asked once when a line is opened. */
const nameOf = (id) => npcs.find((npc) => npc.data.id === id)?.data.name ?? null;

/**
 * The building, read aloud.
 *
 * Everything it can say is a number that is true right now, so there is nothing
 * authored to run out of. It is fed from the same reports the HUD and the
 * headless scripts read.
 */
/**
 * Who the stock belongs to.
 *
 * Accepting an event is the moment the department pulls its par, so from then
 * on everything you draw off the shelf is written down against that room. The
 * room is the ballroom zone; nothing else about the ballroom is known here.
 */
const claims = createClaims({
  shortfallsFor: (beoId) => (beos.active?.beo.id === beoId ? beos.active.beo.shortfalls : null),
  inRoom: (position) =>
    level.zonesAt(position.x, position.z).some((zone) => zone.kind === 'event'),
});

/**
 * Anything with an owner that has come to rest somewhere gets counted.
 *
 * Driven off the entity store rather than off any particular verb, so a linen
 * that ended up in a corridor is a linen in a corridor whether it was thrown,
 * dropped, spilled out of a cart that went over, or left there by somebody who
 * changed their mind. There is no verb for this and nothing asks what you meant.
 */
entities.onChange((what, entity) => {
  if (what === 'spawn' && entity?.claim) claims.settle(entity);
});

/**
 * Which doors are open to you, which is a question about standing.
 */
/*
 * And one answer that is not standing: a strike you have taken puts you on the
 * apron, which belongs to whoever is working it. See `data/teardowns.js`.
 */
let teardown = null;
let rescues = null;
let shuttles = null;
let deliveries = null;
/*
 * Sent out front by the screen. On the first days, when the way to your room's
 * shortfall goes onto the marble (Priya at the front desk, the signage board),
 * the job is what sent you, and a job covers where it sends you
 * (`sim/access.js`, `working`). Before this a new starter did as the screen
 * said, walked up to the front desk in blacks, and was written up for it.
 * Covered from when the line goes out front until you are back off the marble.
 */
let frontCover = false;
/** Whether the cover was on last frame, so its ending is not announced either. */
let coverWas = false;
function inFrontOfHouse(point) {
  return Boolean(point) && level.zonesAt(point.x, point.z).some((zone) => zone.kind === 'foh');
}
/*
 * On a new starter's first days, a shortfall nobody on shift can cover is
 * covered anyway: the department left it out. On day three's early shift
 * nobody from housekeeping or the kitchen was on until eight, the screen said
 * "see Roz" for three hours, and the room's deadline went by. Trading and
 * asking are taught on days that have somebody to ask.
 */
function coverUnmanned() {
  if (!(tutorial.active && tutorial.dayIndex(clock.day) <= 3) || !beos.active || beos.active.beo.teardown) return;
  for (const entry of beos.active.beo.shortfalls ?? []) {
    if (entry.resolved) continue;
    const row = SHORTFALLS[entry.id];
    if (!row || holderFor(row.owner)) continue;
    entry.resolved = true;
    entry.via = 'left-out';
    const released = row.kind !== 'access' ? releaseFor(entry) : null;
    applyShortfalls();
    const where = released ? releasedLine(released) : null;
    const dept = departmentsById[row.owner]?.name ?? 'They';
    hud.say(`Nobody from ${dept} is on yet, so they left ${row.wants ?? 'it'} sorted for you.${where ? ` ${where}` : ''}`, { tone: 'good', seconds: 7 });
    autosave.touch();
  }
}

function updateFrontCover() {
  // The screen sending you out front: a new starter's room, or somebody on
  // the desk asking for a word in the lobby (Priya's beats play there, and
  // can before Management thinks enough of you to leave the marble open).
  const summoned = !beos.active && Boolean(missionStep()) && inFrontOfHouse(routeGoal?.point);
  const goalFront = (tutorial.active && Boolean(beos.active) && inFrontOfHouse(routeGoal?.point)) || summoned;
  if (goalFront) frontCover = true;
  else if (!inFrontOfHouse(player.position)) frontCover = false;
}

const access = createAccess({
  standing: asRead,
  working: (id) =>
    Boolean(
      (frontCover && id === 'foh-lobby') ||
      teardown?.works(id) ||
        rescues?.works(id) ||
        shuttles?.works(id) ||
        deliveries?.works(id) ||
        poolSets.works(id, beos.active?.beo)
    ),
});

/**
 * The desert, which does not know the hotel is there.
 *
 * Rolled rather than triggered, and its sighting log is the one thing in the
 * game that survives a run ending — three strikes takes your job, not the fact
 * that you once saw a rattlesnake on the apron.
 */
const wildlife = createWildlife({
  table: WILDLIFE,
  entities,
  world: level.world,
  bus,
  rooms: () => level.rooms,
  seed: 'lion-desert',
  // Who is about, asked of the one module that answers it. The player and the
  // Banquet Manager are the same kind of thing to a javelina.
  sense: (at, reach) => perception.near(at, reach),
});

/**
 * The animals somebody could look at and think "that should not be there".
 *
 * Offered to perception every frame as sights — states, like the room you are
 * standing in, rather than events — and it decides who is in a position to see
 * them. The row says which kinds anybody minds; nothing here checks eyes.
 */
function wildSights() {
  return wildlife.creatures
    .filter((creature) => creature.definition.minded)
    .map((creature) => ({
      key: creature.id,
      what: 'wildlife',
      detail: creature.what,
      subject: creature.id,
      position: creature.position,
    }));
}

const wildlifeView = createWildlifeView({ wildlife });
stage.scene.add(wildlifeView.object3D);

/**
 * The one animal that is one animal, and remembers what you did the first
 * time you met it. See `sim/individual.js`; saved with the week.
 */
const individuals = createIndividuals({ bus });
const individualView = createIndividualView({ individuals });
stage.scene.add(individualView.object3D);

const chatter = createChatter({
  read: () => ({
    due: beos
      .board(clock)
      .filter((card) => card.state === 'open')
      .map((card) => ({
        code: card.beo.code,
        tables: card.beo.setup?.tableCount ?? 0,
        minutes: Math.max(0, Math.round(card.remaining)),
      })),
    shortfalls: (beos.active?.beo.shortfalls ?? []).map((entry) => ({
      label: SHORTFALLS[entry.id]?.label ?? entry.id,
      amount: entry.amount,
      resolved: entry.resolved,
      stock: stores.available(SHORTFALLS[entry.id]?.item),
      owner: STAFF_BY_ID[SHORTFALLS[entry.id]?.owner]?.name ?? null,
    })),
    leaving: npcs
      .filter((npc) => npc.present && npc.data.window)
      .map((npc) => ({
        name: npc.data.name,
        minutes: Math.max(0, parseTime(npc.data.window.to) - clock.minutes),
      })),
    owed: tasks.list.map((task) => ({
      name: STAFF_BY_ID[task.staffId]?.name ?? 'somebody',
      what: task.target?.label ?? 'something',
    })),
    standing: standing.toJSON(),
  }),
});
const tasks = createTasks();
const save = createSave();
const favors = createFavors({ tasks, standing, seed: 'lion-favours' });
const procurement = createProcurement({
  standing,
  favors,
  tasks,
  clock,
  rng: createRng(hashSeed('errands')),
  // A separate stream from the errands one: whether somebody who says yes
  // actually does it is rolled per person, per shortfall, per day, and it must
  // not move because a trade happened to be negotiated first.
  seed: 'lion-promises',
  // What is on your card, asked for rather than held, because it changes under
  // a negotiation: one ages off, one is filed while you are mid-sentence.
  strikes: () => writeups.count,
  onWriteUp: (reason, detail, options) => writeUp(reason, detail, options),
});

/*
 * The player's settings: volumes, camera speed and invert. Their own key in
 * storage, apart from the save, so starting a new week keeps them.
 */
const settings = createSettings();
const input = createInput(bindings, { element: canvas, look: () => settings.values });
// The master fader follows the settings screen; the keys move both.
settings.onChange((values) => {
  audio.setVolume(values.master);
  audio.setEffects(values.effects);
  audio.setMusic(values.music);
});
const hud = createHud({ parent: ui });

/**
 * Eight ways to be a nuisance, and the one rule each of them adds.
 *
 * Handed nothing unusual: the bus to put an act on, perception to ask who
 * formed a claim, the department ledger for the one prank that ends in a
 * complaint and the personal ledger for the one that costs you somebody's
 * opinion of you. Everything else about a prank — who saw it, how sure they
 * are, what they do about it — happens in the modules that already answer
 * those questions, which is why this is a constructor call and not a system.
 */
const pranks = createPranks({
  bus,
  entities,
  throwing,
  perception,
  npcs,
  clock,
  regard,
  standing,
  hud,
});
/*
 * `talkingTo` is who the player is in a conversation with, asked when a body
 * is drawn, so the person in it plays talking. Read, never written.
 */
/*
 * Where somebody standing still may be drawn sitting: along the break room's
 * two benches, two feet apart, facing the table between them. Drawing only;
 * see `npcView.js`.
 */
const BENCH_SEATS = LEVEL.props
  .filter((prop) => /^break-bench/.test(prop.id))
  .flatMap((prop) => {
    const [x0, z0, x1, z1] = prop.rect;
    const table = LEVEL.props.find((other) => other.id === 'break-table-a');
    const tableZ = table ? (table.rect[1] + table.rect[3]) / 2 : z0;
    const z = (z0 + z1) / 2;
    const yaw = tableZ > z ? 0 : Math.PI;
    const out = [];
    for (let x = x0 + 1; x <= x1 - 1; x += 2) out.push({ x: ft(x), z: ft(z), y: ft(prop.height), yaw });
    return out;
  });
/**
 * And the other places somebody standing still is drawn sitting: the lounge
 * settees and the pool loungers, facing the water. Drawing only, like the
 * benches. Whoever sits on one faces the way it does (`faces` on their row),
 * so the cone on the floor and the body over it agree (`rig.mjs`).
 */
const LOUNGE_SEATS = [
  // The settees facing into the room; the pair facing the wall would sit the
  // guest who paces in front of them.
  ...[139, 145, 155, 161].map((x) => ({ x: ft(x), z: ft(47), y: ft(1.25), yaw: 0 })),
  ...[-74.5, -64.5, -54.5].map((z) => ({ x: ft(283.5), z: ft(z), y: ft(1.3), yaw: Math.PI / 2 })),
];

const npcViews = npcs.map((npc) =>
  createNpcView({
    npc,
    parent: ui,
    talkingTo: () => conversation?.talkingTo ?? null,
    seats: [...BENCH_SEATS, ...LOUNGE_SEATS],
    player: () => player.position,
    life: () => lifeOf(npc),
    // Somebody the task names carries a name tag as far as ever; anybody else
    // only once you are beside them.
    named: () => taskFocus.anyone || taskFocus.people.has(npc.data.id),
  })
);
for (const view of npcViews) stage.scene.add(view.object3D);
const dialogue = createDialogue({ parent: ui, standing });
const sheet = createSheet({ parent: ui, grid: LEVEL.ballroom });
const panel = createDebugPanel({ parent: ui, standing });
const summaryPanel = createShiftSummary({
  parent: ui,
  onClose: () => {
    veil.suppress(false);
    veil.show();
  },
});

const chapterPanel = createChapterPanel({
  parent: ui,
  onDone: () => {
    // Whatever the beat hands you is in your pocket from here on.
    const played = chapters.play();
    pocket.give(played?.gives ?? []);
    // And whatever it asks of you: a dinner somebody wants over, from now on.
    if (played?.posts) {
      dinners.post(played.posts);
      searches.post(played.posts);
      rescues.post(played.posts);
      shuttles.post(played.posts);
      blockades.post(played.posts);
      deliveries.post(played.posts);
      poolSets.post(played.posts);
      story.post(played.posts, clock.day);
    }
    veil.suppress(false);
    veil.show();
    autosave.touch();
  },
});

/** The two questions at the end. See `ui/choicePanel.js`. */
const choicePanel = createChoicePanel({ parent: ui });

const gradePanel = createGradePanel({
  parent: ui,
  onClose: () => {
    veil.suppress(false);
    veil.show();
  },
});

const beoPanel = createBeoPanel({
  parent: ui,
  grid: LEVEL.ballroom,
  onAccept: (beo) => acceptCard(beo),
  onSit: (id) => sitClass(id),
  onClose: () => {
    veil.suppress(false);
    veil.show();
  },
});

/**
 * Taking a card: off the board, or from whoever asks for a job. One path, so a
 * job handed to you in the corridor starts exactly the way the board does.
 */
function acceptCard(beo) {
    const held = heldBackFrom(beo);
    if (held) {
      hud.say(held, { tone: 'warn', seconds: 6 });
      return;
    }
    const result = beos.accept(beo, clock, { onTheClock: shift.status(clock).onTheClock });
    if (!result.ok) {
      hud.say(acceptRefusal(result), { tone: 'warn' });
      return;
    }
    closeBoard();
    /*
     * A strike is not a room: nothing is drawn from a shelf for it and nothing
     * is short. What taking one does is put the lawn's carts back on the lawn
     * and the crew out there with them.
     */
    if (beos.active.beo.teardown) {
      teardown.begin(beos.active.beo);
      applyShortfalls();
      hud.say(`Accepted ${beo.code}: ${beo.title}. The crew are out there. The golf cart is in the staff lot.`, {
        tone: 'good',
        seconds: 6,
      });
      autosave.touch();
      return;
    }
    stores.openFor(beos.active.beo);
    // A job starts the room where the job says (`sim/jobs.js`).
    if (beos.active.beo.job) jobs.begin(beos.active.beo);
    // A new starter's rack hands out the colour the room wants, all through
    // the first days: which colour is a lesson for after them. On day two a
    // new starter laid the rack's ivory on a white sheet's room, and the room
    // read "6 of 6 set" and never finished.
    if (tutorial.active && beos.active.beo.setup?.linen) setLinen(beos.active.beo.setup.linen);
    /*
     * The finale: every department does what its rung does, before you have
     * asked anybody for anything. Through the release every covered shortfall
     * goes through, so what is staged is where that rung always leaves things.
     */
    const helped = beos.active.beo.finale ? story.prepare(beos.active.beo, { release: releaseFor }) : null;
    applyShortfalls();
    if (helped) {
      takeScreen();
      chapterPanel.show(
        {
          kicker: 'The finale',
          title: `${beo.code} · ${beo.title}`,
          speaker: 'Who is behind you',
          lines: helped.filter((row) => row.says).map((row) => `${row.name}: "${row.says}"`),
        },
        { onDone: () => resumeFromStory() }
      );
      autosave.touch();
      return;
    }
    const short = beos.active.beo.shortfalls ?? [];
    // What comes next is the task card's to say (one instruction on the
    // screen), so the line says only what was taken and what it is short of.
    hud.say(
      short.length
        ? `Accepted ${beo.code}: ${beo.title}. Short: ${short
            .map((entry) => {
              // What is short, by name; what is shut, as it stands.
              const row = SHORTFALLS[entry.id];
              return row.kind === 'access' ? row.label.toLowerCase() : (row.wants ?? row.label.toLowerCase()).replace(/^the /, '');
            })
            .join(', ')}.`
        : `Accepted ${beo.code}: ${beo.title}. Set by ${beo.setupBy}.`,
      { tone: short.length ? 'warn' : 'good', seconds: 7 }
    );
}

/**
 * The class screen, and the two ways out of it.
 *
 * `onFinish` is a sitting that ran to its end and has a number on it; `onLeave`
 * is somebody pressing Escape half way through. The difference between them is
 * the whole of "skipping costs little": leaving files nothing, charges nothing
 * and says nothing, and the only thing you are out is the time you spent.
 *
 * A finished sitting costs the shift `CLASS_MINUTES` whether it passed or not,
 * because you stood in the break room for twenty-five minutes either way, and
 * that is the only price in the system. It is charged here rather than inside
 * the panel: what a class costs is the shift's business, and the panel's clock
 * runs in real seconds.
 */
const classPanel = createClassPanel({
  parent: ui,
  onFinish: ({ entry, row, score }) => {
    const filed = classes.record({ id: entry.id, level: row.level, score, day: clock.day });
    clock.skip(CLASS_MINUTES);
    veil.suppress(false);
    veil.show();
    if (filed.banked) {
      hud.say(`Passed ${entry.name} level ${row.level}. ${sentence(entry.reads)} just got better.`, {
        tone: 'good',
        seconds: 6,
      });
    } else if (filed.met) {
      hud.say(`Level ${row.level} again. One pass a class a day.`, { tone: 'info', seconds: 5 });
    } else {
      hud.say(`${entry.name}: ${score} against ${row.target}. The slot is still up.`, {
        tone: 'info',
        seconds: 5,
      });
    }
  },
  onLeave: () => {
    veil.suppress(false);
    veil.show();
  },
});

/**
 * How far into today's shift we are, which is what a class slot is posted
 * against.
 *
 * Minutes from the shift's start rather than a wall-clock hour, because the
 * roster runs a PM, an AM and a double and "half four" is mid-shift on one of
 * them and the middle of the night on another. Negative for somebody who turned
 * up early, which the slot reads as "not yet" without needing to say so.
 */
function intoShift() {
  return clock.minutes - shift.windowFor(clock.day).start;
}

function classBoard() {
  if (!allows('classes')) return [];
  return classes.board({
    day: clock.day,
    since: intoShift(),
    onTheClock: shift.status(clock).onTheClock,
  });
}

/**
 * Sitting one.
 *
 * Goes through the board's own slot state rather than trusting the button,
 * because the panel is reachable from a script and a slot that has closed since
 * the board was drawn must not open again just because a card is still on
 * screen.
 */
function sitClass(id) {
  const slot = classBoard().find((entry) => entry.entry.id === id);
  if (!slot || !slot.open) return { ok: false, reason: slot ? slot.state : 'no-such-class' };
  closeBoard();
  takeScreen();
  /*
   * A different round every time you sit down, at the same difficulty.
   *
   * The sitting number is in the seed, so the level is deterministic — the
   * same sitting always deals the same round, which is what the headless check
   * and every test stand on — and a re-sit is a new one. Keying it on the level
   * alone made a failed level a wall: you got that same room, that same can and
   * that same order off the pass forever, and a player who could not beat it
   * could not ever beat it.
   */
  const seed = `${runSeed}:${id}:${slot.level}:${classes.history.length}`;
  classPanel.show({ entry: slot.entry, row: slot.row, seed });
  return { ok: true, level: slot.level };
}

/**
 * Your supervisor deciding you are not going on the big room this week.
 *
 * `gates: 'assignment'` has been on his row since the roster was written and
 * until now it was a comment — he was the one gate with nothing to offer, and
 * what he was supposed to control was which events reached your board at all.
 * This is it, and it only ever does anything when Banquets, as he reads you
 * (your card included, at double weight), has come off the bottom step.
 *
 * He does not take the board away. He puts you on the short cards, which is
 * both what a supervisor covering himself actually does and the only version of
 * this rule that is survivable: the small rooms still grade, grading them is
 * still how Banquets climbs, and a player who works two clean shifts has the
 * gala back. A rule that emptied the board would be one nobody could work their
 * way out of, and that is the one thing this whole system must never be.
 */
function heldBackFrom(beo) {
  // The finale is the one room nobody keeps you off.
  if (beo.finale) return null;
  const rule = gateFor('banquets')?.assigns;
  /*
   * A lost finale, and the agency's trial: the big rooms are theirs for a week.
   * The same line he draws when he stops carrying you, because it is the same
   * thing happening to you for a different reason. See `LOSS` in `data/story.js`.
   */
  if (rule && story.trialOn(clock.day) && (beo.estimate ?? 0) > rule.keepUnder) {
    return 'The agency has the big rooms this week. Take a shorter one.';
  }
  if (!rule || !escalation.supervisorStopped) return null;
  if ((beo.estimate ?? 0) <= rule.keepUnder) return null;
  return `${gateFor('banquets').name} has you on the small rooms this week. Take a shorter one.`;
}

/**
 * How long your rooms take against the board's estimate for them: the slowest
 * of your last three, never under one and never over three. The middle of
 * them told a new starter a second room fitted when it fitted only on a good
 * afternoon, and the doors found it unfinished (`fullgame.mjs`, day four).
 */
function yourPace() {
  return yourRealPace() * tutorial.paceOn(clock.day);
}

/** The same, in real time: how long you take against the estimate with the clock at full speed. */
function yourRealPace() {
  const mine = beos.history.filter((r) => !r.ghost && r.closedAt != null && r.acceptedAt != null).slice(-3);
  // In real time: a room set on a day the clock ran at half took half the
  // clock's minutes it would have at full speed.
  const ratios = mine
    .map((r) => {
      const beo = beos.catalogueFor?.(r.day)?.find((b) => b.id === r.id);
      return beo?.estimate ? (r.closedAt - r.acceptedAt) / tutorial.paceOn(r.day) / beo.estimate : null;
    })
    .filter((x) => x !== null && x > 0)
    .sort((a, b) => a - b);
  // With nothing on record yet, the board's own pace: a new starter has their
  // guided room on record from the first day, and an ordinary week has no
  // reason to think you slow.
  const slowest = ratios.length ? ratios[ratios.length - 1] : 1;
  return Math.min(3, Math.max(1, slowest));
}

/**
 * Whether anything on the board could still be taken and set in time: at the
 * board's own pace, not yours. Yours is advice on each card; told to go home
 * at the start of every shift because they had been slow, a new starter never
 * set another room and never got faster (`fullgame.mjs`, days eight to eleven).
 */
function anythingFits() {
  const pace = 1;
  return beos
    .board(clock)
    .filter((card) => !card.beo.teardown || allows('strikes'))
    // The finale is taken whatever it says about time: not taking it is its own ending.
    .some((card) => card.state === 'posted' && card.live && card.remaining > 0 && !heldBackFrom(card.beo) && (card.beo.finale || !card.beo.estimate || card.remaining >= card.beo.estimate * pace));
}

/** A department as a sentence says it: "the Kitchen", "Valet". */
function deptName(id) {
  const name = departmentsById[id]?.name ?? id;
  return id === 'kitchen' || id === 'frontdesk' ? `the ${name}` : name;
}

/**
 * Whose room a card is, and whether it wins over a department the act is
 * about and that is not yet warm. See `ui/beoPanel.js`.
 */
function whoseRoom(beo) {
  if (!beo.owner || beo.teardown) return null;
  const department = departmentsById[beo.owner];
  if (!department) return null;
  const wanted = allows('beats') && story.departmentsOf(story.act).includes(beo.owner) && !story.won(beo.owner);
  return { name: department.name, them: deptName(beo.owner), wins: wanted };
}

/**
 * Opening the board releases pointer lock and pauses the sim.
 *
 * Pausing is a real decision and it cuts against the tension: the deadline
 * stops while you read. It is the right call anyway, because the alternative is
 * that the correct way to play is to never open the board twice — you would
 * memorise the diagram from the first read or lose two minutes to a second one,
 * and the game would be punishing you for reading its own brief.
 */
function openBoard() {
  takeScreen();
  const status = shift.status(clock);
  // A strike needs the golf cart and a tow, which the guided days do not teach
  // until their day; until then it is not on the board for a new starter.
  const cards = beos.board(clock).filter((card) => !card.beo.teardown || allows('strikes'));
  beoPanel.show(cards, clock, {
    /*
     * Your supervisor's class slots, at the top of the board.
     *
     * Posted on the same board as the events because that is the one wall in
     * the building anybody actually reads, and at a fixed point in the shift
     * rather than at a fixed hour so they land in the same place on a PM, an AM
     * and a double.
     */
    classes: classBoard(),
    // The same line the sheet shows, on the card, before you commit to it. The
    // decision this board asks for is which department you are prepared to owe,
    // and how far their stock is from the room is half of that question.
    shortfallLine,
    load: boardLoad(cards.map((card) => card.beo), status.shift),
    whose: (beo) => whoseRoom(beo),
    pace: yourPace(),
    canAccept: (beo) => {
      if (beos.active) return 'You already have an event.';
      if (!status.onTheClock) return 'You are not on the clock.';
      return heldBackFrom(beo);
    },
    subtitle: status.onTheClock
      ? `Day ${clock.day} · ${clock.dayName} · ${formatTime(clock.minutes)}`
      : 'You are not on the clock. Nothing here is yours to take.',
  });
}

function closeBoard() {
  beoPanel.close();
}

/** Anything that owns the screen and stops the world. */
function modalOpen() {
  return (
    beoPanel.open
    || gradePanel.open
    || summaryPanel.open
    || chapterPanel.open
    || choicePanel.open
    || classPanel.open
    || creation.open
  );
}

window.addEventListener('keydown', (event) => {
  if (event.code !== 'Escape') return;
  if (menus.open) menus.back();
  else if (!backOutOfPanel()) pause();
});

/**
 * Escape, or B on a pad: closes whichever panel can be closed, and says
 * whether there was one. Not the chapter, the choice or the hiring screen:
 * those are answered, not dismissed, and that is the point of them.
 */
function backOutOfPanel() {
  if (beoPanel.open) closeBoard();
  // Leaving a class part way through. It files nothing and costs nothing.
  else if (classPanel.open) classPanel.close();
  else if (summaryPanel.open) summaryPanel.close();
  else if (gradePanel.open) gradePanel.close();
  else return modalOpen();
  return true;
}

/**
 * Whether to rename the keys in whatever was written to the screen since.
 * A pad only, and four times a second: the HUD renames as it writes, and this
 * is for the panels and boxes that write their own HTML.
 */
let relabelTick = 0;
function relabelDue() {
  if (currentDevice() !== 'gamepad') return false;
  relabelTick = (relabelTick + 1) % 15;
  return relabelTick === 0;
}

/** Whichever panel is on top, for the pad to move round. */
/*
 * The mouse wheel scrolls whatever long thing is up: the sheet in your hands
 * (which the pointer is locked away from, so the page would never see the
 * wheel), or the panel on top, wherever the pointer happens to be over it.
 */
window.addEventListener(
  'wheel',
  (event) => {
    const target = readingSheet ? sheet.root : panelOnTop() ? scrollerOf(panelOnTop()) : null;
    if (!target) return;
    // Over something that already scrolls by itself, the page does it.
    if (!readingSheet && event.target instanceof Node && target.contains(event.target)) return;
    target.scrollBy({ top: event.deltaY });
    event.preventDefault();
  },
  { passive: false }
);

function panelOnTop() {
  for (const panel of [creation, choicePanel, chapterPanel, gradePanel, summaryPanel, beoPanel]) {
    if (panel.open) return panel.root;
  }
  return null;
}

/** Opens any modal: releases the mouse, hides the HUD, silences the veil. */
function takeScreen() {
  // Told first that this release is on purpose, so it is not taken as a pause.
  veil.suppress(true);
  input.releaseLock();
  hud.setVisible(false);
}

function acceptRefusal(result) {
  switch (result.reason) {
    case 'already-accepted':
      return `You already have ${result.active.beo.code}.`;
    case 'already-done':
      return 'That one is filed.';
    case 'not-on-the-clock':
      return 'Clock in first.';
    case 'not-posted':
      return 'Not posted yet.';
    case 'too-late':
      return 'That deadline has gone.';
    default:
      return 'No.';
  }
}
/*
 * The whole of it, and the whole of it fits on a hand. One list, read in two
 * places: the click-to-play screen and the card on H.
 *
 * There were thirteen lines here once. Seven of them were verbs you might use
 * twice a shift, each with a key that meant nothing anywhere else, and the
 * player was expected to hold the lot in their head while a ranking they
 * could not see decided which one a press reached. Then Q, which turned the
 * sheet over and did nothing else anywhere; the action key does that now.
 */
const CONTROLS = [
  ['W A S D', 'move'],
  ['Mouse', 'look'],
  ['Shift', 'run'],
  ['Space', 'jump'],
  ['E / click', 'do what the prompt says'],
  ['Hold E', 'everything else you can do here'],
  ['R', 'file the room once it is set'],
  ['C', 'set what you carry at the nearest spot on the plan'],
  ['V', 'route line to the objective, on or off'],
  ['Hold F', 'read the sheet; E turns it over'],
  ['G / right-click', 'hold to wind up, let go to throw'],
  ['Q  /  H', 'what do I do?  ·  show or hide this card'],
  ['M  -  +   [  ]', 'sound off, quieter, louder · time slower, faster'],
  ['`', 'standings panel'],
];
const veil = createVeil({ parent: ui, input, onPause: () => pause() });

/*
 * The title screen and the pause menu, and the pad's way round every screen.
 *
 * A paused game is not stepped at all (see the loop at the bottom), which is
 * what pausing means: the clock, the people and the herd all wait. The title
 * is the same overlay, up at boot over whatever the save left.
 */
const padNav = createPadNav();
const menus = createMenus({
  parent: ui,
  settings,
  keyboardControls: CONTROLS,
  hasSave: () => identity.created,
  onContinue: () => resume(),
  onResume: () => resume(),
  onHelp: () => {
    resume();
    showHelp();
  },
  onNewGame: () => {
    menus.hide();
    // Nobody hired yet: the hiring screen is already up under the title.
    if (identity.created) newGame();
    else veil.suppress(true);
  },
  onQuit: () => {
    autosave.flush();
    menus.showTitle();
  },
});

/** Stops the world and puts the pause menu up. Not over a panel, and not twice. */
function pause() {
  if (menus.open || modalOpen()) return;
  // The menu first, then the mouse: letting go of the lock announces itself,
  // and an announcement that arrives before the menu is up asks to pause again.
  menus.showPause();
  veil.suppress(true);
  actionKey.cancel();
  hud.setFan(null);
  input.releaseLock();
}

/** Back to the world from the title or the pause menu. */
function resume() {
  menus.hide();
  padNav.clear();
  if (modalOpen()) return;
  veil.suppress(false);
  // A mouse player gets the mouse back; this is inside their click.
  if (input.device !== 'gamepad') input.requestLock();
}

/**
 * The device changed: every key on screen is renamed, and the card and the
 * title say the right list.
 */
input.onDeviceChange((device) => {
  setDevice(device);
  relabel(ui);
  controlsCard.setControls(device === 'gamepad' ? PAD_ROWS : CONTROLS);
  menus.refresh();
  if (device === 'gamepad') veil.hide();
});

/**
 * The hiring screen, and the one thing in the game that opens itself.
 *
 * Every other modal is opened by the player walking up to something. This one
 * is opened by there being nobody hired yet — at first boot, and again whenever
 * the veil's own button wipes the week — because a game about what a hotel
 * thinks of you cannot start until there is somebody for it to think it about.
 */
const creation = createCreation({
  parent: ui,
  onHire: (choice) => {
    identity.hire(choice);
    theOpening.begin();
    tutorial.begin(clock.day);
    // The floor guide is on for a new starter, whatever an old week left it at.
    settings.set('routeLine', true);
    veil.suppress(false);
    hud.setVisible(true);
    // No toast: the first lesson is the first thing said, and one thing is said at a time.
    autosave.touch();
  },
});

// The first afternoon is built with everything else, once `autosave` and the
// pocket exist; see `theOpening` near the board.

/** Puts the hiring screen up. Boot and the new-week button, and nothing else. */
function askWhoYouAre() {
  takeScreen();
  creation.show({ name: '', body: identity.bodyId });
}

/**
 * `writeUp`, `readTheRoom` and `suspend` come from `sim/escalation.js`, built
 * below once the autosave it writes through exists.
 */

/**
 * Everything a shift needs to be started over — which is also everything a save
 * needs, minus where you happened to be standing.
 *
 * The two were separate for about ten minutes and then turned out to be the
 * same list, which is the good outcome: a checkpoint you cannot serialise is a
 * checkpoint that will quietly diverge from what a reload gives you.
 */
function snapshot({ withPlace = false } = {}) {
  const state = {
    seed: runSeed,
    clock: clock.toJSON(),
    standing: standing.toJSON(),
    /*
     * Its own key, next to the department ledger and never inside it. The two
     * are separate ledgers in the save for the same reason they are separate
     * ledgers in memory: neither is allowed to be read as the other.
     */
    regard: regard.toJSON(),
    shift: shift.toJSON(),
    beos: beos.toJSON(),
    writeups: writeups.toJSON(),
    stores: stores.toJSON(),
    tasks: tasks.toJSON(),
    favors: favors.toJSON(),
    procurement: procurement.toJSON(),
    ghosts: ghosts.toJSON(),
    journal: journal.toJSON(),
    noticed: noticed.toJSON(),
    chapters: chapters.toJSON(),
    /** Which act, the visitor, the finale and how the story ended. */
    story: story.toJSON(),
    /*
     * What you have passed, saved next to what you have crossed. A perk is a
     * thing you earned in an afternoon and it must not be cancellable by
     * closing the tab, the same way a beat you are owed is not.
     */
    classes: classes.toJSON(),
    /*
     * What is lying about. A patch of ice on a corridor floor and a caddy that
     * is going to go off in forty minutes are both facts about the building
     * rather than about this session, so they survive a reload like everything
     * else does.
     */
    pranks: pranks.toJSON(),
    /*
     * A lawn half struck: what is left on the stacks, who is carrying what, and
     * what has gone over. The carts are with the rest of the carts.
     */
    teardown: teardown?.toJSON() ?? null,
    /** Which dinners somebody wants over, and how every dinner so far ended. */
    dinners: dinners?.toJSON() ?? null,
    /** Who is being looked for, who has seen them, and how the looking went. */
    searches: searches?.toJSON() ?? null,
    rescues: rescues?.toJSON() ?? null,
    /** The cart road's jobs, and the pool deck's event: which is on, and how each went. */
    shuttles: shuttles?.toJSON() ?? null,
    blockades: blockades?.toJSON() ?? null,
    deliveries: deliveries?.toJSON() ?? null,
    poolSets: poolSets.toJSON(),
    /** The animal that remembers you, and what it remembers. */
    individuals: individuals.toJSON(),
    opening: theOpening.state,
    tutorial: tutorial.toJSON(),
    pocket: pocket.toJSON(),
    boardRungs: Object.fromEntries(boardRungs),
    identity: identity.toJSON(),
    clothing: clothing.toJSON(),
    /*
     * The supervisor's latch, the dismissal, and the warning — saved, because a
     * conversation you are owed must not be cancellable by closing the tab. He
     * is still coming.
     */
    ...escalation.toJSON(),
  };
  if (!withPlace) return state;
  return {
    ...state,
    setup: setups.ballroom.toJSON(),
    poolSetup: setups.pool.toJSON(),
    carry: carry.toJSON(),
    wheeled: wheeled.toJSON(),
    impairment: impairment.toJSON(),
    wildlife: wildlife.toJSON(),
    entities: entities.toJSON(),
    linen: linenChoice,
    player: { x: player.position.x, y: player.position.y, z: player.position.z, yaw: player.yaw },
  };
}

/**
 * Puts the world back.
 *
 * The card comes back with it, always. There used to be a flag on this that
 * dropped the write-ups, and the one caller that passed it was the three-strike
 * reload — which is now a suspension and moves the clock forward instead. A
 * restore that could silently empty your file was a way of being punished that
 * left no trace of having been punished.
 */
function restore(state) {
  if (!state) return;
  // The seed first: the board is a pure function of it, and everything below
  // looks events up by id.
  if (state.seed) runSeed = state.seed;
  // Before the board: the first afternoon's board is the one room.
  theOpening.load(state.opening);
  tutorial.load(state.tutorial ?? null);
  pocket.load(state.pocket);
  identity.load(state.identity ?? null);
  clothing.load(state.clothing ?? null);
  escalation.load(state);
  clock.load(state.clock);
  standing.load(state.standing);
  regard.load(renamedCrew(state.regard ?? STARTING_REGARD));
  shift.load(state.shift);
  // Before the board: the finale's day is a card only the story knows about,
  // and the pool deck's is one only the pool knows about.
  story.load(state.story ?? null);
  poolSets.load(state.poolSets ?? null);
  beos.load(state.beos, clock, { reference: REFERENCE_BEO });
  stores.load(state.stores ?? null);
  tasks.load(state.tasks ?? null);
  favors.load(state.favors ?? null);
  procurement.load(state.procurement ?? null);
  ghosts.load(state.ghosts ?? null);
  journal.load(state.journal ?? null);
  noticed.load(state.noticed ?? null);
  chapters.load(state.chapters ?? null);
  classes.load(state.classes ?? null);
  pranks.load(state.pranks ?? null);
  teardown.load(beos.active?.beo.teardown ? state.teardown ?? null : null);
  dinners.load(state.dinners ?? null);
  searches.load(state.searches ?? null);
  rescues.load(state.rescues ?? null);
  shuttles.load(state.shuttles ?? null);
  blockades.load(state.blockades ?? null);
  deliveries.load(state.deliveries ?? null);
  individuals.load(state.individuals ?? null);
  /*
   * Before the board is read, and from the save rather than from the standing
   * that has just been restored: the rungs that sized today's board are the
   * ones that were true when it went up, not the ones that are true now.
   */
  boardRungs.clear();
  doorsByDay.clear();
  for (const [day, rungs] of Object.entries(state.boardRungs ?? {})) {
    boardRungs.set(Number(day), rungs);
  }
  applyShortfalls();

  writeups.load(state.writeups);
  // A save can be older than the day it is loaded into — the tab was shut on
  // Monday and opened on Sunday — so the card is aged the moment it is read
  // rather than only when a day turns over under a running game.
  writeups.expire(clock.day);

  if (state.setup) setups.ballroom.load(state.setup);
  else setups.ballroom.clear();
  if (state.poolSetup) setups.pool.load(state.poolSetup);
  else setups.pool.clear();

  if (state.carry) carry.load(state.carry);
  else carry.clear();

  if (state.wildlife) wildlife.load(state.wildlife);
  if (state.impairment) impairment.load(state.impairment);
  else impairment.clear();
  if (state.wheeled) wheeled.load(state.wheeled);
  else wheeled.reset();
  climbing.reset();

  if (state.entities) entities.load(state.entities);
  else scatterProps();
  throwing.clear();

  if (state.linen) setLinen(state.linen);

  breaks.clear();
  readBreakRoom();
  crewTalk.clear();
  jobs.clear();
  for (const npc of npcs) npc.reset();

  const place = state.player;
  player.teleport(
    place ?? { x: level.spawn.x, y: level.spawn.y, z: level.spawn.z }
  );
  player.yaw = place?.yaw ?? level.spawn.yaw;
  rig.yaw = behindBody(player.yaw);
}

/**
 * What the body looks like it is doing, as plain numbers.
 *
 * The only thing the animation layer is ever handed, and it is a *copy*: every
 * field is a number, a boolean or a short string read off a getter, and there
 * is no object in it that the rig could reach through and change. Which is the
 * whole of the promise this run was built on — a rendering change cannot move a
 * measured value, because the renderer is not holding anything measurable.
 *
 * It reads state the game already keeps rather than flags added for it. There
 * is no `isWalking` anywhere: how fast the body is going is how fast the body
 * is going, whether the hands are full is a question the mass table already
 * answers, and whether climbing owns the body is a thing `climbing.js` has said
 * out loud since before any of this.
 */
function bodyReading() {
  const held = hands();
  // A pocketful is not held: no pose, nothing in the hand, both arms free.
  const pocketed = held.count && !held.twoHanded && Boolean(OBJECTS[held.kind]?.pocket);
  return {
    speed: player.speed,
    walkSpeed: feel.walkSpeed,
    grounded: player.grounded,
    /** 0, 1 or 2 — and which it is comes off the mass table, not off here. */
    carrying: held.count && !pocketed ? (held.twoHanded ? 2 : 1) : 0,
    cart: wheeled.active ? wheeled.active.state : null,
    /** In a seat rather than standing on a deck: the golf cart, driven. */
    seated: Boolean(wheeled.active?.state === 'ridden' && wheeled.active.seats.length),
    climb: climbing.state,
    /** Which half of a pull-up, so hauling up and getting over are not one. */
    climbProgress: climbing.progress,
    hidden: Boolean(concealment.hidden),
    windup,
    knocked: knockedFor,
    /** In a conversation: the one clip that is about somebody else being there. */
    talking: Boolean(conversation?.talkingTo),
    /*
     * What a one-handed thing in your hands looks like, as plain numbers, so
     * the body can hold it. The rig reads nothing from the game; this is all it
     * is told about the thing.
     */
    held: held.count && !held.twoHanded && !pocketed && !wheeled.active ? heldLook(held) : null,
  };
}

/** A held thing's shape, size in metres and colour, off its catalogue row. */
function heldLook(held) {
  const row = OBJECTS[held.kind];
  if (!row) return null;
  const colour = held.variant && row.variantSet === 'linenColour' ? LINENS[held.variant]?.colour ?? row.colour : row.colour;
  return { kind: held.kind, shape: row.shape ?? 'box', width: ft((row.radius ?? 0.3) * 2), height: ft(row.height ?? 0.3), colour: colour ?? 0xcccccc };
}

/**
 * What the building sounds like from where the player is standing.
 *
 * The audio layer's only per-frame input, and the same kind of thing
 * `bodyReading()` is: every field is a number, a boolean or a short string, and
 * there is nothing in it that could be reached through and changed. A sound
 * cannot move a measured value because the layer making it is not holding
 * anything measurable.
 *
 * Nothing here is a flag added for audio. Where somebody is standing is where
 * they are standing; what is under their feet is a word the level data has
 * carried next to the floor colour since the floors were coloured; and how much
 * sharper than sober this body is, is `impairment`'s own number divided by
 * `impairment`'s own baseline.
 */
/**
 * Which score is playing: the part of the property the player is in, and
 * outdoors, whether it is night. A reading, like everything the audio is given.
 */
function playScore() {
  const music = audio.music;
  if (!music) return;
  const zone = level.roomAt(player.position.x, player.position.z)?.zone ?? 'boh';
  const minutes = clock.minutes;
  music.frame(
    scoreFor({
      zone: zone === 'event' ? 'ballroom' : zone === 'foh' ? 'frontOfHouse' : 'backOfHouse',
      outdoors: zone === 'outside',
      night: minutes < 6 * 60 + 15 || minutes > 19 * 60 + 45,
    })
  );
}

function soundReading() {
  const here = level.roomAt(player.position.x, player.position.z);
  const cart = wheeled.active;
  return {
    /** The ear: where it is and which way it is pointed. */
    listener: { x: player.position.x, y: player.eye.y, z: player.position.z, yaw: player.yaw },
    zone: here?.zone ?? null,
    surface: here?.floor ?? null,
    /*
     * How much further this body hears than a sober one. One, sober. The pen
     * and the joint already move `earshot`; this is that same movement, as a
     * ratio, so there is one definition of acuity and not two.
     */
    acuity: impairment.baseEarshot > 0 ? impairment.earshot / impairment.baseEarshot : 1,
    /** Metres between footfalls, off the rig, so the sound lands on the step. */
    stride: avatar.legLength * 0.725,
    player: { x: player.position.x, z: player.position.z, grounded: player.grounded },
    /*
     * Everybody else on shift, so that a corridor with somebody in it does not
     * sound like an empty one — which is the thing a player needs to know
     * before deciding whether to get on the cart.
     */
    people: npcs
      .filter((npc) => npc.present)
      .map((npc) => ({
        id: npc.data.id,
        x: npc.position.x,
        z: npc.position.z,
        grounded: true,
        surface: level.roomAt(npc.position.x, npc.position.z)?.floor ?? null,
      })),
    cart: cart
      ? { x: cart.position.x, z: cart.position.z, speed: Math.hypot(cart.velocity?.x ?? 0, cart.velocity?.z ?? 0) }
      : null,
  };
}

/**
 * How long the body is still getting up from being hit by something.
 *
 * Counted here rather than asked of the wildlife module, because nothing in the
 * simulation cares: being hit by a thrown orange costs you what you were
 * holding and nothing else, and the sim has no reason to remember it a second
 * later. The animation does, for exactly as long as it takes to stand up.
 */
const KNOCK_SECONDS = 0.9;
let knockedFor = 0;

/**
 * A short rumble, on a pad that can. Only ever a reading of something that
 * already happened (a knock, a bump in a cart); nothing waits on it.
 */
function buzz(strength, ms) {
  if (input.device === 'gamepad') input.pad.rumble(strength, ms);
}

/** The ridden cart's height last frame, so a jolt up a step can be felt. */
let rideHeight = null;

/** Retaken every time the calendar moves, which is the only checkpoint there is. */
let dayStart = null;

const autosave = createAutosave({ save, collect: () => snapshot({ withPlace: true }) });

/** What is in your pocket. See `sim/pocket.js`. */
const pocket = createPocket({
  impairment,
  player,
  hud,
  autosave,
  unavailable: () => readingSheet || climbing.busy || concealment.hidden,
});

/**
 * What write-ups add up to. See `sim/escalation.js`.
 */
const escalation = createEscalation({
  access,
  applyShortfalls,
  autosave,
  beos,
  carry,
  chapterPanel,
  claims,
  climbing,
  clock,
  concealment,
  favors,
  gateFor,
  ghosts,
  hud,
  impairment,
  level,
  noticed,
  npcs,
  perception,
  player,
  procurement,
  rig,
  setup,
  shift,
  standing,
  stores,
  suspicion,
  takeScreen,
  veil,
  wheeled,
  writeups,
  markDay: () => {
    dayStart = snapshot();
  },
  elapsed: () => elapsed,
});
const { writeUp, readTheRoom, suspend } = escalation;

theOpening = createOpening({
  clock,
  clothing,
  player,
  rig,
  npcs,
  perception,
  bus,
  level,
  autosave,
  applyShortfalls,
  pocket,
  activeBeo: () => beos.active?.beo ?? null,
  markDay: () => {
    dayStart = snapshot();
  },
});

// Everything that changes the world marks it dirty; the debounce turns the
// burst that closing a BEO produces into one write.
for (const one of Object.values(setups)) one.onChange(() => autosave.touch());
stores.onChange(() => autosave.touch());
carry.onChange(() => autosave.touch());
for (const cart of wheeled.all) cart.hold.onChange(() => autosave.touch());
standing.onChange(() => autosave.touch());
shift.onEvent(() => autosave.touch());
beos.onEvent(() => autosave.touch());

// The tab closing is the most common way a session ends, and it gets no
// warning. `pagehide` rather than `beforeunload` because Safari and mobile
// back/forward caching do not reliably fire the latter.
window.addEventListener('pagehide', () => autosave.flush());
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') autosave.flush();
});

/**
 * Picks up where the last session left off.
 *
 * A refused save — wrong version, corrupt, storage disabled — is not an error
 * to recover from, it is just a new week. Saying so once is enough.
 */
function boot() {
  const state = save.read();
  if (!state) {
    if (save.lastError) console.info(`[lion&crown] starting fresh: ${save.lastError}`);
    scatterProps();
    dayStart = snapshot();
    askWhoYouAre();
    return false;
  }
  restore(state);
  dayStart = snapshot();
  // A save from before the hiring screen existed has nobody in it. Rather than
  // guessing a name, ask — the week is still there underneath.
  if (!identity.created) askWhoYouAre();
  return true;
}

/**
 * Back to the first minute of day one, without a page reload.
 *
 * Every system that holds state gets cleared here. It exists because clearing
 * localStorage does *not* clear the running game — a fact that cost an
 * afternoon when a test cleared the save, found no Accept buttons on the board,
 * and turned out to still be holding an event from three blocks earlier.
 *
 * Kept as one list rather than hidden behind each module's own reset, because
 * the failure mode is a system somebody forgot, and a list you can read is the
 * only defence against that.
 */
/**
 * Puts the hotel's clutter back.
 *
 * Seeded off the run seed, so a reload and a reset both give the same world.
 * Called once at boot and again on every reset, because the alternative is a
 * property that gets emptier every time you are fired.
 */
function scatterProps() {
  entities.clear();
  scatterInto({ entities, level, world: level.world, seed: runSeed });
}

/**
 * `rehire` is the difference between the veil's button and a script's reset.
 *
 * Wiping the week and being somebody else are two different things, and only
 * one of them stops the world dead behind a form. The headless scripts reset
 * constantly and are not applying for a job each time, so the identity survives
 * unless somebody actually asked to start again as a new starter.
 */
function hardReset({ seed = null, rehire = false } = {}) {
  save.clear();
  // A seed can be pinned, which is how a headless run gets the same board
  // twice. Play never passes one.
  if (seed) runSeed = seed;
  // Modals first. A grade sheet left open freezes the frame, and a reset that
  // leaves the world running behind a dead panel is indistinguishable from a
  // hang.
  beoPanel.close();
  gradePanel.close();
  summaryPanel.close();
  chapterPanel.root.hidden = true;
  choicePanel.close();
  dialogue.close();
  conversation.forget();
  veil.suppress(false);
  clock.load({ day: 1, minutes: 12 * 60 + 30, scale: 1 });
  summaryShownForDay = null;
  // Not a new starter unless the hiring screen says so. See `beginOpening`.
  theOpening.clear();
  tutorial.clear();
  pocket.fill();
  standing.load(STARTING_STANDING);
  regard.load(STARTING_REGARD);
  escalation.clear();
  // Before the board is touched: a new week's boards are sized by a new week's
  // rungs, and a new week starts everybody where a new hire starts.
  boardRungs.clear();
  doorsByDay.clear();
  shift.load(null);
  story.clear();
  beos.load(null, clock);
  stores.close();
  // Everything, the served suspensions included. This is a new starter rather
  // than a clean week, and a clean week is the other method.
  writeups.wipe();
  tasks.clear();
  favors.clear();
  procurement.clear();
  ghosts.clear();
  journal.clear();
  noticed.clear();
  chapters.clear();
  /*
   * Every perk off, in the one path every reset goes through.
   *
   * `bench.mjs` resets through here and then prints four numbers that the
   * project treats as a contract. A class that survived a reset would put an
   * aim cone and a longer throw into that run, and the grid would move for a
   * reason nobody reading it could see. It is cleared here, once, rather than
   * in each caller, so there is no second path to forget about.
   */
  classes.clear();
  classPanel.close();
  pranks.clear();
  teardown.clear();
  dinners.clear();
  searches.clear();
  rescues.clear();
  shuttles.clear();
  blockades.clear();
  deliveries.clear();
  poolSets.clear();
  individuals.clear();
  carry.clear();
  for (const one of Object.values(setups)) one.clear();
  wheeled.reset();
  climbing.reset();
  perception.clear();
  suspicion.clear();
  concealment.clear();
  impairment.clear();
  claims.clear();
  // The sighting log is not cleared. Losing your job does not unsee a coyote.
  wildlife.clear();
  access.clear();
  access.settle();
  throwing.clear();
  bus.clear();
  scatterProps();
  breaks.clear();
  readBreakRoom();
  crewTalk.clear();
  jobs.clear();
  for (const npc of npcs) npc.reset();
  player.teleport({ x: level.spawn.x, y: level.spawn.y, z: level.spawn.z });
  player.yaw = level.spawn.yaw;
  rig.yaw = behindBody(level.spawn.yaw);
  setLinen(LINEN_ORDER[0]);
  // You turn up for your first shift in your uniform. Where it came from is the
  // hotel's business.
  clothing.clear();
  applyShortfalls();
  dayStart = snapshot();
  if (rehire) {
    identity.clear();
    askWhoYouAre();
  }
}

/** Wipes the save and starts the week again. Offered on the click-to-play veil. */
function newGame() {
  hardReset({ rehire: true });
}

/**
 * Every standing change the player earns, said out loud.
 *
 * Without this the matrix is invisible: you would learn three shifts later that
 * the kitchen hates you and never find out which favour did it. One line naming
 * the primary and the worst of the fallout is enough to teach the rule.
 */
standing.onChange((event) => {
  if (!event.reason) return;
  const [primary, ...fallout] = event.changes;
  /*
   * A change list can be empty, and this line is a crash without it.
   *
   * `applyDirect` drops moves that come to nothing — a department already
   * pinned at the floor, or an award that rounds to zero — and then fires the
   * event anyway, correctly, because something did happen and it happened to
   * move nobody. The way it arrives in play is a ghost: an event goes ahead
   * without you, badly, against a department you have already ruined, and the
   * frame loop dies reading a department off nothing.
   *
   * Nothing moved, so there is nothing to say about it.
   */
  if (!primary) return;
  const worst = fallout.sort((a, b) => a.delta - b.delta)[0];
  const name = (id) => departmentsById[id].name;
  // Whole points, except a move smaller than one, which would otherwise read
  // as "Banquets 0" for something that did move.
  const signed = (value) => `${value > 0 ? '+' : ''}${value.toFixed(Math.abs(value) < 1 ? 1 : 0)}`;
  hud.say(
    worst && worst.delta < -0.5
      ? `${event.reason}: ${name(primary.department)} ${signed(primary.delta)}, ${name(
          worst.department
        )} ${signed(worst.delta)}`
      : `${event.reason}: ${name(primary.department)} ${signed(primary.delta)}`,
    { tone: primary.delta >= 0 ? 'good' : 'bad', seconds: 5 }
  );
});

/**
 * What each of these takes personally, which is the other half of the ledger.
 *
 * A debt is between two people. The department already gets its six points for
 * a favour settled and its fourteen for one left standing — that is Banquets
 * deciding whether you are reliable. This is the person deciding whether *they*
 * are going to do it again, and it is by far the bigger number, because being
 * let down by somebody you did a favour for is not a departmental matter.
 *
 * Only the section has one of these. Settling with a gate moves their
 * department and nothing else, because a gate *is* their department.
 */
const REGARD_SETTLED = 16;
const REGARD_REFUSED = -22;

favors.onEvent((event, payload) => {
  if (event === 'called') {
    const said = payload.person.says?.favor;
    hud.say(
      `${payload.person.name}${said ? `: "${said}"` : ' is calling in the favour.'} ${FAVOR_MINUTES} minutes.`,
      { tone: 'warn', seconds: 7 }
    );
    return;
  }
  const favor = payload?.favor ?? payload;
  const who = favor?.staffId;
  if (!who) return;
  if (event === 'settled') {
    think(who, REGARD_SETTLED, `You turned up for ${STAFF_BY_ID[who]?.name ?? who}`);
  } else if (event === 'refused') {
    think(who, REGARD_REFUSED, `You left ${STAFF_BY_ID[who]?.name ?? who} standing there`);
  }
});

tasks.onEvent((event, task) => {
  if (event !== 'expired') return;
  if (task.source === 'favor') {
    favors.refuse(task.favorId, { expired: true });
    hud.say(`You left ${STAFF_BY_ID[task.staffId].name} standing there.`, {
      tone: 'bad',
      seconds: 6,
    });
  } else {
    hud.say(`${STAFF_BY_ID[task.staffId].name} gave up waiting.`, { tone: 'bad', seconds: 5 });
  }
  tasks.drop(task.id);
  autosave.touch();
});

beos.onEvent((event, payload) => {
  if (event === 'ghost') {
    // Quietly. The end-of-shift summary is where this lands properly; a toast
    // per abandoned event would bury everything else on a busy board.
    return;
  }
  /*
   * Past the setup time a room is late, not lost. Every minute until the doors
   * comes off its grade (`LATE_PER_MINUTE` in `sim/grading.js`), and the grade
   * is what its departments make of it, so late costs standing in proportion
   * to how late. It is not a write-up: a new starter who is twenty minutes
   * over on a room they are still setting is a slow new starter, and three of
   * those used to be a suspension in the first week.
   */
  if (event === 'due') {
    hud.say(`LATE: ${payload.beo.code} was due at ${payload.beo.setupBy}. Doors at ${payload.beo.doors}; every minute costs the grade.`, {
      tone: 'warn',
      seconds: 7,
    });
    return;
  }
  if (event !== 'deadline') return;
  // The doors grade the room itself. Whatever is standing when the guests walk
  // in is what they walked into, and letting the player choose when to be
  // judged after the fact would make the deadline advisory. A room still
  // unset at the doors is the one that is a write-up.
  hud.say(`DOORS: ${payload.beo.code} opened at ${payload.beo.doors} on an unfinished room.`, {
    tone: 'bad',
    seconds: 6,
  });
  writeUp(REASONS.DEADLINE, payload.beo.code);
  if (payload.beo.teardown) fileTeardown({ auto: true });
  else fileSetup({ auto: true });
});

/** What a loss costs as a share of its full price: eased in the first act (`FIRST_ACT_EASE`). */
function firstActEase() {
  return story.act <= 1 ? FIRST_ACT_EASE : 1;
}

/** A room's award, with a loss on a room the doors caught eased in the first act. */
function awardOf(beo, result, auto) {
  const award = awardFor(beo, result);
  if (!auto) return award;
  const ease = firstActEase();
  return Object.fromEntries(Object.entries(award).map(([id, v]) => [id, v < 0 ? Math.round(v * ease * 10) / 10 : v]));
}

/** The department a room belongs to, when it is not Banquets' own. */
const ownerSpared = (beo) => (beo.owner && beo.owner !== 'banquets' && beo.award?.[beo.owner] ? [beo.owner] : []);

/**
 * Grades a strike and closes it out: what is on the dock, what went over on
 * the way, and when. The same two routes a room has — the key and the
 * deadline — through the one function, and the same award path afterwards.
 */
function fileTeardown({ auto = false } = {}) {
  const active = beos.active;
  if (!active?.beo.teardown) return null;
  const minutesLate = Math.max(0, clock.minutes - active.times.setupBy);
  const result = teardown.grade({ minutesLate });
  const beo = active.beo;
  beos.close({ grade: result.letter, score: result.score, breakdown: result.breakdown }, clock);
  teardown.finish();
  applyShortfalls();
  const event = standing.applyAll(awardOf(beo, result, auto), { reason: null, spared: ownerSpared(beo) });
  takeScreen();
  gradePanel.show({ beo, result, changes: event.changes, auto });
  autosave.touch();
  return result;
}

/**
 * Grades whatever is standing in the ballroom and closes the event out.
 *
 * Called by the player pressing submit, and by the deadline passing. Both
 * routes go through here so a late submission and an auto-grade cannot end up
 * scored by two different code paths.
 */
function fileSetup({ auto = false } = {}) {
  const active = beos.active;
  if (!active) return null;
  // Held now: once the event is closed, `setup` is the ballroom again.
  const venue = setups[venueOf(active.beo)];

  const minutesLate = Math.max(0, clock.minutes - active.times.setupBy);
  const result = gradeSetup({
    spec: active.beo.setup,
    tables: venue.tables,
    anchorAt: venue.anchorAt,
    minutesLate,
    shortfalls: active.beo.shortfalls ?? [],
    // Sucky Sucky, as one number on one of the six marks. Zero until it is sat.
    tidyForgiveness: perks.tidy.forgiveness,
  });

  const beo = active.beo;
  // A job's own part is a quarter of its grade (`JOB_SHARE`): the glasses
  // carried out, the urn by a station. The room is the rest.
  const part = beo.job ? jobs.part() : null;
  if (part !== null) {
    result.score = result.score * (1 - JOB_SHARE) + part * JOB_SHARE;
    const grade = letterFor(result.score);
    result.letter = grade.letter;
    result.multiplier = grade.multiplier;
    result.breakdown = [...(result.breakdown ?? []), { label: beo.job === 'refresh' ? 'Glasses' : 'Urn', value: part, detail: beo.job === 'refresh' ? `${Math.round(part * 6)}/6 out to the kitchen pass` : part >= 1 ? 'by a station' : 'not by a station' }];
  }
  if (beo.job) jobs.finish();
  beos.close({ grade: result.letter, score: result.score, breakdown: result.breakdown }, clock);
  stores.close();
  applyShortfalls();

  /*
   * The finale, weighed: the letter, who was behind you, and his file. Before
   * the room's own award, because that ripples, and who was behind you is who
   * was behind you while you set it.
   */
  if (beo.finale) settleFinale({ letter: result.letter });

  // Quietly, because the grade sheet is about to say it properly and two
  // announcements of the same thing read as a bug. The room's owner takes its
  // own share and none of Banquets' ripple (`spared` in `sim/standing.js`).
  const event = standing.applyAll(awardOf(beo, result, auto), { reason: null, spared: ownerSpared(beo) });

  venue.clear();
  carry.clear();

  ghosts.record(result.score);
  takeScreen();
  gradePanel.show({ beo, result, changes: event.changes, auto });
  return result;
}

/**
 * The finale is over, and what it came to lands. See `OUTCOME`, `WIN` and
 * `LOSS` in `data/story.js`. Losing is a trial for the agency and a step off
 * Banquets, and the week goes on.
 */
function settleFinale(how) {
  const result = story.settle(how);
  if (!result) return null;
  if (result.won) {
    standing.applyAll(WIN, { reason: 'The section kept its rooms' });
  } else {
    story.beginTrial(clock.day);
    // A whole step, like any verdict. Already on the bottom one, it is the trial alone.
    const banquets = standing.get('banquets');
    if (rungFor(banquets).id !== 'hostile') {
      standing.floorTo('banquets', belowRung(banquets), { reason: 'The agency has the big rooms' });
    }
  }
  autosave.touch();
  return result;
}

/** Back to work after a panel the story put up. */
function resumeFromStory() {
  veil.suppress(false);
  veil.show();
  autosave.touch();
}

/** A scene with the one name in it filled in. */
function scene(row, values = {}) {
  const fill = (text) => text.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');
  return { ...row, speaker: fill(row.speaker), lines: row.lines.map(fill), kicker: 'After' };
}

/**
 * The last act, one panel at a time, from wherever the story has got to.
 *
 * Saved as a step rather than as a screen, so closing the tab in the middle of
 * it opens it again at the same question.
 */
function runStoryStep() {
  const step = story.due;
  if (!step || modalOpen()) return;
  const who = story.feeder;
  const name = who ? who.name.split(' ')[0] : 'somebody';
  const result = story.result;
  takeScreen();
  switch (step) {
    case 'outcome':
      chapterPanel.show(
        scene(result?.missed ? SCENES.missed : result?.won ? SCENES.won : SCENES.lost),
        { onDone: () => { story.advance('ask'); resumeFromStory(); } }
      );
      return;
    case 'ask':
      chapterPanel.show(scene(SCENES.ask), {
        onDone: () =>
          choicePanel.show({
            title: SCENES.ask.title,
            speaker: SCENES.ask.speaker,
            line: 'Who has been feeding him?',
            options: CREW.map((person) => ({ id: person.id, label: person.name })),
            onPick: (id) => answerGuess(id),
          }),
      });
      return;
    case 'truth':
      chapterPanel.show(scene(SCENES.truth, { name }), {
        onDone: () => { story.advance('choose'); resumeFromStory(); },
      });
      return;
    case 'choose':
      choicePanel.show({
        title: SCENES.choose.title,
        speaker: SCENES.choose.speaker,
        line: scene(SCENES.choose, { name }).lines[0],
        options: ENDINGS.map((row) => ({ ...row })),
        onPick: (choice) => endStory(choice, name),
      });
      return;
    default:
      resumeFromStory();
  }
}

/**
 * Your answer to who has been feeding him.
 *
 * Right, and it is worth something small. Wrong, and it lands on the person you
 * named: what they think of you drops hard, and they take it by their shape.
 * The truth comes out either way.
 */
function answerGuess(id) {
  const guess = story.guess(id);
  story.advance('truth');
  const person = STAFF_BY_ID[id];
  if (guess.right) {
    standing.applyDirect(SAW_IT, { reason: 'You saw it first' });
    chapterPanel.show(scene(SCENES.saw), { onDone: () => resumeFromStory() });
    return;
  }
  think(id, WRONGED, `You named ${person.name}`);
  /*
   * What they do about it lands once you have heard them say it, so anything
   * it puts on the screen (a write-up can be a suspension) comes after their
   * words and not underneath them.
   */
  chapterPanel.show(
    {
      kicker: 'After',
      title: 'The Staff Lot, After',
      speaker: person.name,
      lines: [person.says?.accused ?? 'Me?', 'Somebody else will tell you. It was not me.'],
    },
    {
      onDone: () => {
        witnesses.wronged(person);
        resumeFromStory();
      },
    }
  );
}

/** Cover for him, or report him. The save goes on after either. */
function endStory(choice, name) {
  story.end(choice);
  if (choice === 'report') standing.applyAll(REPORTED, { reason: `You reported ${name}` });
  takeScreen();
  chapterPanel.show(scene(choice === 'report' ? SCENES.reported : SCENES.covered, { name }), {
    onDone: () => resumeFromStory(),
  });
}

shift.onEvent((event, payload) => {
  if (event === 'day') {
    // Every way the calendar can move ends up here — sleeping, a suspension,
    // or a clock left running past midnight — which makes it the one place the
    // card has to be aged.
    const off = writeups.expire(payload.day);
    if (off.length) {
      hud.say(
        off.length === 1
          ? `${describeWriteup(off[0].reason)} came off your card today.`
          : `${off.length} write-ups came off your card today.`,
        { tone: 'good', seconds: 6 }
      );
      readTheRoom();
      escalation.armWarning();
    }
    dayStart = snapshot();
    return;
  }
  if (event === 'missed') {
    /*
     * The write-up is the consequence, so it is the line that stays on screen:
     * "WRITTEN UP (2/3) — No show. Clears day 9." It used to be said and then
     * talked over in the same frame by a NO SHOW line, so a player missed a
     * whole shift, was written up for it, and never read that they had been.
     * The HUD's own state line says NO SHOW for the rest of the day.
     */
    const filed = writeUp(REASONS.NO_SHOW, `${payload.shift.name}, day ${payload.day}`);
    if (!filed && !escalation.dismissed) {
      hud.say(`NO SHOW: ${payload.shift.name} started ${formatSpan(payload.minutesLate)} ago.`, { tone: 'bad', seconds: 6 });
    }
  }
});

/**
 * What a target does when you press E at it.
 *
 * Kept as one dispatch rather than a method on each interactable, because every
 * one of these needs to reach several systems at once — the clock, the shift
 * record, the HUD — and hanging that off a data file would put the game's rules
 * in `src/data/`, which is exactly where they must not be.
 */
function use(target, status) {
  switch (target.kind) {
    case 'timeclock': {
      if (status.canClockOut && !status.canClockIn) {
        const out = shift.clockOut(clock);
        if (out.ok) {
          sound.cue('clock.punch');
          // Everything still on the board goes ahead without you the moment you
          // walk out, which is what makes clocking out a decision rather than a
          // formality.
          beos.sweepDay(clock.day);
          showSummary(status);
        }
        return;
      }
      /*
       * The clock will not take a punch from somebody in their own clothes.
       *
       * Checked here rather than inside the shift tracker, which is about time:
       * what the window is, whether you are inside it, whether you are late.
       * This refusal is not about time at all, and folding it in would give the
       * tracker a second thing to be right about.
       */
      if (!clothing.canClockIn) {
        hud.say(
          `In ${describeClothing(clothing.state)}. The clock wants a uniform; yours is in your locker.`,
          { tone: 'warn', seconds: 4 }
        );
        return;
      }
      const punch = shift.clockIn(clock);
      if (!punch.ok) {
        hud.say(refusal(punch, status), { tone: 'warn' });
        return;
      }
      // The one sound in the game that means the shift has started.
      sound.cue('clock.punch');
      // The guided day's lessons say what is next; nothing else does then.
      // What is next is on the task card; the line is only the punch.
      if (punch.punctuality === 'on-time') {
        hud.say(`Clocked in at ${formatTime(punch.at)}. On time.`, { tone: 'good', seconds: 5 });
        return;
      }
      hud.say(`Clocked in at ${formatTime(punch.at)}, ${formatSpan(punch.minutesLate)} late.`, {
        tone: 'bad',
        seconds: 5,
      });
      // A no-show has already been written up by the late window closing;
      // writing them up again for finally turning up is two strikes for one
      // offence.
      if (punch.punctuality === 'late') {
        writeUp(REASONS.LATE, `${formatSpan(punch.minutesLate)} past the start`);
      }
      return;
    }

    case 'endday': {
      // The one thing you are not allowed to walk away from.
      if (chapters.blocking) {
        takeScreen();
        chapterPanel.show(chapters.pending);
        return;
      }
      /*
       * Before anything about a day ending: asleep in the small hours, before
       * today's shift has opened, you wake when it opens, not a day later.
       * Sleeping at half past midnight slept through the whole of that day's
       * shift, which is a no-show.
       */
      if (shift.status(clock).phase === 'before') {
        const today = shift.windowFor(clock.day);
        clock.advanceTo(today.opens);
        hud.say(`Day ${clock.day}. ${today.shift.name} opens now.`, { tone: 'info', seconds: 5 });
        autosave.touch();
        return;
      }
      // Anything still posted resolves without you before the day turns over.
      beos.sweepDay(clock.day);
      // And anybody still turning something over has slept on it. Sleeping is
      // the other way a day can end without the clock ever being punched out.
      noticed.closeOut();
      // Thefts nobody saw get counted overnight.
      const found = procurement.settlePending(clock.day + 1);
      if (found.length) {
        hud.say(`${found.length} missing from somebody's count overnight.`, {
          tone: 'bad',
          seconds: 5,
        });
      }
      // Sleeping is the only thing that moves the calendar. Advancing to the
      // moment the next clock opens, rather than to midnight, means you always
      // wake up with the same amount of runway — the day starts when the day
      // starts.
      const tomorrow = shift.windowFor(clock.day + 1);
      /*
       * Far enough forward to be certain it is tomorrow, and not merely
       * forward.
       *
       * `atLeast: 1` was wrong for a morning shift and quietly so. Sleep at
       * six in the morning with tomorrow's clock opening at one in the
       * afternoon and "at least a minute later" is satisfied by one o'clock
       * *this* afternoon: the day counter never moved, the game woke you into
       * a day whose only shift had already ended, and the banner said "Day 3,
       * PM Banquet opens now" over a Wednesday morning that was still
       * Wednesday. A second sleep fixed it, which is why it went unnoticed —
       * the calendar was only ever one press behind.
       *
       * The night has to be long enough to reach midnight, so that is what is
       * asked for: everything left of today, plus a minute.
       */
      clock.advanceTo(tomorrow.opens, { atLeast: MINUTES_PER_DAY - clock.minutes + 1 });
      // The rolling window moved, so whether he is still holding it back is a
      // different question this morning than it was last night.
      readTheRoom();
      hud.say(`Day ${clock.day}. ${tomorrow.shift.name} opens now.`, { tone: 'info', seconds: 5 });
      return;
    }

    case 'lockers': {
      /*
       * The only place in the building clothes change, which is the rule that
       * gives the slot its geography: the locker is in the break room, the
       * marble is two hundred feet east of it, and deciding to cross the lobby
       * in civvies is therefore a decision you make before you set off rather
       * than one you make when you get there.
       *
       * Any change of clothes takes a smell off you — it is a change of
       * clothes. The spare is what you reach for when the smell is the reason
       * you came, and it is one press away for exactly that reason.
       */
      const swap = clothing.change();
      const laundered = impairment.changeUniform();
      hud.say(
        laundered
          ? `Into ${describeClothing(swap.to)}. Whatever that was, it is in the laundry now.`
          : `Into ${describeClothing(swap.to)}.`,
        { tone: 'info', seconds: 3 }
      );
      autosave.touch();
      return;
    }

    case 'beoboard':
      openBoard();
      return;

    case 'station': {
      // Helping yourself. The only option a station offers, because there is
      // nobody standing here to ask.
      const entry = unresolvedFor(target.department);
      if (!entry) return;
      const person = pickHolder(target.department);
      if (!person) return;
      const result = procurement.resolve({
        person,
        entry,
        kind: 'take',
        beo: beos.active?.beo ?? null,
      });
      if (!result.ok) {
        hud.say(result.reason, { tone: 'warn', seconds: 3 });
        return;
      }
      const released = releaseFor(entry);
      announceTake(entry);
      applyShortfalls();
      // One line: two toasts in a frame is the second one only.
      const where = releasedLine(released);
      hud.say(where ? `${takeLine(person)} ${where}` : takeLine(person), { tone: 'info', seconds: 6 });
      autosave.touch();
      return;
    }

    /*
     * Collecting what a resolved shortfall left somewhere.
     *
     * Deliberately the same shape as a shelf: it fills your arms or your cart,
     * it claims what it gives you against the room, and it can be empty. The
     * only difference is that it is not in banquet storage, and the distance
     * between here and the ballroom is the entire cost of the shortfall that
     * put it here.
     */
    case 'delivery': {
      const pile = stores.waitingAt(target.prop.id);
      if (!pile) return;
      const item = OBJECTS[pile.item];
      /*
       * In the colour the sheet asked for. What a department lets go of is the
       * balance of *this* room's linen, not a stack of whatever — and a pile
       * with no colour on it graded as the wrong colour on every table it
       * dressed, so a linen shortfall covered perfectly could never be better
       * than a B.
       */
      const variant = item.variantSet === 'linenColour' ? beos.active?.beo.setup?.linen ?? null : null;
      const into = hands();
      const room = into.room(pile.item, variant);
      if (room <= 0) {
        const other = into.kind && into.kind !== pile.item ? OBJECTS[into.kind] : null;
        hud.say(
          other
            ? `Your hands are full of ${other.plural.toLowerCase()}. Set them in the room first, or tap [G] to put them down.`
            : wheeled.active
              ? `Cart is already full of ${item.plural}.`
              : `Your arms are already full of ${item.plural}.`,
          { tone: 'warn', seconds: 4 }
        );
        return;
      }
      const got = stores.collect(target.prop.id, room);
      if (got <= 0) return;
      // Claimed the moment it leaves the pallet, exactly like the shelf. A
      // round fetched off the dock and abandoned in a corridor is as missing as
      // one drawn from storage and abandoned in a corridor.
      into.claim = beos.active?.beo.id ?? null;
      const result = into.add(pile.item, variant, got);
      if (result.returned) stores.give(result.returned.kind, result.returned.count);
      const left = stores.waitingAt(target.prop.id);
      const eventRoom = beos.active?.beo.room ? objectives.roomName(beos.active.beo.room) : null;
      hud.say(
        `${got} ${got > 1 ? item.plural : item.name}.` +
          (left ? `  ${left.count} still here.` : eventRoom ? `  Take ${got > 1 ? 'them' : 'it'} to the ${eventRoom}.` : ''),
        { tone: 'info', seconds: 3 }
      );
      return;
    }

    case 'supply': {
      const item = OBJECTS[target.supplies];
      const variant = target.supplies === 'linen' ? linenChoice : null;
      const into = hands();
      const room = into.room(target.supplies, variant);
      if (room <= 0) {
        hud.say(
          wheeled.active
            ? `Cart is already full of ${item.plural}.`
            : `Arms already full of ${item.plural}.`,
          { tone: 'warn', seconds: 2 }
        );
        return;
      }

      // The shelf can be empty. This is the moment a goods shortfall stops
      // being a line on a sheet and becomes a room you cannot finish.
      // Strays lying on the shelf's own spot go back on it: the prompt there is
      // the shelf's, so nothing lying under it can ever be picked up, and seven
      // chairs from a room three days old sat there while the shelf said it
      // was empty (`fullgame.mjs`, day eight).
      if (stores.available(target.supplies) <= 0) {
        const strays = straysAt(target.supplies);
        for (const e of strays) entities.remove(e.id);
        if (strays.length) stores.give(target.supplies, strays.length);
      }
      const got = stores.draw(target.supplies, room);
      // Counted against the room it was pulled for, from the moment it leaves
      // the shelf. Nothing marks it as yours; it is marked as theirs.
      if (got > 0) into.claim = beos.active?.beo.id ?? null;
      if (got <= 0) {
        const owner = shortfallOwnerFor(target.supplies);
        const parked = loadedCartWith(target.supplies);
        const cartRoom = parked ? level.roomAt(parked.position.x, parked.position.z) : null;
        hud.say(
          owner
            ? `No ${item.plural} left. ${owner} is holding the rest.`
            : parked
              ? `No ${item.plural} left on the shelf. ${parked.hold.count} are on the cart you left${cartRoom?.name ? ` in ${cartRoom.name}` : ''}: follow the arrows.`
              : `No ${item.plural} left on the shelf.`,
          { tone: 'bad', seconds: 4 }
        );
        return;
      }

      const result = into.add(target.supplies, variant, got);
      if (result.returned) stores.give(result.returned.kind, result.returned.count);
      const what = variant ? `${LINENS[variant].name.toLowerCase()} ${item.plural}` : item.plural;
      const left = stores.available(target.supplies);
      hud.say(`${got} ${what}.  ${Number.isFinite(left) ? `${left} left on the shelf` : 'Plenty more on the shelf'}.`, {
        tone: 'info',
        seconds: 2.5,
      });
      return;
    }

    default:
      hud.say(`${target.label}: nothing to do here yet.`, { tone: 'info', seconds: 2 });
  }
}

/**
 * What the accepted event is short of, and what that does to the property.
 *
 * Three shapes, and each one bites in a different place:
 *
 *   goods    banquet storage is short, and `stores` will simply run out.
 *   staging  the grade takes it; nothing physical changes.
 *   access   something you work with is unavailable — the cart is padlocked in
 *            its cage, or the near ballroom doors are chained and the walk goes
 *            the long way round past the far ones.
 *
 * Applied on accept, on resolve, and on close, all through here, so the world
 * can never disagree with the sheet.
 */
function applyShortfalls() {
  const active = beos.active;
  const list = active?.beo.shortfalls ?? [];
  const denied = new Set(
    list
      .filter((entry) => !entry.resolved)
      .map((entry) => SHORTFALLS[entry.id]?.denies)
      .filter(Boolean)
  );

  cartLocked = denied.has('cart');
  if (cartLocked && wheeled.active) wheeled.release();

  for (const barrier of LEVEL.barriers) {
    /*
     * Two things can put a chain on a door and either is enough: a shortfall
     * that says the run is blocked, or Engineering not thinking enough of you
     * to leave it off. The second is what makes the long way round something
     * you can stop paying rather than something you wait out, and it is not a
     * key — it goes back on after a bad shift.
     */
    const shut = denied.has(`barrier:${barrier.id}`) || !access.allows(barrier.id);
    /*
     * A chain going on or coming off a door is a thing that happens in a
     * building and makes a noise, and it is worth hearing: it is the moment a
     * route you were using stops existing, or starts.
     *
     * Not on the first pass. Every barrier that starts the day chained changes
     * state on the frame the building is built, and a game that opens with two
     * doors slamming has told the player something happened.
     */
    if (barriersKnown && shut !== level.closedBarriers.includes(barrier.id)) {
      sound.cue('door', {
        x: ft((barrier.rect[0] + barrier.rect[2]) / 2),
        z: ft((barrier.rect[1] + barrier.rect[3]) / 2),
      });
    }
    level.setBarrier(barrier.id, shut);
  }
  barriersKnown = true;
}

/** Whether the chains have been set once, so the first pass is silent. */
let barriersKnown = false;

/** Set by applyShortfalls. The cart is in a cage and Engineering has the key. */
let cartLocked = false;

/**
 * One shortfall, as the sheet and the board card read it.
 *
 * This is the only place in the game that says what rung you are on, and it
 * says it without ever using the word. The line names the place the thing will
 * land if you get it covered — their cart, the branch, the shelf — and whether
 * that is a walk at all. Learn the ladder by reading the work: a player who
 * notices that Housekeeping's linen now says "the linen shelf, no fetch" has
 * learned more about where they stand than a number on a panel could tell them,
 * because it is the thing the number was *for*.
 *
 * Read live off standing rather than off anything stored, so it is true of the
 * board before you accept and true of the sheet while you work.
 */
function shortfallLine(entry) {
  const shortfall = SHORTFALLS[entry.id];
  const at = placeFor(shortfall, rungFor(seenValue(shortfall.owner)).id);
  const place = PLACES[at];
  const pile = at ? stores.waitingAt(at) : null;
  return {
    id: entry.id,
    label: entry.amount ? `${shortfall.label} (${entry.amount})` : shortfall.label,
    resolved: Boolean(entry.resolved),
    holderName: departmentsById[shortfall.owner].name,
    /*
     * Where it will land, or where it already is. An access shortfall lands
     * nowhere — the thing it is about is a padlock and a chain — so it says so
     * rather than naming a place you would walk to for nothing.
     */
    lands: shortfall.item
      ? place.arrived
        ? `${place.spot}, no fetch`
        : place.spot
      : 'nothing to fetch',
    /** Whether there is a walk in it, which is the whole of what the rung buys. */
    fetch: Boolean(shortfall.item) && !place.arrived,
    /** And where it is this minute, once somebody has let go of it. */
    waiting: pile
      ? `at ${place.spot}`
      : entry.delivered
        ? 'in the room'
        : entry.resolved && place.arrived
          ? 'on the shelf'
          : entry.resolved
            ? 'collected'
            : null,
  };
}

/** The shortfall list the sheet draws from. */
function shortfallsForSheet() {
  return (beos.active?.beo.shortfalls ?? []).map(shortfallLine);
}

/**
 * Reading the sheet.
 *
 * Held, not toggled, and it does not pause anything. The board in the alcove
 * stops the world so you can study it; this is the same information in the
 * middle of the room with the clock running, and the cost is that you cannot
 * work while you hold it. The handle goes out of your hands the moment the
 * paper comes up.
 */
let readingSheet = false;
/**
 * Which side of the paper is up: the event, or your own card.
 *
 * Two pages rather than two keys, because the card must be reachable from
 * anywhere at any time and there is exactly one thing already in your hands
 * that costs you the right amount to look at.
 */
let sheetPage = 'sheet';
/**
 * How long the sheet has actually been in front of your face, in seconds.
 *
 * Not "did you accept the event" and not "did you open the board". You held the
 * paper up and looked at it. Nothing else in the game needed the distinction
 * until the anchors did.
 */
let sheetSeconds = 0;
const SHEET_READ = 1.5;
const sheetRead = () => sheetSeconds >= SHEET_READ;

function runSheet(dt) {
  const wanted = keys.held('sheet') && allows('sheet');
  if (wanted && !readingSheet && wheeled.active) wheeled.release();
  // Always opens on the sheet. The card is a page you turn to, and a held key
  // that remembers which side it was on last is a held key that shows you the
  // wrong one in the middle of a room you are trying to set.
  if (wanted && !readingSheet) sheetPage = 'sheet';
  // Paper coming up, and paper being turned over. Both are the same sound.
  if (wanted !== readingSheet) sound.cue('page');
  readingSheet = wanted;

  if (!readingSheet) {
    sheet.hide();
    return false;
  }
  // The action key turns the page: it has nothing else to do while your
  // hands are full of paper.
  if (pressed('interact')) {
    sheetPage = sheetPage === 'card' ? 'sheet' : 'card';
    sound.cue('page');
  }

  /*
   * A long sheet scrolls in your hands: the right stick or the d-pad on a pad
   * (the camera holds still while you read), the mouse wheel on a keyboard
   * (see the wheel listener by the panels).
   */
  const padReading = input.padState;
  if (padReading?.scroll && Math.abs(padReading.scroll.y) > 0) sheet.root.scrollBy?.({ top: (padReading.scroll.y * SCROLL_SPEED) / 60 });
  if (input.pad.nav?.('down')) sheet.root.scrollBy?.({ top: 120 });
  if (input.pad.nav?.('up')) sheet.root.scrollBy?.({ top: -120 });

  if (sheetPage === 'card') {
    sheet.showCard(cardReadout());
    return true;
  }
  if (beos.active) sheetSeconds += dt;
  if (beos.active) sheet.show(beos.active, clock, shortfallsForSheet());
  else sheet.showEmpty();
  return true;
}

/**
 * Your card, in the words a person would use.
 *
 * Assembled here rather than in the sheet because it is three modules' worth of
 * fact — the file, the calendar and what the next one costs — and the sheet's
 * job is to draw a page, not to know what a suspension is.
 */
function cardReadout() {
  return {
    day: clock.day,
    strikes: writeups.count,
    max: writeups.max,
    served: writeups.served,
    next: writeups.nextSuspension(),
    entries: writeups.entries.map((entry) => ({
      reason: entry.reason,
      what: describeWriteup(entry.reason),
      detail: entry.detail ?? '',
      day: entry.day,
      clears: entry.clears,
      in: Math.max(0, entry.clears - clock.day),
    })),
  };
}

/**
 * Winding up and letting go.
 *
 * Held, like the sheet. The longer you hold it the harder it goes, and the arc
 * comes entirely from the mass of whatever is in your hands — nothing here
 * knows what that is. Releasing with nothing held drops nothing and costs
 * nothing.
 *
 * Tapping the same key with a full wind-up of zero is how you *put something
 * down*, which is a different verb with different consequences: no arc, no
 * impact, a very small noise, and the object is on the floor at your feet. That
 * is the counterplay to being seen holding what you just threw.
 */
const WINDUP_FULL = 0.9;
const DROP_BELOW = 0.18;
let windup = 0;

function runThrow(dt) {
  const holding = hands();
  // Putting something down is a short tap and always works; only the throw
  // waits for its day. Locked with the throw, a new starter who had picked up
  // a wet floor sign by mistake had no way to put it down again (`fullgame.mjs`).
  const wanted = keys.held('throw');

  if (wanted) {
    if (!holding.count) {
      windup = 0;
      return null;
    }
    windup = Math.min(WINDUP_FULL, windup + dt);
    return allows('throw') ? { power: windup / WINDUP_FULL, kind: holding.kind } : null;
  }

  if (windup <= 0) return null;
  const power = windup / WINDUP_FULL;
  windup = 0;
  if (!holding.count) return null;

  const eye = player.eye;
  const at = { x: player.position.x, z: player.position.z };

  if (power < DROP_BELOW || !allows('throw')) {
    throwing.drop({ kind: holding.kind, variant: holding.variant, claim: holding.claim ?? null, at, actor: 'player' });
  } else {
    throwing.launch({
      kind: holding.kind,
      variant: holding.variant,
      claim: holding.claim ?? null,
      from: { x: eye.x, y: eye.y, z: eye.z },
      yaw: rig.yaw,
      // The rig's pitch is positive looking down; a throw is aimed the other way.
      pitch: -rig.pitch,
      power,
      actor: 'player',
    });
  }
  holding.spend();
  autosave.touch();
  return null;
}

/**
 * Where a placed item comes from.
 *
 * Pushing the cart makes the cart your hands. That is the entire interaction
 * design of the cart: there is no "unload four rounds into your arms" step,
 * because nobody does that — you walk the dolly to the table and set off it.
 * `carry.js` and the cart's hold are the same shape, so everything downstream
 * of this line is unchanged.
 */
function hands() {
  const cart = wheeled.active;
  return cart ? cart.hold : carry;
}

/**
 * Which linen the rack is set to.
 *
 * One rack with a selector rather than five racks. Five props is five things
 * to walk between and a storeroom that reads as a colour chart; one rack with a
 * dial is what a linen room actually looks like, and it makes taking the wrong
 * colour a decision you made rather than a shelf you misread.
 */
const LINEN_ORDER = Object.keys(LINENS);
let linenChoice = LINEN_ORDER[0];

/** Tints the rack itself, so the choice is visible from across the storeroom. */
const linenMesh = level.object3D.getObjectByName('prop:store-linen');
if (linenMesh) linenMesh.material = linenMesh.material.clone();

function setLinen(next) {
  if (!LINENS[next]) return;
  linenChoice = next;
  if (linenMesh) linenMesh.material.color.setHex(LINENS[linenChoice].colour);
}

function cycleLinen() {
  setLinen(LINEN_ORDER[(LINEN_ORDER.indexOf(linenChoice) + 1) % LINEN_ORDER.length]);
  hud.say(`Linen rack: ${LINENS[linenChoice].name}`, { tone: 'info', seconds: 2 });
  autosave.touch();
}

setLinen(linenChoice);

/**
 * Lets a department go of something, wherever they feel like leaving it.
 *
 * One place, so that asking, trading and helping yourself all read the same
 * rung — those three differ in what they cost you and not in what the
 * department thinks of you afterwards.
 */
function releaseFor(entry) {
  const shortfall = SHORTFALLS[entry.id];
  if (!shortfall) return { count: 0, at: null, arrived: false };
  const result = stores.release(entry, rungFor(seenValue(shortfall.owner)).id);
  /*
   * A staging line that arrives is met. Marked here rather than inside
   * `stores`, because "the risers are in the room" is a fact about the room and
   * the shelf has no opinion about it.
   */
  if (result.arrived && shortfall.kind === 'staging') entry.delivered = true;
  return result;
}

/**
 * Where a resolved shortfall has left the thing, in words.
 *
 * Three sentences rather than one, because there are three things that can have
 * happened and the difference between them is the entire point of the rung you
 * are on: it is where it was, it is half way, or it is waiting for you.
 */
function releasedLine(released) {
  if (!released.count || !released.at) return null;
  const item = OBJECTS[released.item];
  const what = released.count > 1 ? `${released.count} ${item.plural}` : item.name;
  const place = PLACES[released.at];
  // And what to do about it: the step after, so the line is somewhere to walk.
  const next = place.arrived ? 'Already in the room.' : `Fetch ${released.count > 1 ? 'them' : 'it'} from there.`;
  return `${what}, ${place.arrived ? '' : 'at '}${place.where}. ${next}`;
}

/** The first thing this department is holding that you still need. */
function unresolvedFor(department) {
  return (beos.active?.beo.shortfalls ?? []).find(
    (entry) => !entry.resolved && SHORTFALLS[entry.id]?.owner === department
  );
}

/**
 * Whose stock it is, for a take at an empty station.
 *
 * The one whose window covers now if there is one — they are the person who
 * will notice — and otherwise whoever is nominally responsible. Never by name:
 * the roster decides.
 */
function pickHolder(department) {
  const holders = gatekeepersFor(department);
  if (!holders.length) return null;
  const onDuty = holders.find((person) => windowCovers(person.window, clock.minutes));
  return onDuty ?? holders[0];
}

/** Who is sitting on the missing stock, for the "shelf is empty" line. */
function shortfallOwnerFor(item) {
  const entry = (beos.active?.beo.shortfalls ?? []).find(
    (candidate) => !candidate.resolved && SHORTFALLS[candidate.id]?.item === item
  );
  return entry ? departmentsById[SHORTFALLS[entry.id].owner].name : null;
}

function refusal(punch, status) {
  if (punch.reason === 'too-early') {
    return `The clock opens at ${formatTime(status.opens)}.`;
  }
  if (punch.reason === 'shift-over') return 'That shift is over.';
  if (punch.reason === 'already-in') return 'Already on the clock.';
  return 'The clock refuses.';
}

/**
 * Whether a target is offered, refused-with-a-reason, or silent.
 *
 * A prompt that simply disappears when the answer is no leaves the player
 * standing in the right place wondering what they got wrong, so a target that
 * exists but cannot be used says why.
 */
function gateTargets(status) {
  // Said on the clock rather than at it, because walking the length of the
  // building to be told no is the sort of thing a prompt exists to prevent.
  if (status.canClockIn && !clothing.canClockIn) {
    interaction.gate('time-clock', 'Not in uniform');
  } else if (status.canClockIn) interaction.gate('time-clock', null);
  else if (status.canClockOut) interaction.gate('time-clock', null);
  else if (status.phase === 'before') {
    interaction.gate('time-clock', `Clock opens ${formatTime(status.opens)}`);
  } else if (status.phase === 'over') interaction.gate('time-clock', 'Shift closed');
  else interaction.gate('time-clock', null);

  // Note what the cot does *not* gate on: a pending chapter. A gated target
  // refuses the key press, and the chapter gate has to be reachable — it is the
  // one thing in the game you are made to sit through, so pressing E at the cot
  // must open it rather than be turned away by it.
  interaction.gate('cot', status.phase === 'working' ? 'Not while you are on the clock' : null);

  /*
   * A delivery point is only offered while something is actually sitting on it.
   * The rest of the time it is a housekeeping cart, a pallet and a riser stack,
   * which is what they are.
   */
  for (const point of interaction.targets) {
    if (point.kind !== 'delivery') continue;
    const pile = stores.waitingAt(point.prop.id);
    interaction.gate(point.id, null, { hidden: !pile });
  }

  // A station is only a station while you are short of what it holds.
  for (const station of interaction.targets) {
    if (station.kind !== 'station') continue;
    const entry = unresolvedFor(station.department);
    interaction.gate(
      station.id,
      entry ? null : `${station.label}: nothing you need`,
      { hidden: !entry }
    );
  }
  interaction.gate(
    'beo-board',
    // Readable off the clock, not acceptable off the clock. Knowing what is
    // coming tomorrow is free; taking it is not.
    null
  );
}

/**
 * The end-of-shift summary.
 *
 * Assembled here rather than in the panel because every one of these five
 * blocks comes from a different system, and a UI module that reaches into five
 * simulations to build itself is a UI module that breaks when any of them moves.
 */
/** The last day the summary was put up, so it never appears twice. */
let summaryShownForDay = null;

function showSummary(status) {
  const day = clock.day;
  summaryShownForDay = day;
  const history = beos.history.filter((record) => record.day === day);
  /*
   * The shift is over, so anybody still holding something never did anything
   * about it. Done here rather than on a timer because this is the moment the
   * question is asked: the player is about to read the answer.
   */
  noticed.closeOut();
  takeScreen();
  summaryPanel.show({
    day,
    dayName: clock.dayName,
    shift: status.shift,
    // Whose card this is. The one place the name you typed reads back as the
    // hotel's record of you rather than as a thing you typed.
    who: identity.name,
    // Which act of the story this shift was in, quietly, in the header.
    act: { n: story.act, name: story.actName },
    set: history.filter((record) => !record.ghost),
    ghosts: ghosts.forDay(day),
    standing: journal.summaryForDay(day),
    favors: favors.owed,
    writeups: writeups.entries.filter((entry) => entry.day === day),
    ghostBaseline: ghosts.baseline,
    noticed: noticed.forDay(day),
    // When you punched in, so what was noticed before it reads as your arrival.
    clockedInAt: status.clockedInAt,
    sightings: sightingsForDay(day),
  });
}

/**
 * What you saw of the desert today.
 *
 * The sighting log is cumulative and keyed by species, so today's is whatever
 * was last seen today. It is the one line on that screen that is not about what
 * the hotel thinks of you, which is exactly why it belongs there: a shift where
 * the only thing that happened was a javelina on the dock is still a shift that
 * happened.
 */
function sightingsForDay(day) {
  return Object.entries(wildlife.sightings)
    .filter(([, entry]) => entry.last === day)
    .map(([id, entry]) => {
      const row = WILDLIFE.find((candidate) => candidate.id === id);
      return {
        id,
        label: (row?.name ?? OBJECTS[row?.kind]?.name ?? id).toLowerCase(),
        /*
         * The log's count is every sighting there has ever been, not today's,
         * so it is not offered — a number that reads as "four coyotes tonight"
         * and means "four coyotes since Monday" is worse than no number. What
         * is worth saying is whether this is the first one, which the log does
         * know.
         */
        firstTime: entry.first === day,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Errands, formatted for the objective block. */
/** The line that says where to go next. See `sim/objective.js`. */
const objectives = createObjective({ beos, clock, setup, shift, standing, tasks, level, writeups, stores, dressed: () => clothing.canClockIn, carting: () => Boolean(wheeled.active), howFor: (entry) => howForObjective(entry), whoFor: (entry) => holderFor(SHORTFALLS[entry.id]?.owner)?.name ?? null, learner: () => tutorial.active && tutorial.dayIndex(clock.day) <= 3, loose: (kind) => looseCount(kind), gone: () => goneKinds(), stray: () => strayInHands(), holding: () => hands() });
/**
 * The cart road's jobs, as lines under the objective, the way errands are:
 * what it is and how long there is. Empty when none is on.
 */
function missionLines() {
  const lines = [];
  const now = clock.day * 1440 + clock.minutes;
  const left = (minutes) => (minutes >= 0 ? formatSpan(Math.round(minutes)) : 'gone');
  const tone = (minutes) => (minutes < 0 ? 'bad' : minutes < 25 ? 'warn' : 'idle');
  const shuttle = shuttles.active;
  if (shuttle) {
    const row = shuttles.rows.find((r) => r.id === shuttle.id);
    const due = shuttle.day * 1440 + parseTime(row.due) - now;
    lines.push({ label: `Drive two guests to dinner at the Caytons by ${formatTime(parseTime(row.due))}`, left: left(due), tone: tone(due) });
  }
  const blockade = blockades.active;
  if (blockade) {
    const row = BLOCKADES_BY_ID[blockade.id];
    const due = blockade.from + row.due - now;
    lines.push({ label: `Get the burros off the cart road before the shuttle (${blockade.onRoad} still on it)`, left: left(due), tone: tone(due) });
  }
  const ice = deliveries.reading;
  if (ice) {
    const melts = deliveries.active.from + deliveries.rows.find((r) => r.id === ice.id).melts - now;
    lines.push({ label: `Ice to the Caytons terrace: ${ice.on} of ${ice.of} bags on the cart, ${Math.round(ice.good * 100)}% still ice`, left: left(melts), tone: tone(melts) });
  }
  return lines;
}

function objectiveFor() {
  const road = missionLines();
  const withRoad = (objective) => (road.length ? { ...objective, errands: [...(objective.errands ?? []), ...road] } : objective);
  return withRoad(objectiveNow());
}

function objectiveNow() {
  // Nothing on the roster is yours once you have been let go; the line says so
  // rather than calling you a no-show every day for the rest of the week.
  if (escalation.dismissed) {
    return {
      errands: [],
      title: 'Dismissed',
      line: 'You no longer work here. Start the week again from the pause screen (Esc)',
      tone: 'bad',
      progress: 'Nothing more goes on your card. There is nobody left to file it.',
    };
  }
  const active = beos.active;
  if (active?.beo.teardown && teardown?.active) {
    return { errands: objectives.errandLines(), ...teardown.objective(active) };
  }
  return withErrand(withFront(withCart(withJob(withStory(withBoardSense(objectives.objectiveFor()))))));
}

/** Whether you can be out front as you are: not in blacks, the marble open to you, or sent there. */
function frontOk() {
  return !clothing.isUniform || access.allows('foh-lobby') || frontCover;
}

/**
 * Something waiting out front while you are in blacks: what it costs and the
 * two ways round it, instead of the floor guide walking you onto the marble.
 * A new starter followed the line to the signage at the front desk and was
 * written up for it, by the screen (`fullgame.mjs`, act three's first day).
 */
function withFront(base) {
  const active = beos.active;
  if (!base || !active || frontOk()) return base;
  // Somebody to ask who is out front.
  for (const entry of active.beo.shortfalls ?? []) {
    if (entry.resolved) continue;
    const row = SHORTFALLS[entry.id];
    const holder = row ? holderFor(row.owner) : null;
    if (!holder || !inFrontOfHouse(holder.npc.position)) continue;
    const say = `${sentence(whom(holder.name))} has ${row.wants ?? 'it'}, out front: staff in blacks get reported on the marble. Change into your own clothes at your locker to go, or file without it`;
    const next = (base.next ?? []).filter((line) => !line.includes(holder.name));
    return { ...base, next: [say, ...next].slice(0, 2), task: frontTask(base, `${sentence(whom(holder.name))} has ${row.wants ?? 'it'}, out front, where staff in blacks get reported: change into your own clothes at your locker to go`, holder.name) };
  }
  for (const entry of active.beo.shortfalls ?? []) {
    if (!entry.resolved || entry.delivered) continue;
    for (const place of Object.values(SHORTFALLS[entry.id]?.at ?? {})) {
      const p = stores.waitingAt(place) ? propAt(place) : null;
      if (!p || !inFrontOfHouse(p)) continue;
      const wants = SHORTFALLS[entry.id].wants ?? 'it';
      const say = `${sentence(wants)} is out front: staff in blacks get reported on the marble. Change into your own clothes at your locker to fetch it, or file without it`;
      const next = (base.next ?? []).filter((line) => !/^Fetch /.test(line));
      return { ...base, next: [say, ...next].slice(0, 2), task: frontTask(base, `${sentence(wants)} is out front, where staff in blacks get reported: change into your own clothes at your locker to fetch it`, wants) };
    }
  }
  return base;
}

/**
 * The task while something is out front: the room's own step if it has one
 * left, said as it was; once the room is otherwise set, filing it (with the
 * key) or the locker first, which is the whole choice.
 */
function frontTask(base, why, about) {
  const filing = /^File the room/.test(base.task?.do ?? '');
  if (filing) return { do: 'File the room as it is: press [R]', why, progress: base.task.progress ?? null };
  // The floor guide passes the step out front by (`shortForTip`), so the
  // card does too: the room's next step instead.
  const rest = (base.tasks ?? []).filter((task) => !task.do.includes(about));
  return rest[0] ?? base.task;
}

/** A favour being called in, said as somewhere to go, first under the line. */
function withErrand(base) {
  const errand = errandForTip();
  if (!base || !errand || !shift.status(clock).onTheClock) return base;
  const say = `${errand.label}: go to ${whom(errand.who)} by ${formatTime(errand.due % 1440)} and press [E] there`;
  const left = Math.round(errand.due - (clock.day * 1440 + clock.minutes));
  return { ...base, next: [say, ...(base.next ?? []).filter((line) => line !== say)].slice(0, 2), task: { do: `Go to ${whom(errand.who)} and press [E] there`, why: `${errand.label}, by ${formatTime(errand.due % 1440)}`, progress: left >= 0 ? `${formatSpan(left)} left` : null } };
}

/**
 * Pushing a cart somewhere it cannot go (a three-foot door, a person in a
 * shop to talk to): said, and how to let go of it. A new starter who had
 * taken a table cart's handle by mistake pushed it at the engineering shop's
 * door all morning, on the way to ask for the risers (`fullgame.mjs`).
 */
function withCart(base) {
  if (!base || wheeled.active?.state !== 'pushed' || !routeGoal?.point || routeGoal.arrived) return base;
  const path = cartRouter.ready ? cartRouter.path(player.position) : null;
  const end = path?.[path.length - 1];
  const reaches = end && Math.hypot(end.x - routeGoal.point.x, end.z - routeGoal.point.z) < ft(8);
  if (reaches) return base;
  return { ...base, next: ['The cart will not go that way: [hold E] to let go of the cart', ...(base.next ?? [])].slice(0, 2), task: { do: 'Let go of the cart: [hold E]', why: 'The cart will not go that way' } };
}

/**
 * A job's own step, under the room's (`sim/jobs.js`): the glasses to carry
 * out, the urn to bring up. Once the room itself is done it is the line, so
 * nobody files a refresh with the glasses still on the floor without being
 * told they are there.
 */
function withJob(base) {
  const beo = beos.active?.beo;
  if (!beo?.job || !base) return base;
  const step = jobs.next(hands(), player.position);
  if (!step) return base;
  // Hands full of something else: that first, or the job's thing cannot be picked up.
  const held = strayInHands();
  if (held && !step.drop) return { ...base, line: `Your hands are full of the ${held}: put it down here ([G], a short tap)`, next: [step.say], task: { do: `Put down the ${held}: tap [G]`, why: 'Your hands are full, so the job\'s things cannot be picked up' } };
  const task = { do: step.say, why: `Part of the job, ${beo.title}` };
  if (/^File the room/.test(base.line ?? '')) return { ...base, line: step.say, next: ['Then file the room with [R]'], task };
  return { ...base, next: [step.say, ...(base.next ?? [])].slice(0, 2), task };
}

/**
 * On the clock with nothing in hand and nothing on the board that can be
 * taken and set in time: said, with what to do instead. It said "take an
 * event" all day to a new starter whose every card was refused (Serg had them
 * on the small rooms) or past what they could set in time, and they stood at
 * the board until the shift ended.
 */
function withBoardSense(base) {
  if (beos.active || !base || base.title !== 'On the clock' || anythingFits()) return base;
  const cards = beos.board(clock).filter((card) => !card.beo.teardown || allows('strikes'));
  const later = cards.filter((card) => card.state === 'posted' && !card.live).map((card) => card.times.posted).sort((a, b) => a - b)[0];
  const held = cards.map((card) => (card.state === 'posted' && card.live && card.remaining > 0 ? heldBackFrom(card.beo) : null)).find(Boolean);
  const status = shift.status(clock);
  /*
   * A card still to post today that could be set once it is up: that is the
   * day's work, and the line says wait for it, never go home. On the finale's
   * day a new starter clocked in at five to the finale posting at six, was
   * told to clock out or read the board again at six, and clocked out
   * (`fullgame.mjs`, the second time through day seventeen).
   */
  const coming = cards
    .filter((card) => card.state === 'posted' && !card.live && !heldBackFrom(card.beo))
    .filter((card) => card.beo.finale || !card.beo.estimate || card.times.setupBy - card.times.posted >= card.beo.estimate)
    .sort((a, b) => (b.beo.finale ? 1 : 0) - (a.beo.finale ? 1 : 0) || a.times.posted - b.times.posted)[0];
  if (coming) {
    return {
      ...base,
      title: 'Before the board posts',
      where: { prop: 'beo-board' },
      line: `Wait at the BEO board: ${coming.beo.code}, ${coming.beo.title}, posts at ${formatTime(coming.times.posted)}`,
      next: ['Then take it there, beside the time clock'],
      task: { do: 'Wait at the BEO board', why: `${coming.beo.title} posts at ${formatTime(coming.times.posted)}; take it when it does` },
      tone: 'idle',
    };
  }
  return {
    ...base,
    title: 'Nothing you can take',
    where: { prop: 'time-clock' },
    line: later ? `Clock out at the time clock, or read the board again at ${formatTime(later)}` : 'Clock out at the time clock: nothing on the board can be set in time now',
    task: { do: '[hold E] at the time clock to clock out early', why: later ? `Nothing on the board fits now; more posts at ${formatTime(later)}` : 'Nothing on the board can be set in time now' },
    next: [
      held ?? 'Every card left is due before anybody could set it',
      `[hold E] at the time clock to clock out early; the shift ends ${formatTime(status.end)}`,
    ],
    tone: 'idle',
  };
}

/**
 * What the story wants of you now, as one line, or null when it wants nothing
 * you can work at. Under the objective whenever there is no event in hand, so
 * a player who has finished a room always knows what the week is for.
 */
function storyLine() {
  if (!allows('beats')) return null;
  const name = story.actName;
  const rung = (id) => rungFor(standing.get(id)).label.toLowerCase();
  // Who is still to win over: not warm, and not warm at any time this act.
  const short = (ids) => ids.filter((id) => !story.won(id));
  const list = (ids) => ids.map((id) => `${deptName(id)} (${rung(id)})`).join(', ').replace(/, ([^,]*)$/, ' and $1');
  switch (story.act) {
    case 1:
      return `${name}: set Banquets' rooms well and Serg will notice (he has you as ${rung('banquets')})`;
    case 2: {
      const left = short(story.departmentsOf(2));
      return left.length ? `${name}: win over ${list(left)}. Their rooms are marked on the board` : null;
    }
    case 3:
      return short(['management']).length
        ? `${name}: win over Management (${rung('management')}). Their rooms are marked on the board`
        : `${name}: Management thinks well of you now. Keep an eye on the lobby`;
    default:
      return story.state.finale && !story.state.result ? `${name}: the ownership dinner is on the board. Grant will be in the room` : null;
  }
}

/** The first beat waiting for you that has a place to walk to, as a line and a place. */
function missionStep() {
  if (!allows('beats')) return null;
  const up = missionsUp().find((m) => m.place && m.chapter.where);
  if (!up) return null;
  const who = up.chapter.speaker ? sentence(whom(up.chapter.speaker)) : 'Somebody';
  return { line: `${who} wants a word, in the ${objectives.roomName(up.chapter.where)}`, where: { room: up.chapter.where } };
}

/**
 * The story and the beats waiting for you, under an objective with no event in
 * it. Off the clock, a beat waiting somewhere is the line (and where the floor
 * guide goes) before the cot; on it, it is the step after the board. Before,
 * a beat with a place played only if you happened to walk in.
 */
function withStory(base) {
  if (beos.active || !base || !allows('beats')) return base;
  const mission = missionStep();
  const goal = storyLine();
  if (base.title === 'Off the clock' && mission) {
    return { ...base, title: 'After the shift', line: mission.line, where: mission.where, next: ['Then sleep on the cot in the break room', goal].filter(Boolean).slice(0, 2), task: { do: mission.line, why: 'Then sleep on the cot in the break room' } };
  }
  if (base.title === 'On the clock' || base.title === 'Off the clock' || base.title === 'That is the day\'s work' || base.title === 'Before the shift') {
    const next = [mission ? `${mission.line}, when you have a minute` : base.next?.[0], goal].filter(Boolean);
    return { ...base, next: next.slice(0, 2) };
  }
  return base;
}

function verbFor(target, status) {
  if (target.kind === 'timeclock') {
    return status.canClockOut && !status.canClockIn ? 'clock out at the' : 'clock in at the';
  }
  if (target.kind === 'endday') {
    return chapters.blocking ? `${sentence(whom(chapters.pending.speaker))} wants a word before you use the` : 'sleep on the';
  }
  if (target.kind === 'beoboard') return 'read the';
  // Naming what the next press puts you in is the whole of the clothing UI.
  // Without it the locker is a cycle you have to learn by pressing it, and the
  // press that teaches you is the one that puts you in civvies on the clock.
  if (target.kind === 'lockers') return `change into ${describeClothing(clothing.next)} at your`;
  if (target.kind === 'supply' && target.supplies === 'linen') {
    return `take ${LINENS[linenChoice].name.toLowerCase()}`;
  }
  if (target.kind === 'supply') return 'take';
  if (target.kind === 'delivery') {
    const pile = stores.waitingAt(target.prop.id);
    if (!pile) return 'collect from';
    const item = OBJECTS[pile.item];
    return `collect ${pile.count > 1 ? `${pile.count} ${item.plural}` : `the ${item.name.toLowerCase()}`} from`;
  }
  if (target.kind === 'station') {
    const entry = unresolvedFor(target.department);
    return entry ? `help yourself to ${SHORTFALLS[entry.id].wants ?? SHORTFALLS[entry.id].label.toLowerCase()} at the` : 'use';
  }
  return 'use';
}

// --- frame -------------------------------------------------------------------

/**
 * Every key read in a frame, and who gets to read it. See `sim/frameInput.js`.
 * `scripted` is a headless run's way in, exposed as it always was.
 */
const keys = createFrameInput({ input });
const scripted = keys.scripted;
const pressed = (action) => keys.pressed(action);

/** Seconds of gameplay since boot. The clock write-up cooldowns run on. */
let elapsed = 0;

/**
 * Whether the frame draws.
 *
 * A headless run walks the player down two hundred feet of corridor a dozen
 * times over, and rendering every one of those frames into a software
 * rasteriser costs more than the entire simulation does. Turning it off makes a
 * full shift measurable in seconds instead of minutes. It is never off in play;
 * the only caller is the test harness, and it turns it back on before it takes
 * a screenshot.
 */
let rendering = true;

function readIntent(speedCap = 0) {
  // The pad's wheel is chosen with the left stick, so while it is up the stick
  // is not walking you anywhere.
  const move = actionKey.fanning && wheelMode() ? { x: 0, y: 0 } : keys.move;
  // The camera decides what forward means, and the body is told in world space.
  const forward = rig.forward;
  const right = rig.right;
  return {
    move: {
      x: forward.x * move.y + right.x * move.x,
      z: forward.z * move.y + right.z * move.x,
    },
    sprint: keys.sprinting,
    /*
     * How quickly the body does what it is told, and how fast it can go.
     *
     * Being out of it does not slow you down. It slows you down *in a chase* —
     * which is the only moment "reacting" is a thing you are doing, and the
     * only moment the word means anything. Walking a cart down an empty
     * corridor is not a chase, and a version of this rule that taxed that walk
     * made the whole question "can I afford the minutes", answered once at the
     * start of a shift with a calculator.
     */
    response: chased() ? impairment.reaction : 1,
    pace: impairment.pace,
    jump: keys.jumped(),
    speedCap,
    blockSprint: speedCap > 0,
  };
}

/**
 * Picking up whatever is lying about.
 *
 * Any entity tagged carryable, with no list of what those are. An orange, a
 * broken shard, a fire extinguisher and a caught scorpion are the same prompt
 * and the same key.
 */
const PICKUP_REACH = ft(5);

/**
 * The things bolted to the building: the clock, the board, the shelves, the
 * lockers, a station, the door you sleep behind.
 *
 * `interaction.js` resolves which one you are at; this only has to say it as a
 * sentence and hand over what pressing it does. A blocked one is still offered,
 * because "the clock wants a uniform — yours is in your locker" is the most
 * useful thing the screen can say while you are standing in front of it in
 * blacks.
 */
/**
 * A thing's label as it reads in the middle of a sentence: "the time clock",
 * not "the Time Clock". Acronyms keep their capitals (the BEO board), and the
 * lockers are one locker, because it is yours.
 */
function sayLabel(label) {
  if (label === 'Lockers') return 'locker';
  return label.replace(/\b([A-Z])([a-z]+)/g, (word, first, rest) => first.toLowerCase() + rest);
}

/** The point on a target's box nearest to where you stand, on the floor plan. */
function nearestOnBox(target, position) {
  const clampTo = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  return {
    x: clampTo(position.x, target.centre.x - target.half.x, target.centre.x + target.half.x),
    z: clampTo(position.z, target.centre.z - target.half.z, target.centre.z + target.half.z),
  };
}

/**
 * The linen rack against the sheet: when the colour it would hand you is not
 * the one the event wants, the prompt says so and where to change it. The
 * choice stays yours; the BEO still grades it.
 */
function linenNote(target) {
  if (target?.kind !== 'supply' || target.supplies !== 'linen') return '';
  const wanted = beos.active?.beo.setup?.linen;
  if (!wanted || wanted === linenChoice) return '';
  return ` · the sheet wants ${LINENS[wanted]?.name.toLowerCase() ?? wanted}, [hold E] to change`;
}

/**
 * Whether clocking out now is clocking out early: before the shift's end, with
 * no room filed today. Then it is not what a press at the clock does; it is in
 * the hold menu as "clock out early". A new starter who pressed the clock twice
 * (once to clock in, once to be sure) clocked straight back out, and every card
 * on the board refused them for the rest of the day.
 */
/** Whether today's shift is open or being worked: when sleeping would sleep it away. */
function shiftUnderWay(status) {
  return (status.phase === 'open' || status.phase === 'working') && !escalation.dismissed;
}

function clockingOutEarly(status) {
  if (!(status.canClockOut && !status.canClockIn)) return false;
  if (clock.minutes >= shift.windowFor(clock.day).end) return false;
  return !beos.history.some((record) => record.day === clock.day && !record.ghost);
}

function offerTarget(target, status) {
  if (!target) return null;
  /*
   * Two presses a new starter makes without meaning them, each turned into a
   * refusal that says why and names the hold, with the real thing in the hold:
   * clocking out early (pressing the clock a second time to be sure clocked
   * you straight back out), and sleeping before your shift (waking at the cot
   * and pressing the key slept the whole shift away, which is a no-show).
   */
  if (target.kind === 'timeclock' && clockingOutEarly(status)) {
    return [
      {
        id: target.id,
        tier: FIXED,
        distance: 0,
        verb: 'clock out',
        what: null,
        prop: target.prop?.id ?? target.prop,
        blocked: `your shift runs to ${formatTime(shift.windowFor(clock.day).end)}: [hold E] to clock out early`,
        at: target.centre ? nearestOnBox(target, player.position) : null,
        height: 1.5,
      },
      { id: `${target.id}-early`, tier: POSSIBLE, distance: 0, verb: 'clock out early at the', what: sayLabel(target.label), run: () => use(target, status) },
    ];
  }
  if (target.kind === 'endday' && !chapters.blocking && shiftUnderWay(status)) {
    return [
      {
        id: target.id,
        tier: FIXED,
        distance: 0,
        verb: 'sleep',
        what: null,
        prop: target.prop?.id ?? target.prop,
        blocked: 'not until your shift is done: [hold E] to sleep through it',
        at: target.centre ? nearestOnBox(target, player.position) : null,
        height: 1.5,
      },
      { id: `${target.id}-early`, tier: POSSIBLE, distance: 0, verb: 'sleep through your shift on the', what: sayLabel(target.label), run: () => use(target, status) },
    ];
  }
  const said = verbFor(target, status);
  return {
    id: target.id,
    tier: FIXED,
    distance: 0,
    prop: target.prop?.id ?? target.prop,
    // Drawn on the thing (the near face of its box), never aimed: FIXED is
    // decided by facing in `interaction.js`.
    at: target.centre ? nearestOnBox(target, player.position) : null,
    height: 1.5,
    verb: said || 'use',
    what: sayLabel(target.label) + linenNote(target),
    // A shelf by what is on it, as anybody would say it: "take chairs".
    say: target.kind === 'supply' ? `take ${target.supplies === 'linen' ? `${LINENS[linenChoice]?.name.toLowerCase() ?? ''} tablecloths`.trim() : `${plainName(target.supplies)}s`}${linenNote(target)}` : undefined,
    blocked: target.blocked || null,
    run: () => use(target, status),
  };
}

/** The rack, which is the one prop with a second thing you can do to it. */
function offerLinen(target) {
  if (!target || target.kind !== 'supply' || target.supplies !== 'linen') return null;
  return {
    id: 'linen-colour',
    // The rack hands you linen when you press it; which colour is the rarer
    // question and belongs a hold away.
    tier: POSSIBLE,
    distance: 1,
    verb: 'change',
    what: `to the next linen colour (${LINENS[linenChoice]?.name ?? linenChoice})`,
    run: () => cycleLinen(),
  };
}

/**
 * The action key's state machine lives in `sim/actionKey.js`: one outcome per
 * frame, and this file performs it. `onOffer` is the last thing the router
 * offered, for anything that wants to ask.
 */
const actionKey = createActionKey();
let onOffer = { all: [], top: null, rest: [] };

/**
 * The number under a fan row, read straight off the keyboard.
 *
 * Not a binding, and deliberately not one. `bindings.json` is the list of verbs
 * this game reasons about, and every key in it is live for the whole shift and
 * swallowed from the browser for the whole shift. A number that means "the
 * second row of a menu that is open right now" is neither of those things: it
 * is only a key while the menu is up, it performs whatever happens to be on
 * that row, and outside the menu it should go back to being an ordinary
 * keystroke the page has no opinion about.
 *
 * So it is a listener that does nothing unless the fan is open, and it queues
 * rather than acts, because acting here would run a verb outside the frame and
 * half the game reads the clock.
 */
window.addEventListener('keydown', (event) => {
  if (!actionKey.fanning || event.repeat) return;
  const digit = /^(?:Digit|Numpad)([1-9])$/.exec(event.code);
  if (!digit) return;
  event.preventDefault();
  actionKey.pick(Number(digit[1]) - 1);
});

/** The label for the row in position `index`. One per row, and they are shown. */
const fanKey = (index) => String(index + 1);

function fanRows(options) {
  // A pad has no number keys; the d-pad moves the mark and A lets go on it.
  const numbered = currentDevice() !== 'gamepad';
  return options.map((option, index) => {
    const label = option.say ? (option.blocked ? `${option.say} · ${option.blocked}` : option.say) : line(option, { key: null });
    return {
      key: numbered ? fanKey(index) : null,
      label,
      // What a slice of the pad's wheel has room for.
      short: label.length <= 26 ? label : option.verb,
      blocked: Boolean(option.blocked),
    };
  });
}

/** Whether the held-key fan is drawn as the pad's wheel. */
const wheelMode = () => currentDevice() === 'gamepad';

/**
 * The wheel's row from the left stick: the first row at the top and on round
 * clockwise, past half a push. Under that the mark stays where it was, so the
 * stick falling back to the middle before A comes up keeps the choice.
 */
function wheelPick(count) {
  const stick = input.padState?.move ?? { x: 0, y: 0 };
  if (count < 1 || Math.hypot(stick.x, stick.y) < WHEEL_PUSH) return null;
  const angle = Math.atan2(stick.x, stick.y);
  const slice = (Math.PI * 2) / count;
  return ((Math.round(angle / slice) % count) + count) % count;
}
const WHEEL_PUSH = 0.5;

/**
 * Where the player is aiming, for picking among the things in reach
 * (`prompts.js`, `AIM`): the left stick or the keys while they are pushed, in
 * the world, and the camera's forward when they are not. `keep` is what was on
 * top last frame, so it does not flicker.
 */
let lastFocus = null;
function aimNow() {
  const move = keys.move;
  const forward = rig.forward;
  const right = rig.right;
  let dir = forward;
  if (Math.hypot(move.x, move.y) > 0.3) {
    const x = forward.x * move.y + right.x * move.x;
    const z = forward.z * move.y + right.z * move.x;
    const length = Math.hypot(x, z) || 1;
    dir = { x: x / length, z: z / length };
  }
  return { from: player.position, dir, keep: lastFocus };
}

/**
 * Where the prompt sits on the screen for a candidate that says where it is:
 * projected through the camera, or null when that is behind the camera or off
 * the screen (the prompt then sits at the bottom).
 */
function promptAt(candidate) {
  const at = candidate?.at;
  if (!at) return null;
  const point = screenOf({ x: at.x, y: (at.y ?? 0) + (candidate.height ?? 1.2), z: at.z });
  return point && point.inside ? point : null;
}

/**
 * What the button will do, in plain words, naming the thing: "pick up chair",
 * "set chair here", "talk to Roz". No article where a person would not say
 * one, and the room's own things by the names anybody uses for them.
 */
function promptWords(candidate) {
  if (candidate.say) return candidate.say;
  return describe(candidate).replace(/\b(pick up|take|collect) the /, '$1 ');
}

/**
 * The one prompt (`hud.setPrompt`), from the top of what the key would do
 * here: the button and what it does, on the thing. When the key would do
 * nothing useful with what you carry and the snap would set it on the plan,
 * the snap is the prompt instead, on the spot it would use.
 */
function promptFor(here) {
  const top = here.top;
  const snapUseful = snapping?.anchor && (!top || top.blocked || top.id === 'why') && hands().count > 0;
  if (snapUseful) {
    const a = snapping.anchor;
    const point = screenOf({ x: a.x, y: 1.1, z: a.z });
    const what = plainName(hands().kind, hands().variant);
    return { key: '[C]', verb: `set ${what} on the nearest spot`, x: point?.inside ? point.x : null, y: point?.inside ? point.y : null };
  }
  if (!top) return null;
  const point = promptAt(top);
  const holding = hands();
  const verb = top.id === 'why' ? (holding.count ? `set ${plainName(holding.kind, holding.variant)}` : 'not here') : promptWords(top);
  return { key: '[E]', verb, blocked: top.blocked || null, x: point ? point.x : null, y: point ? point.y : null };
}

/**
 * The room's things by the names anybody uses for them: a round table, a
 * chair, an ivory tablecloth. Everything else by its catalogue name.
 */
function plainName(kind, variant = null) {
  switch (kind) {
    case 'round':
      return 'round table';
    case 'chair':
      return 'chair';
    case 'linen':
      return variant ? `${(LINENS[variant]?.name ?? variant).toLowerCase()} tablecloth` : 'tablecloth';
    case 'signage':
      return 'sign board';
    default:
      return (OBJECTS[kind]?.name ?? kind ?? 'it').toLowerCase();
  }
}

/** A world point on the screen, in CSS pixels; `inside` when it is in view and in front. */
function screenOf(point) {
  const camera = rig.camera;
  const V3 = camera.position.constructor;
  const v = new V3(point.x, point.y, point.z);
  camera.updateMatrixWorld();
  v.applyMatrix4(camera.matrixWorldInverse);
  const behind = v.z > -0.05;
  v.applyMatrix4(camera.projectionMatrix);
  const width = canvas.clientWidth || window.innerWidth;
  const height = canvas.clientHeight || window.innerHeight;
  const x = (v.x * 0.5 + 0.5) * width;
  const y = (-v.y * 0.5 + 0.5) * height;
  const inside = !behind && x >= 0 && x <= width && y >= 0 && y <= height;
  return { x, y, inside, behind, ndc: { x: v.x, y: v.y }, width, height };
}

function runAction(here) {
  const held = keys.held('interact');
  // B backs out of the wheel without doing anything: letting go of A always
  // runs the choice it is on, so a wheel opened by mistake needed a way out.
  if (actionKey.fanning && wheelMode() && input.pad.cancel()) {
    actionKey.dismiss();
    hud.setFan(null);
    tutorialSeen.fanned = true;
  }
  if (actionKey.fanning && wheelMode()) {
    const pick = wheelPick([here.top, ...here.rest].filter(Boolean).length);
    if (pick !== null) actionKey.aim(pick);
  }
  const key = actionKey.frame(
    {
      held,
      tapped: pressed('interact'),
      up: pressed('forward') || pressed('left'),
      down: pressed('back') || pressed('right'),
    },
    here
  );

  switch (key.outcome) {
    case 'picked': {
      hud.setFan(null);
      const done = perform(key.option);
      if (!done.ok && done.reason) hud.say(done.reason, { tone: 'warn', seconds: 3 });
      return;
    }
    case 'released': {
      hud.setFan(null);
      const done = perform(key.option);
      if (!done.ok && done.reason && key.option) hud.say(done.reason, { tone: 'warn', seconds: 3 });
      return;
    }
    case 'navigating':
    case 'opened':
      // The fan says it all; the prompt it came out of goes.
      hud.setPrompt(null);
      hud.setFan({ rows: fanRows(key.options), at: key.at, wheel: wheelMode() });
      return;
    case 'spent':
      hud.setFan(null);
      hud.setPrompt(null);
      return;
    case 'tapped': {
      const done = perform(key.option);
      if (!done.ok && done.reason && key.option) hud.say(done.reason, { tone: 'warn', seconds: 3 });
      return;
    }
    case 'holding':
      return;
    default:
      break;
  }
  hud.setFan(null);
  const shown = promptFor(here);
  promptPoint = shown && Number.isFinite(shown.x) ? { x: shown.x, y: shown.y } : null;
  hud.setPrompt(shown);
}

/** Where the prompt sat on the screen last frame, so the marker can step aside for it. */
let promptPoint = null;

function offerPickup() {
  // Not from the driver's seat. The floor is out of reach and your hands are on the wheel.
  if (bodyReading().seated) return null;
  const near = pickupInReach();
  if (!near) return null;
  const definition = OBJECTS[near.entity.kind];

  return {
    id: 'pickup',
    tier: NEAR,
    kind: LITTER,
    distance: near.distance,
    at: near.entity.position,
    focus: `entity:${near.entity.id}`,
    entityKind: near.entity.kind,
    // On the thing, just over it, not a person's height above the floor.
    height: ft(definition.height ?? 1) + 0.35,
    verb: 'pick up',
    what: `the ${definition.name.toLowerCase()}`,
    say: `pick up ${plainName(near.entity.kind, near.entity.variant)}`,
    run() {
      const holding = hands();
      // Picking one up carries its ownership with it. Walk it back into the
      // room and the room stops being short; walk it further away and it does
      // not.
      if (near.entity.claim) {
        holding.claim = near.entity.claim;
        claims.recover(near.entity);
      }
      holding.add(near.entity.kind, near.entity.variant, 1);
      entities.remove(near.entity.id);
      hud.say(`Picked up the ${definition.name.toLowerCase()}.`, { tone: 'info', seconds: 1.5 });
      autosave.touch();
    },
  };
}

/**
 * What is on the floor in front of you that you could actually carry.
 *
 * Split out from `runPickup` so the frame can ask the question without
 * answering it: the ranking above has to know whether a press is spoken for
 * before it decides who gets it, and a function that picks the thing up as a
 * side effect of being asked cannot be consulted twice.
 */
function pickupInReach() {
  const holding = hands();
  const aim = aimNow();
  return entities.nearest(player.position, {
    within: PICKUP_REACH,
    tag: 'carryable',
    // The one you are aiming at, by the router's own rule (`prompts.js`, AIM).
    score: (entity, distance) => {
      const dx = entity.position.x - aim.from.x;
      const dz = entity.position.z - aim.from.z;
      const length = Math.hypot(dx, dz);
      let value = distance;
      if (length > 0.2) value += AIM.weight * (1 - (dx * aim.dir.x + dz * aim.dir.z) / length);
      if (aim.keep === `entity:${entity.id}`) value -= AIM.keep;
      return value;
    },
    /*
     * Only what adds to what you already have.
     *
     * Hands hold one kind, so picking up a napkin with your arms full of
     * chairs would *swap* them — twelve chairs on the floor for one napkin,
     * from a single unannounced press of E. The prompt simply does not appear
     * unless your hands are empty or it is more of the same thing.
     */
    where: (entity) =>
      (holding.empty || (entity.kind === holding.kind && entity.variant === holding.variant)) &&
      holding.room(entity.kind, entity.variant) > 0,
  });
}

/**
 * What you heard, and what the room is saying.
 *
 * Both of these are the same idea — the building already knows things and this
 * is whether you are in a state to take any of it in — so they share a line.
 * Chatter needs somebody near enough to be overheard; a noise needs nothing but
 * ears, which is why it works sober and further when you are not.
 */
let saidAt = -Infinity;
const CHATTER_GAP = 14;

/** A noise's cause, as the thing you hear: "Something going over, close by." */
const HEARD = {
  impact: 'Something crashing down',
  charge: 'Something charging',
  popper: 'A bang',
  prank: 'Something going off',
  rattle: 'A rattle',
  ride: 'Something rolling past',
  rustle: 'Something in the brush',
  setdown: 'Something put down hard',
  slip: 'Somebody hitting the floor',
  sneeze: 'Somebody sneezing',
  tip: 'Something going over',
};

function runEars() {
  if (heard && elapsed - heard.at < 0.2) {
    hud.say(
      heard.near ? `${HEARD[heard.cause] ?? 'Something'}, close by.` : `${HEARD[heard.cause] ?? 'Something'}, off to the ${heard.where}.`,
      { tone: 'info', seconds: 3 }
    );
    heard = null;
    return;
  }
  if (!impairment.chatter || elapsed - saidAt < CHATTER_GAP) return;
  const near = nearestNpc();
  if (!near) return;
  // Which of the true things gets said depends on who is standing there, so
  // two people in a corridor are not two copies of the same voice.
  const line = chatter.line(near.npc.data.name.length % 3);
  if (!line) return;
  saidAt = elapsed;
  hud.say(`${near.npc.data.name}: ${line}`, { tone: 'info', seconds: 5 });
}

/**
 * Talking to people, and what the key does about them: the deal on the table,
 * the conversation, your manager coming to find you, a word to talk somebody
 * down, and — separately — what your body can do here. See `sim/deals.js`,
 * `sim/conversation.js`, `sim/managerWarning.js`, `sim/charm.js` and
 * `sim/bodyOffers.js`.
 */
const deals = createDeals({
  beos,
  procurement,
  standing,
  writeups,
  hud,
  bus,
  player,
  perception,
  autosave,
  applyShortfalls,
  releaseFor,
  releasedLine,
});
const { dealFor, offerDeal, takeLine, announceTake } = deals;
const conversation = createConversation({
  npcs,
  player,
  rig,
  dialogue,
  regard,
  standing,
  dealFor,
  offerDeal,
  // Somebody being looked for, and what anybody you ask has seen of them.
  speaks: (npc) => story.speaks(npc) ?? searches?.speaks(npc) ?? null,
  met: (npc) => {
    story.met(npc, clock.day);
    searches?.met(npc);
  },
});
const { nearestNpc, clearTalking, greetingFor, runDialogue, offerTalk } = conversation;
const { interceptWarning } = createManagerWarning({
  npcs,
  player,
  escalation,
  writeups,
  clock,
  hud,
  autosave,
  conversation,
  modalOpen,
});
const charming = createCharm({
  regard,
  standing,
  suspicion,
  clock,
  hud,
  autosave,
  npcs,
  // Built after this; asked for at the moment somebody is charmed.
  think: (...args) => think(...args),
  nearestNpc,
  elapsed: () => elapsed,
  unavailable: () => readingSheet || climbing.busy,
});
const { offerCharm } = charming;
const { runConceal, offerConceal, offerClimb } = createBodyOffers({
  concealment,
  climbing,
  wheeled,
  player,
  rig,
  reading: () => readingSheet,
});



/** Who is in the golf cart with you. See `sim/riders.js`. */
const riders = createRiders({ wheeled, npcs, player, level, bus, hud, autosave, think: (...args) => think(...args) });

/**
 * What the key offers when you are carrying a nuisance. See `sim/prankOffers.js`.
 *
 * Note what it is not handed: anything that could tell it whether somebody is
 * looking. A prank is offered whenever you are holding one and standing in the
 * right place, and whether you get away with it is a question asked afterwards,
 * by perception, like every other question of that shape in the game.
 */
const { offerPranks } = createPrankOffers({
  pranks,
  carry,
  player,
  rig,
  npcs,
  level,
  hud,
  autosave,
  propAt,
  unavailable: () => readingSheet || Boolean(concealment.hidden),
});

/** What the key offers at a cart. See `sim/cartOffers.js`. */
const { offerCart } = createCartOffers({ wheeled, carry, riders, player, rig, hud, autosave, throwing });





function offerErrand() {
  const near = tasks.nearest(player.position);
  if (!near) return null;
  const task = near.task;
  const settling = task.source === 'favor';
  // The label is written as "Somebody: go to a place"; what the key does is the
  // second half of it, and the sentence reads better without the colon.
  const where = task.label.includes(':') ? task.label.split(':').slice(1).join(':').trim() : task.label;

  return {
    id: 'errand',
    tier: FIXED,
    distance: 0,
    verb: settling ? 'settle up with' : 'hand it over to',
    what: whom(task.target?.label ?? where),
    run() {
      tasks.complete(task.id);
      if (settling) {
        favors.settle(task.favorId);
        hud.say(`Settled with ${STAFF_BY_ID[task.staffId].name}.`, { tone: 'good', seconds: 4 });
      } else {
        const entry = (beos.active?.beo.shortfalls ?? []).find(
          (candidate) => candidate.id === task.shortfallId
        );
        if (entry && !entry.resolved) {
          entry.resolved = true;
          entry.via = 'trade';
          const released = releaseFor(entry);
          applyShortfalls();
          hud.say(releasedLine(released) ?? 'Done. They will cover it.', {
            tone: 'good',
            seconds: 4,
          });
        } else {
          hud.say('Done.', { tone: 'good', seconds: 3 });
        }
      }
      autosave.touch();
    },
  };
}

/**
 * What the people in the building do about what they saw. See
 * `sim/witnesses.js`; this is only where it is plugged in.
 */
const witnesses = createWitnesses({
  access,
  autosave,
  clock,
  clothing,
  favors,
  hands,
  hud,
  impairment,
  level,
  nameOf,
  noticed,
  npcs,
  perception,
  player,
  procurement,
  regard,
  standing,
  suspicion,
  wildlife,
  writeUp,
  writeups,
  elapsed: () => elapsed,
  // The one you covered for at the end, letting something of yours go.
  forgives: (id) => story.forgives(id),
});
const { chased, conductNow, runSurveillance, reactionOf, think, TELLS, facingOf, patienceShift } = witnesses;

/**
 * A lawn being struck: the crew on it, and what reached the dock. See
 * `sim/teardown.js`. Declared near the top as a slot, because the doors ask it
 * whether the job you are on puts you on the apron.
 */
teardown = createTeardown({
  wheeled,
  npcs,
  bus,
  clock,
  level,
  hud,
  think: (...args) => think(...args),
  propAt,
  shiftFor: shiftForDay,
  activeBeo: () => beos.active?.beo ?? null,
});

/**
 * Dinners on the lawn: who is still sat at them and how they ended. See
 * `sim/dinner.js`. It moves no animal and decides nothing about what anybody
 * saw; it reads perception and the herd and gets people up from the table.
 */
const dinners = createDinners({ npcs, perception, wildlife, clock, standing, hud, entities, propAt });

/**
 * Somebody who is not at their post, and everybody who has seen them since.
 * See `sim/search.js`: it moves nobody until they are found.
 */
const searches = createSearches({ npcs, level, clock, standing, hud });

/**
 * Somebody stranded, and getting them back. See `sim/rescue.js`: it puts one
 * animal out and holds one guest still, and watches.
 */
rescues = createRescues({
  npcs,
  wildlife,
  level,
  clock,
  bus,
  standing,
  hud,
  complain: (accusation) => witnesses.complain(accusation),
});

/*
 * The cart road's jobs, each posted by a beat and each only watching: two
 * guests to be driven to the Caytons by a time (`sim/shuttle.js`), burros to
 * be got off the road before the shuttle (`sim/blockade.js`), and a cart of
 * ice to be got down the road before it melts (`sim/delivery.js`). The pool
 * deck's event is a card, and is on the board with the others.
 */
shuttles = createShuttles({
  npcs,
  level,
  clock,
  standing,
  hud,
  complain: (accusation) => witnesses.complain(accusation),
});
const blockades = createBlockades({ wildlife, wheeled, level, clock, bus, standing, hud });
deliveries = createDeliveries({ wheeled, level, clock, standing, hud });

/**
 * Setting the room: one frame of it.
 *
 * Returns whatever should be in the prompt. There is one E and one Q, and this
 * works out what they mean where you are standing — choosing between verbs from
 * a menu is not something anyone does with six rounds to set and forty minutes
 * left.
 */
/**
 * Sets what is in your hands on an anchor, and says how that went.
 *
 * The one place a thing is set, for the action key and the snap alike, so the
 * two cannot differ in what they allow or in what they tell you. The line is
 * the "what now": right or wrong against the BEO, how far along the room is,
 * and what is next on this round.
 */
function setItem(anchor, role, holding) {
  const kind = holding.kind;
  const variant = holding.variant;
  const done = setup.place(anchor.key, { role, kind, variant, max: OBJECTS[kind]?.place?.max });
  if (!done.ok) return done;
  holding.spend();
  const said = placedLine(anchor, role, kind, variant);
  hud.say(said.text, { tone: said.tone, seconds: said.tone === 'good' ? 2.5 : 4 });
  buzz(said.tone === 'good' ? 0.25 : 0.6, said.tone === 'good' ? 60 : 160);
  return done;
}

/** What setting something just did, against the plan: `{ text, tone }`. */
function placedLine(anchor, role, kind, variant) {
  const spec = beos.active?.beo.setup ?? null;
  const planned = plannedKeys(spec);
  const table = setup.tableAt(anchor.key);
  const counts = setup.counts;
  const name = (OBJECTS[kind]?.name ?? 'it').toLowerCase();
  const wantChairs = spec?.chairsPerTable ?? null;
  const wantTables = spec?.tableCount ?? null;
  if (anchor.keepClear) return { text: 'That is the dance floor, which stays clear. [hold E] to strike it.', tone: 'warn' };
  if (planned && !planned.has(anchor.key)) return { text: 'That mark is not on the plan. [hold E] to strike it, or [F] to check the sheet.', tone: 'warn' };
  if (role === 'base') {
    const left = wantTables ? Math.max(0, wantTables - counts.tables) : null;
    const next = left ? `${count(left, 'round')} to go.` : 'All the rounds are down: chairs and linen next.';
    return { text: `Round set${wantTables ? `, ${counts.tables} of ${wantTables}` : ''}. ${next}`, tone: 'good' };
  }
  if (role === 'cover') {
    if (spec?.linen && variant !== spec.linen) return { text: `Wrong linen: the BEO wants ${spec.linen}. [hold E] to strip it.`, tone: 'warn' };
    const dressed = `${counts.linens} of ${wantTables ?? counts.tables} dressed`;
    return { text: `Linen on, ${dressed}.`, tone: 'good' };
  }
  const seats = table?.seats ?? 0;
  if (wantChairs && seats > wantChairs) return { text: `One too many: this round wants ${wantChairs}. [hold E] to pull one.`, tone: 'warn' };
  if (wantChairs && seats === wantChairs) return { text: `This round has its ${wantChairs} ${name}s.${table?.cover ? '' : ' It still wants its linen.'}`, tone: 'good' };
  return { text: `${seats} of ${wantChairs ?? seats} ${name}s on this round.`, tone: 'good' };
}

/**
 * The snap (C, or RT on a pad): what you carry, set at the nearest spot on the
 * plan within reach (`sim/snap.js`), through `setItem` like the key.
 */
let snapping = null;
function runSnap(inBallroom, holding, reading) {
  snapping = allows('snap') && inBallroom && !reading && !bodyReading().seated && !wheeled.active
    ? snapTarget({ setup, carry: holding, position: player.position, spec: beos.active?.beo.setup ?? null })
    : null;
  if (!pressed('snap')) return;
  if (!snapping) return;
  if (snapping.anchor) setItem(snapping.anchor, snapping.role, holding);
  else if (snapping.why) hud.say(snapping.why, { tone: 'warn', seconds: 3 });
}

function runBallroom(place, holding) {
  const strike = resolveStrike({ setup, position: player.position, linen: beos.active?.beo.setup?.linen ?? null });

  /*
   * Anchor ghosting.
   *
   * The only time the anchors are ever drawn. Sober they are not there, and
   * they are not there before you have read the sheet either — the drug does
   * not tell you where the tables go, it lets you see what you already read.
   * Two conditions, both necessary, and the second one is the point.
   */
  setupView.setGuides({
    visible: true,
    anchors: impairment.ghosting && sheetRead(),
    // The anchor the key would use; failing that the one a snap would.
    target: place.anchor ?? strike.anchor ?? snapping?.anchor ?? lessonAnchor ?? null,
    bad: Boolean(place.bad),
  });

  const out = [];

  /*
   * One placement call for every kind of thing.
   *
   * The object said which role it fills and placement worked out where; this
   * only has to hand it over and spend one. There used to be three branches
   * here naming rounds, chairs and linens, and adding a fourth kind of thing
   * that goes on a table meant editing this line.
   */
  /*
   * Cloth of a colour the sheet does not want is not the tap: it goes on with
   * a hold, and the tap says where the right one is. Having stripped a wrong
   * cloth into your arms, the press laid it straight back on.
   */
  const sheetLinen = beos.active?.beo.setup?.linen ?? null;
  const wrongCloth = place.action === 'place' && holding.kind === 'linen' && sheetLinen && holding.variant && holding.variant !== sheetLinen;
  if (wrongCloth) {
    const want = LINENS[sheetLinen]?.name.toLowerCase() ?? sheetLinen;
    out.push({ id: 'why', tier: PLACING, verb: `the sheet wants ${want}: swap this at the linen rack`, what: null, blocked: `the sheet wants ${want}: swap this at the linen rack` });
  }
  /*
   * A round on a mark the sheet does not want is a hold, not the tap: stood a
   * step off the gold ring, a new starter's press set it there, and the room
   * then wanted a round the shelf no longer had.
   */
  const sheetPlan = beos.active?.beo.setup ? plannedKeys(beos.active.beo.setup) : null;
  const offPlan = place.action === 'place' && place.role === 'base' && sheetPlan && place.anchor?.key && !sheetPlan.has(place.anchor.key);
  if (offPlan) {
    out.push({ id: 'why', tier: PLACING, verb: 'not a ring on the plan: the arrows go to the next one', what: null, blocked: 'not a ring on the plan: the arrows go to the next one' });
  }
  /*
   * A chair on a round that already has its count is a hold too: pressed out
   * of habit at a full round, the extra chairs ran the shelf dry with another
   * round still bare, and the room missed its deadline.
   */
  const fullRound = place.action === 'place' && place.role === 'seat' && place.bad;
  if (fullRound) {
    out.push({ id: 'why', tier: PLACING, verb: 'this round has its chairs: the arrows go to one that needs more', what: null, blocked: 'this round has its chairs: the arrows go to one that needs more' });
  }
  /*
   * A chair or a cloth onto a round that is not on the plan is a hold too.
   * Striking one of those rounds hands its chairs and cloth back to you one at
   * a time, and the tap then set each straight back on the round it came off:
   * a room flip went strike, set, strike, set until its doors (`fullgame.mjs`).
   */
  const ontoOffPlan = place.action === 'place' && place.role !== 'base' && sheetPlan && place.anchor?.key && !sheetPlan.has(place.anchor.key);
  if (place.action === 'place') {
    out.push({
      id: 'place',
      tier: wrongCloth || offPlan || fullRound || ontoOffPlan ? POSSIBLE : PLACING,
      at: place.anchor,
      height: 1.1,
      verb: 'set',
      what: place.what ?? (OBJECTS[holding.kind]?.name ?? 'it').toLowerCase(),
      say: `${holding.kind === 'linen' ? 'lay' : 'set'} ${plainName(holding.kind, holding.variant)} here`,
      run() {
        setItem(place.anchor, place.role, holding);
      },
    });
  }

  if (strike.action) {
    out.push({
      id: 'strike',
      tier: PLACING,
      distance: 1,
      at: strike.anchor,
      height: 1.1,
      // "pull a banquet chair", "strip the linen", "strike the 72\" round".
      verb: strike.label.split(' ')[0].toLowerCase(),
      what: strike.label.split(' ').slice(1).join(' '),
      say: strike.role === 'seat' ? 'take a chair off this table' : strike.role === 'cover' ? 'take the cloth off this table' : 'pick this table back up',
      run() {
        const result = setup.strike(strike.anchor.key, strike.role === 'cover' ? 'cover' : null);
        // Struck furniture comes back into your arms when there is room,
        // because walking a single mistaken chair to storage is not gameplay.
        if (result.ok && !hands().gain(result.removed, result.variant ?? null)) {
          // No room in your arms: it goes back on the shelf rather than
          // evaporating, which matters now that the shelf has a bottom.
          stores.give(result.removed, 1);
          hud.say(`${OBJECTS[result.removed].name} back to stores.`, { tone: 'info', seconds: 1.5 });
        }
      },
    });
  }

  if (place.why && !out.length) {
    out.push({ id: 'why', tier: PLACING, verb: place.why, what: null, blocked: place.why });
  }

  /*
   * Filing the room is not in here, and that is the whole of what it is.
   *
   * It went in for one commit, on the grounds that the scheme has a place for
   * everything and the hold is where the rare verbs live. It is not a rare
   * verb. It is the most-pressed deliberate act in the game and the end of
   * every event, and burying the thing you do forty times a week under the
   * gesture for the things you do twice is exactly backwards.
   *
   * It has a key of its own instead — see `runSubmit` — which is also the only
   * arrangement that keeps both of its old faults impossible at once. Out of
   * the press, it cannot eat the placement press. Off the ranking entirely, the
   * Captain wandering within seven feet of your last table cannot eat it.
   */
  return out;
}

/**
 * One key that only ever files the room.
 *
 * Read at the top of the frame, before anything gathers a candidate, because
 * it is not a candidate: nothing else reads this key and it reads nothing else.
 * A verb with its own key is a verb that cannot be in an argument.
 *
 * Available from anywhere in a room with an accepted event in it — not at a
 * mark, not facing anything, not within reach of a prop. You are standing in
 * the room you set and you are done with it, and having to walk somewhere
 * particular to say so would be a second rule about a verb whose whole point is
 * that it ends the thing.
 */
function runSubmit(inRoom) {
  if (!pressed('submit')) return;
  /*
   * A strike is filed from the room it ends in, the dock, for the same reason
   * a room is filed from the room: you are standing where the work is.
   */
  if (beos.active?.beo.teardown) {
    const row = teardown.rowFor(beos.active.beo);
    if (level.roomAt(player.position.x, player.position.z)?.id === row?.dock) {
      fileTeardown({ auto: false });
      return;
    }
    hud.say('File the strike from the loading dock, where the carts go.', { tone: 'warn', seconds: 3 });
    return;
  }
  if (!inRoom || !beos.active) {
    // Said rather than swallowed. A key that does nothing in most of the
    // building has to say so the first time you try it, or it reads as broken.
    hud.say('Nothing to file. You are not in a room with an event on it.', {
      tone: 'warn',
      seconds: 3,
    });
    return;
  }
  fileSetup({ auto: false });
}

/**
 * Keeps the player out of a parked cart.
 *
 * The cart is not in the broadphase — that is a static structure built once at
 * boot and rebuilding it every frame for one moving box would be absurd — so
 * the one dynamic collision in the game is done by hand, right here. It only
 * applies when the cart is parked: a cart you are pushing lives behind you and
 * shoving you along would be a fight with your own dolly.
 */
function pushOutOfParkedCarts() {
  for (const cart of wheeled.all) {
    if (cart.state !== 'parked' || cart.tipped) continue;
    const dx = player.position.x - cart.position.x;
    const dz = player.position.z - cart.position.z;
    const distance = Math.hypot(dx, dz);
    const minimum = wheeled.radius(cart) + player.tuning.radius;
    if (distance >= minimum) continue;
    if (distance < 1e-4) {
      player.position.x += minimum;
      continue;
    }
    const out = (minimum - distance) / distance;
    player.position.x += dx * out;
    player.position.z += dz * out;
  }
}

/** The cart you were on last frame, so stepping off does not teleport you. */
let lastRide = null;

/**
 * The last place you stood that is part of the property.
 *
 * There is one way out of the building and it is not a door. Walls here are six
 * inches thick; a body far enough into one has its nearest free side on the
 * *far* side, and the resolver will take it — you come out through the wall,
 * into the void, penetrating nothing, and there is no way back in because the
 * way back is the wall you came through. Stepping off a cart at eleven metres a
 * second against the side of the main run is how it happens, and the ride was
 * always going to be where it happened first.
 *
 * Rather than teach the resolver about thickness, this is the invariant the
 * property actually has: every place you are allowed to be is a place inside a
 * room that a body fits in — and the outside ones, the apron, the lot, the golf
 * course, are rooms too. So a frame that ends nowhere did not happen, and you
 * are put back at the last place you stood that was somewhere.
 *
 * It has to be the last place that was *standable*, not merely the last one
 * inside a room's rectangle: a room's rectangle includes the three inches of
 * its own wall, so remembering that would put you back inside the wall and the
 * resolver would send you straight back out of it, once a frame, for ever.
 */
let lastInside = null;

function stayInTheWorld() {
  const { x, z, y } = player.position;
  const inside =
    Boolean(level.roomAt(x, z)) &&
    !penetrates(x, z, feel.radius, y, y + feel.height, level.world);
  if (inside) {
    lastInside = { x, y, z };
    return;
  }
  if (!lastInside || level.roomAt(x, z)) return;
  player.teleport({ ...lastInside });
}

function handsReadout() {
  const held = hands();
  if (!held.count) return null;
  return {
    ...OBJECTS[held.kind],
    name: held.variant
      ? `${LINENS[held.variant].name} ${OBJECTS[held.kind].name}`
      : OBJECTS[held.kind].name,
    count: held.count,
    capacity: held.capacity,
    via: wheeled.active ? 'on the cart' : 'in your arms',
  };
}

/** One frame. Named so a headless test can drive it with a fixed dt. */
function step(dt) {
  // A pad drives the world only while no panel owns the screen; on a panel
  // its buttons belong to the panel (see below).
  input.padLive = !modalOpen();
  input.update();
  keys.beginFrame();
  fadeOnChange();

  // A modal owns the screen. The world keeps rendering behind it — it is a
  // board on a wall, not a different place — but nothing in it moves.
  if (modalOpen()) {
    for (const view of npcViews) view.setLabelVisible(false);
    // The chapter gate is the one modal you interact with rather than dismiss.
    if (chapterPanel.open && pressed('interact')) chapterPanel.advance();
    /*
     * The one modal with a clock in it. The world is frozen behind it and the
     * shift's minutes are frozen with it — what runs in here is real seconds,
     * which is the only clock a ball in the air can be on. The sitting's cost
     * to the shift is charged once, at the end, by `onFinish`.
     */
    if (classPanel.open) {
      classPanel.padStep(input.padState, dt);
      if (input.pad.cancel()) classPanel.close();
      classPanel.step(dt);
    } else {
      /*
       * The pad on a panel: the d-pad moves round its buttons, A presses the
       * one it is on, B is Escape. It only ever clicks what a mouse could.
       */
      padNav.step(panelOnTop(), input.pad, { onBack: backOutOfPanel });
      if (relabelDue()) relabel(ui);
    }
    /*
     * A menu left open behind a panel is a menu still listening for numbers,
     * and the numbers would come back to whatever was under the panel when it
     * closes. It goes when the panel arrives.
     */
    if (actionKey.cancel()) hud.setFan(null);
    hud.setVisible(false);
    runTutorial(dt);
    if (rendering) draw(dt);
    keys.endFrame();
    return;
  }

  padNav.clear();
  if (relabelDue()) relabel(ui);
  if (input.pad.pause()) pause();
  if (pressed('controls')) controlsCard.toggle();
  runHelp(dt);
  if (pressed('debug') && allows('standings')) panel.toggle();
  if (pressed('timeUp') && allows('timescale')) hud.say(`Time scale ${clock.faster()}x`, { seconds: 1.5 });
  if (pressed('timeDown') && allows('timescale')) hud.say(`Time scale ${clock.slower()}x`, { seconds: 1.5 });
  /*
   * The fader. Not verbs, and deliberately nowhere near the action key or the
   * fan: the scheme is WASD, the mouse, one key and the same key held, and a
   * volume control is not a thing you do in a hotel.
   */
  if (pressed('mute')) {
    hud.say(audio.toggleMute() ? 'Sound off' : `Sound ${Math.round(audio.volume * 100)}%`, {
      seconds: 1.5,
    });
  }
  if (pressed('louder') || pressed('quieter')) {
    const level = settings.set('master', audio.nudge(pressed('louder') ? 0.1 : -0.1));
    if (audio.muted) audio.setMuted(false);
    hud.say(`Sound ${Math.round(level * 100)}%`, { seconds: 1.5 });
  }

  elapsed += dt;
  // The body's clock. Real seconds, not the shift's minutes.
  impairment.update(dt);
  /*
   * Whatever is out there, and whatever it does about you. A charge arrives on
   * the impact bus, so what it costs is decided by the same listener that
   * decides what a thrown bottle costs — which turns out to be: you drop what
   * you were holding.
   */
  const inHands = hands();
  const held = inHands.count ? [{ id: 'player', position: player.position, kind: inHands.kind }] : [];
  for (const hit of wildlife.update(dt, { clock, player, held })) {
    const load = hands();
    /*
     * How much of a load survives a charge is the hands' business now rather
     * than this listener's. It used to be a flat `spend()` — bumped, drop one
     * — and that is still exactly what an untaught pair of hands does, because
     * the grip is zero until somebody has sat Quick Refresh. What comes back is
     * how many units actually went, which is how many land on the floor.
     *
     * What was being held is read before the jostle. Afterwards the hands may
     * be empty and have forgotten what they were holding, which is the whole
     * reason this is two statements and not one.
     */
    const was = { kind: load.kind, variant: load.variant, claim: load.claim ?? null };
    const spilled = load.count ? load.jostle() : 0;
    for (let i = 0; i < spilled; i += 1) {
      throwing.drop({
        kind: was.kind,
        variant: was.variant,
        claim: was.claim,
        at: { x: player.position.x, z: player.position.z },
        actor: 'player',
      });
    }
    hud.say(`${OBJECTS[hit.what]?.name ?? hit.definition.name} went straight through you.`, {
      tone: 'bad',
      seconds: 4,
    });
    // Visual only. Nothing reads this but the rig.
    knockedFor = KNOCK_SECONDS;
    buzz(0.8, 220);
  }
  if (knockedFor > 0) knockedFor = Math.max(0, knockedFor - dt);

  /*
   * Whatever is lying about being a nuisance.
   *
   * Everybody who can go over on spilled ice is offered the same way — the
   * player and everybody on shift — because a server slipping and the player
   * slipping are the same event and writing it twice would be how they stop
   * being the same event.
   */
  const onFoot = [
    { id: 'player', position: player.position },
    ...npcs.filter((npc) => npc.present).map((npc) => ({ id: npc.data.id, position: npc.position })),
  ];
  const nuisance = pranks.update(dt, { bodies: onFoot });
  for (const slip of nuisance.slips) {
    if (slip.id === 'player') {
      const load = hands();
      const was = { kind: load.kind, variant: load.variant, claim: load.claim ?? null };
      const spilled = load.count ? load.jostle() : 0;
      for (let i = 0; i < spilled; i += 1) {
        throwing.drop({
          kind: was.kind,
          variant: was.variant,
          claim: was.claim,
          at: { x: player.position.x, z: player.position.z },
          actor: 'player',
        });
      }
      knockedFor = KNOCK_SECONDS;
      buzz(0.8, 220);
      hud.say('Your feet go out from under you.', { tone: 'bad', seconds: 3 });
      continue;
    }
    const who = npcs.find((npc) => npc.data.id === slip.id);
    // Sat on the floor for a moment, looking at the ice. `hold` is the same
    // call somebody makes when they stop to look at anything.
    who?.hold(PRANKS_BY_ID['ice-spill'].down, { at: slip.at });
    who?.say('whoa', 2.5);
  }
  for (const sneeze of nuisance.sneezes) {
    npcs.find((npc) => npc.data.id === sneeze.id)?.say('hh-HUH', 1.5);
  }
  for (const gone of nuisance.left) {
    npcs.find((npc) => npc.data.id === gone.id)?.say('what is that smell', 3);
  }
  autosave.update(dt);
  // Slow on the guided first day until the first room is filed (`tutorial.js`).
  clock.update(dt * tutorial.paceOn(clock.day));
  shift.update(clock);
  beos.update(clock);
  const status = shift.status(clock);
  // Snapshotted here as well as on the day event, because the very first frame
  // of a session has never had a day roll over.
  if (!dayStart) dayStart = snapshot();

  journal.at(clock);
  chapters.update({
    // A beat that hands you something for your pocket waits for the pocket's
    // day; every other beat for the day the guided days let beats in.
    allow: (chapter) => allows(chapter.gives?.length ? 'consumables' : 'beats'),
    room: level.roomAt(player.position.x, player.position.z)?.id ?? null,
    shiftOver: status.phase === 'over',
    day: clock.day,
    minutes: clock.minutes,
  });
  /*
   * A beat with a place is played where it happens, the moment it arms —
   * there is nobody at the cot to hear it. Every other beat waits for the end
   * of the day, as it always has.
   */
  if (chapters.pending?.where && !modalOpen()) {
    takeScreen();
    chapterPanel.show(chapters.pending);
  }
  /*
   * The story: the acts, the visitor's hours, the clock over the front desk,
   * and a finale that went by without you. Before anybody moves, so he is
   * posted where he stands before he is asked to walk.
   */
  for (const happened of story.update(clock)) {
    if (happened.kind === 'missed') settleFinale({ missed: true });
    // A new act says so, once, with what it is about: the summary header was
    // the only place it ever showed, and a player who never read it had no
    // idea the story had moved or what it now wanted.
    if (happened.kind === 'act' && allows('beats')) {
      const goal = storyLine();
      hud.say(`Act ${happened.act}: ${story.actName}.${goal ? ` ${goal.replace(/^[^:]*: /, '')}.` : ''}`, { tone: 'info', seconds: 9 });
    }
  }
  runStoryStep();
  // Before they move, so the detour he is given is toward where you are this
  // frame rather than where you were last one.
  interceptWarning(dt, status);
  breakRouter.pump(BREAK_BUDGET);
  breaks.update(clock);
  for (const npc of npcs) npc.update(dt, clock);
  crewTalk.update(dt, clock);
  // The man from the ownership group, walking past you on his tour
  // (`story.passing`): once you have been near him a couple of seconds and
  // are not already talking to him.
  for (const npc of npcs) {
    if (!npc.data.visitor) continue;
    const near = npc.present && !npc.note && !conversation?.talkingTo && Math.hypot(npc.position.x - player.position.x, npc.position.z - player.position.z) < ft(12);
    passingFor = near ? passingFor + dt : 0;
    if (passingFor < 2) continue;
    const line = story.passing(npc, clock.day);
    if (line) npc.say(line, 6);
  }
  teardown.run(dt);
  dinners.update(dt);
  searches.update(dt);
  rescues.update(dt);
  shuttles.update(dt);
  blockades.update(dt);
  deliveries.update(dt);
  theOpening.run(status);
  tasks.update(clock);
  // Only while you are on the clock and in the building. A debt called in on
  // your own time is a debt nobody can act on.
  favors.update(clock, { canInterrupt: status.onTheClock });

  // Two rounds under your arms is genuinely a worse way to cross the property
  // than a stack of linens, and that is a decision the player gets to make.
  // Pushing is cheaper than carrying the same load, which is the whole point.
  const riding = wheeled.active?.state === 'ridden';
  player.burden = wheeled.active
    ? Math.max(carry.burden, wheeled.burdenOf(wheeled.active))
    : carry.burden;

  if (riding) {
    // While you are standing on one, your input drives the cart and the cart
    // drives you. The controller is not asked for an opinion.
    /*
     * How steady your hands are, which is the state you are in less whatever
     * somebody watching you does to it.
     *
     * The reaction penalty was already gated on "somebody past CALM can see
     * you", and riding a loaded cart past a person in a corridor is that case
     * exactly — so the thing that makes the corridor run possible is the thing
     * that stops working the moment the run is noticed. The cart is loud, which
     * is how it gets noticed. None of that is wired up anywhere; it is three
     * rules that were already here meeting each other.
     */
    const steady = chased() ? impairment.steadiness * impairment.reaction : impairment.steadiness;
    const drive = keys.drive;
    wheeled.steer({ forward: drive.forward, turn: -drive.turn, steady });
    wheeled.update(dt, player);
    // A bump or a kerb taken in the seat, felt in the hands.
    const ridden = wheeled.active;
    if (ridden) {
      const rise = rideHeight === null ? 0 : ridden.position.y - rideHeight;
      if (rise > 0.03) buzz(Math.min(1, 0.3 + rise * 5), 90);
      rideHeight = ridden.position.y;
    }
    const cart = wheeled.active ?? lastRide;
    if (cart) {
      // In a seat if it has one, standing on the deck if it does not.
      const seat = wheeled.seatAt(cart, 'driver');
      player.position.x = seat ? seat.x : cart.position.x;
      player.position.z = seat ? seat.z : cart.position.z;
      player.position.y = seat ? seat.y : cart.position.y + ft(1.3);
      player.yaw = cart.yaw;
    }
  } else if (climbing.busy) {
    rideHeight = null;
    /*
     * While you are on an edge the controller is not asked for an opinion
     * either, for the same reason it is not while you are riding a cart: two
     * things integrating the same body disagree, and the disagreement is a
     * player who slides off whatever they are holding on to.
     */
    const where = climbing.update(dt, player.position);
    if (where) player.teleport(where);
    wheeled.update(dt, player);
  } else {
    rideHeight = null;
    player.update(dt, readIntent(readingSheet ? feel.sheetSpeed : 0));
    wheeled.update(dt, player);
    pushOutOfParkedCarts();
  }
  // After all three of them, because all three can put a body somewhere the
  // building does not go: riding follows the cart, climbing follows an edge,
  // and walking is resolved against six inch walls.
  stayInTheWorld();
  /*
   * The animal on the cart path, which is met in the golf cart. What it does
   * the second time is what you did the first, and the screen says which.
   */
  const tug = wheeled.active?.state === 'ridden' && wheeled.active.seats.length ? wheeled.active : null;
  for (const met of individuals.update(dt, { vehicle: tug, radius: tug ? wheeled.radius(tug) : 0 })) {
    hud.say(met.line, { tone: met.struck ? 'bad' : 'info', seconds: 6 });
    if (met.struck) {
      const load = hands();
      const was = { kind: load.kind, variant: load.variant, claim: load.claim ?? null };
      const spilled = load.count ? load.jostle() : 0;
      for (let i = 0; i < spilled; i += 1) {
        throwing.drop({ kind: was.kind, variant: was.variant, claim: was.claim, at: { x: player.position.x, z: player.position.z }, actor: 'player' });
      }
      knockedFor = KNOCK_SECONDS;
      buzz(0.8, 220);
    }
  }
  individualView.sync();
  riders.run();
  lastRide = wheeled.active?.state === 'ridden' ? wheeled.active : null;
  cartView.sync();
  wildlifeView.sync();
  // Reading the sheet on a pad, the right stick scrolls the paper, not the view.
  const look = readingSheet && currentDevice() === 'gamepad' ? { x: 0, y: 0 } : keys.look;
  rig.update(dt, player.position, look, {
    outdoors: level.roomAt(player.position.x, player.position.z)?.zone === 'outside',
  });
  avatar.sync(player.position, player.yaw);
  avatar.pose(bodyReading(), dt);
  avatar.object3D.visible = rig.bodyVisible;
  sound.frame(soundReading(), dt);
  playScore();
  runHands();

  const reading = runSheet(dt);

  gateTargets(status);
  const inBallroom = level
    .zonesAt(player.position.x, player.position.z)
    .some((zone) => zone.kind === 'event');
  const target = reading ? null : interaction.update(player.position, rig.forward);

  const aim = runThrow(dt);
  throwing.update(dt);
  entityView.sync();

  /*
   * Submitting is a contextual prompt again, and this time it is safe.
   *
   * It had its own key because it kept losing an argument it should never have
   * been in: the Banquet Captain wandering within seven feet of your last table
   * silently ate the press and the room was never filed. That argument was the
   * `else if` chain, and there is no chain any more — filing the room is a
   * candidate in the ballroom like any other, it never wins the press over
   * something you are actually doing, and it is always one hold away.
   */


  /**
   * Priority: the thing on the wall, then the cart, then the person, then the
   * floor you are standing on.
   *
   * Objects beat people, and that is the opposite of what it was. Talking used
   * to win so that somebody standing in the muster alcove could not lock you
   * out of the time clock — but the reverse case is far worse and far more
   * common: the Engineer on Call dwells eight feet from the chair shelf, and
   * for four seconds out of every lap, pressing E to load a cart started a
   * conversation instead. A shelf you cannot use because a colleague is nearby
   * is unusable at exactly the moment the corridor is busiest.
   *
   * With objects first you can still always talk — take one step back from the
   * shelf — and you can always use the shelf.
   */
  // Reading the sheet takes precedence over every prompt: your hands are full
  // of paper and the game should say so rather than silently ignoring E.
  if (reading) {
    hud.setPrompt({ key: '[E]', verb: sheetPage === 'card' ? 'turn to the sheet' : 'turn to your card' });
    setupView.setGuides({ visible: false });
    clearTalking();
    // No wind-up bar: you cannot throw while your hands are full of paper.
    hud.setHands(handsReadout());
    /*
   * Doors follow standing, so they are checked where standing is: every frame,
   * cheaply, rather than hooked to the dozen places standing moves from. A
   * change is announced, because a door that quietly re-locks feels broken.
   */
  const swung = access.changes();
  const coverNow = coverWas;
  coverWas = frontCover;
  if (swung.length) {
    applyShortfalls();
    for (const change of swung) {
      // The errand's cover coming and going is not the marble being yours.
      if (change.id === 'foh-lobby' && (frontCover || coverNow)) continue;
      hud.say(change.allowed ? change.rule.granted : change.rule.withdrawn, {
        tone: change.allowed ? 'good' : 'warn',
        seconds: 5,
      });
    }
  }

  perception.observe(dt, {
    position: player.position,
    conduct: conductNow(),
    // Whatever is in the building worth looking at that is not the player: the
    // animals, and anybody walking round with a note on their back.
    sights: [...wildSights(), ...pranks.sights()],
  });
  hud.setAlarm(runSurveillance(dt, status));
  runEars();
    hud.setStrikes(writeups.count, writeups.max);
    hud.setTask(taskCard());
    hud.update(clock, escalation.dismissed ? { ...status, phase: 'dismissed' } : status);
    finishFrame(dt);
    return;
  }


  /*
   * What you are holding beats what is lying about.
   *
   * Placement is resolved before pickup so that a napkin on the ballroom floor
   * cannot eat the key press that was going to set a round. Your hands are
   * already full of something with a use here; the floor can wait.
   */
  const holding = hands();
  runSnap(inBallroom, holding, reading);
  const placement = inBallroom
    ? resolvePlacement({
        setup,
        carry: holding,
        position: player.position,
        spec: beos.active?.beo.setup ?? null,
        // The one thing being out of it is unambiguously good for.
        tolerance: impairment.tolerance,
      })
    : null;

  /*
   * Everything you could do where you are standing, gathered without doing any
   * of it, and then put in an order by `prompts.js`.
   *
   * This used to be two chains of `else if` — one deciding who got the key and
   * a second, written separately, deciding which line the screen showed. When
   * those two disagree the player presses a key that does something other than
   * what they read, and three presses were eaten that way. Now there is one
   * list: the front of it is what the key does and what the screen says, and
   * the rest of it is what holding the key offers.
   *
   * Every rule about which comes first is in that module, next to the failure
   * that bought it, and has tests.
   */
  /*
   * Before anything else is asked what it wants, because it is the one verb
   * that is never in the running for the action key and never in the fan.
   */
  runSubmit(inBallroom);

  const dealOnTheTable = runDialogue();
  const concealing = runConceal(dt);

  const candidates = [
    ...offerTalk(dealOnTheTable),
    ...[].concat(offerTarget(target, status) ?? []),
    offerLinen(target),
    target ? null : offerErrand(),
    ...(inBallroom ? runBallroom(placement, holding) : []),
    ...offerCart(),
    offerPickup(),
    ...offerClimb(),
    ...offerConceal(concealing),
    offerCharm(),
    ...offerPranks(),
    ...pocket.offer(),
    offerClass(),
    offerJob(),
  ];
  if (!inBallroom) setupView.setGuides({ visible: false });

  const here = route(quieted(keptBack(candidates)), aimNow());
  lastFocus = here.top?.focus ?? null;
  // Held for the debug handle, so a script can read exactly what the player is
  // reading and choose exactly what the player could choose.
  onOffer = here;
  runAction(here);

  hud.setHands(handsReadout(), aim);

  /*
   * Doors follow standing, so they are checked where standing is: every frame,
   * cheaply, rather than hooked to the dozen places standing moves from. A
   * change is announced, because a door that quietly re-locks feels broken.
   */
  const swung = access.changes();
  const coverNow = coverWas;
  coverWas = frontCover;
  if (swung.length) {
    applyShortfalls();
    for (const change of swung) {
      // The errand's cover coming and going is not the marble being yours.
      if (change.id === 'foh-lobby' && (frontCover || coverNow)) continue;
      hud.say(change.allowed ? change.rule.granted : change.rule.withdrawn, {
        tone: change.allowed ? 'good' : 'warn',
        seconds: 5,
      });
    }
  }

  perception.observe(dt, { position: player.position, conduct: conductNow(), sights: wildSights() });
  hud.setAlarm(runSurveillance(dt, status));
  runEars();
  hud.setStrikes(writeups.count, writeups.max);
  hud.setTask(taskCard());
  hud.update(clock, escalation.dismissed ? { ...status, phase: 'dismissed' } : status);

  finishFrame(dt);
}

/**
 * The tail of every frame: name tags, the debug panel, and drawing.
 *
 * Its own function because reading the sheet returns out of the middle of the
 * frame, and a frame that returns early and forgets to draw is a frame that
 * looks like a crash.
 */
/**
 * How far the ballroom is set, from nothing to every table the function wants
 * dressed. A plain fraction for the look to light the room by; worked out here
 * so the look never holds the setup.
 */
function ballroomSet() {
  const room = setups.ballroom;
  const dressed = room.tables.filter((table) => table.cover).length;
  if (!dressed) return 0;
  // Against what the function asked for while there is one; once it has
  // started (and is no longer on the board) the room stays dressed for it.
  const want = (venueOf(beos.active?.beo) === 'ballroom' ? beos.active?.beo.setup?.tableCount : null) ?? room.tables.length;
  return Math.round(Math.min(1, dressed / Math.max(1, want)) * 100) / 100;
}

/** Lights the frame for the hour and where the camera is, and draws it. */
/**
 * Where a fade is due: the day turned over (a night's sleep, a suspension's
 * days off, a shift slept through) or a chapter card has just come up.
 */
let fadeDay = null;
let chapterWas = false;
function fadeOnChange() {
  if (clock.day !== fadeDay) {
    if (fadeDay !== null) fader.flash({ hold: 0.35, seconds: 1.2 });
    fadeDay = clock.day;
  }
  if (chapterPanel.open && !chapterWas) fader.flash({ hold: 0.05, seconds: 0.7 });
  chapterWas = chapterPanel.open;
}

/** Real frame times, for the post's own budget. */
let slowFrames = 0;
let lastDrawAt = null;

/** How far into the night it is, 0 by day and 1 after dark, eased at dusk and dawn. */
function nightOf(minutes) {
  const ramp = (from, to) => Math.min(1, Math.max(0, (minutes - from) / (to - from)));
  return Math.max(1 - ramp(5 * 60 + 45, 6 * 60 + 45), ramp(18 * 60 + 45, 20 * 60));
}

function draw(dt) {
  fader.frame(dt);
  if (look) {
    const at = rig.camera.position;
    look.update({
      minutes: clock.minutes,
      camera: { x: at.x, y: at.y, z: at.z },
      room: level.roomAt(at.x, at.z)?.id ?? null,
      dt,
      ballroomSet: ballroomSet(),
    });
  }
  if (post) {
    // Held to a budget on the machine it is on: a second and a half of frames
    // slower than 45 a second drops the next most costly thing.
    const now = performance.now();
    if (lastDrawAt !== null) slowFrames = now - lastDrawAt > 1000 / 45 ? slowFrames + 1 : Math.max(0, slowFrames - 2);
    lastDrawAt = now;
    if (slowFrames > 90) {
      slowFrames = 0;
      post.downgrade();
    }
    const at = rig.camera.position;
    post.render({
      zone: level.roomAt(at.x, at.z)?.zone ?? 'boh',
      night: nightOf(clock.minutes),
      dt,
    });
    return;
  }
  stage.render();
}

/* --- the minimap ------------------------------------------------------------------
 *
 * What it draws is decided here, because only here knows what the player
 * perceives. The walls and the solid things are the level's own boxes; the
 * markers are where the objective line says to go (the same branch that wrote
 * the words: `where` on `objectiveFor()`), plus errands, things waiting to be
 * fetched, and the live missions. People go on it only when the player can
 * see them — in front of the camera, near enough, nothing in the way — or
 * hear them: somebody saying something in earshot, whoever you are talking
 * to, or somebody close enough to touch. Nobody else, ever.
 */
const minimap = (() => {
  const solids = levelSolids(LEVEL).filter((entry) => entry.solid !== false);
  const ground = (b) => b.min.y < 0.1;
  const walls = solids.filter((e) => !['floor', 'ceiling', 'swipe', 'prop', 'barrier'].includes(e.tag) && ground(e.metres)).map((e) => e.metres);
  const props = solids.filter((e) => e.tag === 'prop' && e.visible !== false).map((e) => e.metres);
  const rooms = level.rooms.map((room) => ({
    id: room.id,
    ceiling: room.ceiling,
    bounds: { min: { x: room.bounds.minX, z: room.bounds.minZ }, max: { x: room.bounds.maxX, z: room.bounds.maxZ } },
  }));
  // Inside the HUD's own block, so whatever hides the HUD for a panel hides
  // the map with it, whether or not a frame runs while the panel is up.
  return createMinimap({ parent: hud.root, walls, props, rooms });
})();

/** Every key and what it does, on H. In the HUD's block for the same reason. */
const controlsCard = createControlsCard({ parent: hud.root, controls: CONTROLS });
const helpCard = createHelpCard({ parent: ui });

/**
 * "What do I do?" (Q, the d-pad's down, or the pause menu): the goal on the
 * task card, where it is with the floor guide drawn to it, and the one or two
 * buttons it needs (`ui/helpCard.js`). The route line is drawn while it is up
 * even if it was switched off.
 */
function showHelp() {
  helpCard.show(helpState());
  helpSince = 0;
}

let helpSince = 0;
function runHelp(dt) {
  if (pressed('help') && !actionKey.fanning && !readingSheet) {
    if (helpCard.open) helpCard.hide();
    else if (!modalOpen()) showHelp();
  }
  if (!helpCard.open) return;
  if (modalOpen() || (input.pad.cancel?.() && !actionKey.fanning)) {
    helpCard.hide();
    return;
  }
  helpSince += dt;
  if (helpSince >= 0.25) {
    helpSince = 0;
    helpCard.refresh(helpState());
  }
  helpCard.tick(dt);
}

/** Which way a point is from where the camera looks, in words. */
function turnWord(point) {
  const dx = point.x - player.position.x;
  const dz = point.z - player.position.z;
  const f = rig.forward;
  const angle = Math.atan2(dx * f.z - dz * f.x, dx * f.x + dz * f.z);
  const a = Math.abs(angle);
  const side = angle > 0 ? 'left' : 'right';
  if (a < 0.6) return 'straight ahead';
  if (a < 1.4) return `ahead, to your ${side}`;
  if (a < 2.4) return `to your ${side}`;
  return 'behind you';
}

/** What the card says: the goal, why, where, the buttons, what comes after. */
function helpState() {
  const card = taskCard();
  const objective = allows('objective') ? objectiveFor() : null;
  const goal = card ? `${card.do}${card.progress ? `  (${card.progress})` : ''}` : 'Nothing needs doing right now';
  let where = null;
  if (routeGoal?.point) {
    const room = level.roomAt(routeGoal.point.x, routeGoal.point.z)?.name ?? null;
    if (routeGoal.arrived) where = room ? `You are there: the ${room}.` : 'You are there.';
    else {
      const metres = routePath ? pathLength(routePath) : Math.hypot(routeGoal.point.x - player.position.x, routeGoal.point.z - player.position.z);
      const next = routePath?.[Math.min(2, routePath.length - 1)] ?? routeGoal.point;
      where = `${room ? `The ${room}, ` : ''}${sayFeet(metres)} away, ${turnWord(next)}. Follow the gold arrows on the floor.`;
    }
  }
  // With a room in hand the first step under the line is the task itself;
  // without one, the line is the task and the first step is what follows it.
  const after = ((objective?.next ?? [])[beos.active ? 1 : 0] ?? '').replace(/^Then,? /, '') || null;
  return {
    goal,
    why: card?.why ?? null,
    where,
    buttons: helpButtons(card),
    then: card?.kind === 'lesson' ? null : after,
    foot: '[hold E] everything else you can do here  ·  [H] every control  ·  [Q] close',
  };
}

/**
 * The one or two buttons the goal needs: the one it names, and the press the
 * prompt is offering if there is one; else what the goal's verb is done with.
 */
function helpButtons(card) {
  const rows = [];
  const text = card?.do ?? '';
  const named = /\[(hold )?([A-Za-z]+)\]/.exec(text);
  if (named) {
    let what = text.replace(/\s*[:(]?\s*(press |tap |with )?\[(hold )?[A-Za-z]+\][^,.;]*\)?/, '').replace(/^\s*(Press|Tap)\s*/, '').trim();
    if (!what) what = text.replace(/\[(hold )?[A-Za-z]+\]\s*/, '').trim();
    rows.push([`[${named[1] ?? ''}${named[2]}]`, what.charAt(0).toLowerCase() + what.slice(1)]);
  }
  const top = onOffer.top;
  if (top && !top.blocked && rows.length < 2 && !(named && named[2] === 'E' && !named[1])) rows.push(['[E]', promptWords(top)]);
  if (rows.length < 2) {
    const verb = (text.match(/^[A-Za-z]+/) ?? [''])[0].toLowerCase();
    const say = {
      bring: 'pick them up where the arrows end',
      set: 'beside the table, or [C] for the nearest spot',
      lay: 'beside the table, or [C] for the nearest spot',
      ask: 'talk to them when you get there',
      go: 'when you get there',
      collect: 'pick it up where the arrows end',
      pick: 'pick it up',
      clock: 'at the time clock',
      be: 'at the time clock, once it opens',
      take: 'read the board, then Accept a card',
      change: 'at your locker',
      sleep: 'on the cot',
      wait: 'read the board once it posts',
    }[verb];
    if (say && !rows.some(([key]) => key === '[E]')) rows.push(['[E]', say]);
  }
  return rows.slice(0, 2);
}

const SEE_REACH = ft(90);
const HEAR_REACH = ft(35);
const HEAR_THROUGH = ft(12);
const TOUCH_REACH = ft(5);

/** The people the player can see or hear this moment, and only them. */
function perceivedPeople() {
  const eye = player.eye;
  const forward = rig.forward;
  const length = Math.hypot(forward.x, forward.z) || 1;
  const talking = conversation?.talkingTo ?? null;
  const out = [];
  for (const npc of npcs) {
    if (!npc.present) continue;
    const { x, z } = npc.position;
    const dx = x - player.position.x;
    const dz = z - player.position.z;
    const d = Math.hypot(dx, dz);
    const inFront = d > 1e-3 && (dx * forward.x + dz * forward.z) / (d * length) > 0.5;
    const seen =
      d < SEE_REACH &&
      inFront &&
      (segmentClear(eye, { x, y: 1.5, z }, level.world) || segmentClear(eye, { x, y: 0.9, z }, level.world));
    const speaking = Boolean(npc.note) && d < HEAR_REACH && (d < HEAR_THROUGH || segmentClear({ x: eye.x, y: 1.2, z: eye.z }, { x, y: 1.2, z }, level.world));
    const heard = npc === talking || speaking || d < TOUCH_REACH;
    if (seen || heard) out.push({ x, z, seen, heard, talking: npc === talking });
  }
  return out;
}

/** A place named as a prop or a room, in metres. */
function placeOf(where) {
  if (!where) return null;
  if (where.prop) {
    const p = propAt(where.prop);
    return p ? { x: p.x, z: p.z } : null;
  }
  const room = where.room ? level.rooms.find((r) => r.id === where.room) : where.roomName ? level.rooms.find((r) => r.name === where.roomName) : null;
  return room ? { x: (room.bounds.minX + room.bounds.maxX) / 2, z: (room.bounds.minZ + room.bounds.maxZ) / 2 } : null;
}

/** Everything the map marks: the objective, the missions, errands, fetches. */
function mapTargets() {
  const out = [];
  const add = (at, main) => at && out.push({ x: at.x, z: at.z, main });
  add(placeOf(objectiveFor()?.where), true);
  // The missions a beat can start, each where it is.
  const rescue = rescues?.active;
  if (rescue) {
    const guest = npcs.find((n) => n.data.strands === rescue.id);
    const row = RESCUES_BY_ID[rescue.id];
    if (guest && !rescue.aboard && guest.present) add({ x: guest.position.x, z: guest.position.z }, true);
    else add(placeOf({ room: row?.safe?.[0] }), true);
  }
  const search = searches.active;
  if (search && !search.found) {
    // A hunt: where somebody last said they saw him, never where he is.
    const seen = Object.values(searches.sightings(search.id));
    const last = seen.sort((a, b) => (b.at ?? 0) - (a.at ?? 0))[0];
    if (last) add(placeOf({ roomName: last.room }), true);
  }
  for (const id of dinners.wanted) add(placeOf({ room: DINNERS_BY_ID[id]?.lawn }), true);
  // The cart road's jobs: whoever is waiting, and then where they are going;
  // the burros; the ice cart, and where it is wanted.
  const shuttle = shuttles.active;
  if (shuttle) {
    const waiting = npcs.filter((n) => n.data.shuttle === shuttle.id && n.present && !n.aboard && shuttle.arrived[n.data.id] === undefined);
    const left = waiting.filter((n) => level.roomAt(n.position.x, n.position.z)?.id !== shuttles.destination?.room);
    if (left.length) for (const n of left) add({ x: n.position.x, z: n.position.z }, true);
    else if (shuttles.destination) add({ x: ft(shuttles.destination.x), z: ft(shuttles.destination.z) }, true);
  }
  if (blockades.active) add(blockades.where, true);
  const ice = deliveries.reading;
  if (ice) {
    add(ice.to, true);
    if (ice.cart) add(ice.cart, false);
  }
  // Errands, and anything left waiting for you to fetch.
  const now = clock.day * 1440 + clock.minutes;
  for (const task of tasks.list) {
    if (task.expired || task.dueAbsolute < now || !task.target) continue;
    add({ x: ft(task.target.x), z: ft(task.target.z) }, false);
  }
  if (beos.active) for (const place of Object.keys(PLACES)) if (stores.waitingAt(place)) add(placeOf({ prop: place }), false);
  // Classes on or about to be, and where a mission is waiting to start.
  for (const { at } of classesUp()) out.push({ x: at.x, z: at.z, main: false, kind: 'class' });
  for (const { place } of missionsUp()) if (place) out.push({ x: place.x, z: place.z, main: false, kind: 'mission' });
  return out;
}

/*
 * The way to the objective: a GPS line on the floor, the same line on the
 * minimap, and a marker with the distance that pins itself to the edge of the
 * screen when the objective is behind you (`docs/feel-reference.md`, section
 * 3). A reading of the collision world (`core/route.js`), built a fixed number
 * of cells a frame, deciding nothing.
 */
const router = (() => {
  const b = { minX: Infinity, minZ: Infinity, maxX: -Infinity, maxZ: -Infinity };
  for (const room of level.rooms) {
    b.minX = Math.min(b.minX, room.bounds.minX);
    b.minZ = Math.min(b.minZ, room.bounds.minZ);
    b.maxX = Math.max(b.maxX, room.bounds.maxX);
    b.maxZ = Math.max(b.maxZ, room.bounds.maxZ);
  }
  const margin = 6;
  // A quarter metre: finer than the shallowest stair tread (the apron's are a
  // foot), so no step between two cells is ever two risers.
  return createRouter({ world: level.world, bounds: { minX: b.minX - margin, minZ: b.minZ - margin, maxX: b.maxX + margin, maxZ: b.maxZ + margin }, cell: 0.25 });
})();
const routeView = createRouteView();
stage.scene.add(routeView.object3D);
const highlightView = createHighlightView();
stage.scene.add(highlightView.object3D);
const PROP_HEIGHT = new Map(LEVEL.props.map((prop) => [prop.id, ft(prop.height ?? 3)]));

/** A thing's box for its outline: `{ x, y, z, hx, hy, hz, person }`, metres. */
function propBox(id) {
  const p = propAt(id);
  return p ? { x: p.x, y: p.y, z: p.z, hx: p.halfX, hy: (PROP_HEIGHT.get(id) ?? 1) / 2, hz: p.halfZ } : null;
}
function entityBox(e) {
  const row = OBJECTS[e.kind];
  const r = ft(row?.radius ?? 0.5);
  return { x: e.position.x, y: e.position.y ?? 0, z: e.position.z, hx: r, hy: ft(row?.height ?? 1) / 2, hz: r };
}
function personBox(n) {
  return { x: n.position.x, y: n.position.y ?? 0, z: n.position.z, hx: 0.34, hy: 0.95, hz: 0.34, person: true };
}

/** The box the prompt's thing takes up, for the bright outline round it. */
function boxOf(c) {
  if (!c) return null;
  const tag = /^(entity|npc|cart):(.+)$/.exec(c.focus ?? '');
  if (tag?.[1] === 'entity') {
    const e = entities.all.find((x) => String(x.id) === tag[2]);
    if (e) return entityBox(e);
  }
  if (tag?.[1] === 'npc') {
    const n = npcs.find((x) => x.data.id === tag[2]);
    if (n) return personBox(n);
  }
  if (tag?.[1] === 'cart') {
    const cart = wheeled.all.find((x) => String(x.id) === tag[2]);
    if (cart) {
      const r = ft(cart.definition?.radius ?? 2);
      return { x: cart.position.x, y: cart.position.y ?? 0, z: cart.position.z, hx: r, hy: ft(cart.definition?.height ?? 3) / 2, hz: r };
    }
  }
  if (c.prop) return propBox(c.prop);
  if (String(c.id).startsWith('job-') && c.at) return personBox({ position: c.at });
  if (c.at && (c.id === 'place' || c.id === 'strike')) {
    return { x: c.at.x, y: 0, z: c.at.z, hx: ft(3), hy: ft(1.4), hz: ft(3) };
  }
  return null;
}

/**
 * What is outlined this frame: the prompt's thing, bright; and, soft, what
 * the task needs that is near enough to see: the places it sends you, the
 * people it names, and loose things of the kinds it wants.
 */
function runHighlights(dt) {
  const list = [];
  if (!hud.root.hidden && !modalOpen()) {
    const top = onOffer.top && !onOffer.top.blocked ? boxOf(onOffer.top) : null;
    if (top) list.push({ ...top, strong: true });
    const me = player.position;
    const near = (b, metres) => b && Math.hypot(b.x - me.x, b.z - me.z) <= metres && !(top && Math.abs(top.x - b.x) < 0.05 && Math.abs(top.z - b.z) < 0.05);
    for (const id of taskFocus.props) {
      const b = propBox(id);
      if (near(b, 24)) list.push(b);
    }
    for (const n of npcs) {
      if (!n.present || !taskFocus.people.has(n.data.id)) continue;
      const b = personBox(n);
      if (near(b, 24)) list.push(b);
    }
    const loose = entities.all
      .filter((e) => e.state === 'world' && taskFocus.kinds.has(e.kind) && !onShelf(e))
      .map((e) => entityBox(e))
      .filter((b) => near(b, 14))
      .sort((a, b) => Math.hypot(a.x - me.x, a.z - me.z) - Math.hypot(b.x - me.x, b.z - me.z))
      .slice(0, 8);
    list.push(...loose);
  }
  highlightView.set(list);
  highlightView.update(dt);
}

/** Cells of route work a frame: a fraction of a millisecond, the same count every run. */
const ROUTE_BUDGET = 6000;

/** Within this of the objective, or in its room, you are there and the line goes. */
const ARRIVED = ft(8);
let routePath = null;
let routeGoal = null;
let routeSince = Infinity;
let routeBarriers = null;

/*
 * The guided first days, on the screen.
 *
 * Built here because only here is everything a lesson asks about: where the
 * player is, what they are wearing and holding, the board, the room, the
 * panels. `tutorial.js` decides; this reads the game for it and hides what is
 * not open yet.
 */
let lesson = null;
let lessonAnchor = null;
/** Things a tip waits to see once: the sheet read, the wheel opened, a snap, a throw, a class sat. */
const tutorialSeen = { sheet: false, fanned: false, snapped: false, threw: false, satClass: false };

/** Feet from the player to the nearest edge of a prop's box. */
function feetTo(propId) {
  const p = propAt(propId);
  if (!p) return Infinity;
  const dx = Math.max(Math.abs(player.position.x - p.x) - (p.halfX ?? 0), 0);
  const dz = Math.max(Math.abs(player.position.z - p.z) - (p.halfZ ?? 0), 0);
  return toFeet(Math.hypot(dx, dz));
}

/** The room the lesson is about: the active event's, or day one's first. */
function lessonSpec() {
  return beos.active?.beo.setup ?? FIRST_BEO.setup;
}

/** The planned anchor a lesson points at: the next one to set, seat or dress. */
function anchorFor(kind) {
  const spec = lessonSpec();
  const keys = [...(plannedKeys(spec) ?? [])];
  const anchors = keys.map((key) => setup.anchorAt(key)).filter(Boolean);
  const each = spec.chairsPerTable ?? 0;
  const wanted =
    kind === 'round'
      ? anchors.filter((a) => !setup.tableAt(a.key))
      : kind === 'seat'
        ? anchors.filter((a) => setup.tableAt(a.key) && setup.tableAt(a.key).seats < each)
        : kind === 'cover'
          ? anchors.filter((a) => setup.tableAt(a.key) && !setup.tableAt(a.key).cover)
          : [];
  if (!wanted.length) return null;
  return wanted.reduce((best, a) =>
    Math.hypot(a.x - player.position.x, a.z - player.position.z) < Math.hypot(best.x - player.position.x, best.z - player.position.z) ? a : best
  );
}

/** Where the lesson or tip on screen points, as the floor guide's target. */
function lessonPlace() {
  const where = lesson?.where;
  if (!where) return null;
  if (where.prop) {
    const p = propAt(where.prop);
    return p ? { point: { x: p.x, z: p.z }, half: { x: p.halfX ?? 0, z: p.halfZ ?? 0 }, room: null } : null;
  }
  if (where.point) return { point: where.point, room: null };
  if (where.anchor === 'room') {
    const room = level.rooms.find((r) => r.id === (beos.active?.beo.room ?? FIRST_BEO.room));
    return room ? { point: { x: (room.bounds.minX + room.bounds.maxX) / 2, z: (room.bounds.minZ + room.bounds.maxZ) / 2 }, room: room.id } : null;
  }
  if (where.anchor) {
    const a = anchorFor(where.anchor);
    return a ? { point: { x: a.x, z: a.z }, room: null } : null;
  }
  return null;
}

/** A job somebody wants done first for this room (a trade), and where it is. */
/*
 * Any errand with somewhere to be: a trade's job, and a favour being called in
 * ("You owe me and I am calling it. Now, please."). The floor guide went only
 * to a trade's, and only with a room in hand, so a new starter who had asked
 * for linen, as the screen told them to, never went when the favour was
 * called, and was down fourteen with Housekeeping each time (`fullgame.mjs`).
 */
function errandForTip() {
  const now = clock.day * 1440 + clock.minutes;
  const task = tasks.list.find((t) => !t.expired && t.dueAbsolute >= now && t.target && (t.shortfallId || t.source === 'favor'));
  return task ? { label: task.label, at: { x: ft(task.target.x), z: ft(task.target.z) }, who: task.target.label, due: task.dueAbsolute, favour: task.source === 'favor' } : null;
}

/**
 * Which way of covering a shortfall is actually open with its person, said as
 * the press or the hold that does it. Never an ask that gets you reported
 * (a new starter who did as the screen said was written up and suspended),
 * never an ask that already came to nothing today (asking again gets the
 * same shrug), and not an ask a cold department refuses.
 */
function howToGet(entry, npc) {
  const options = (npc && dealFor(npc)?.options) ?? [];
  const flaked = deals.flaked(entry);
  const open = (kind) => options.some((o) => o.kind === kind && o.allowed && !o.reportsYou && !(kind === 'ask' && flaked));
  const reports = options.some((o) => o.kind === 'ask' && o.allowed && o.reportsYou);
  const why = reports ? ' (asking would get you reported)' : flaked ? ' (asking did not work today)' : '';
  if (open('ask')) return 'press [E] again to ask';
  // The safe one is the tap now (`sim/deals.js`), so it is a press, not a hold.
  if (open('trade')) return `press [E] again to trade: they will want a job done first${why}`;
  /*
   * Never "take it". It was the last word here, and the person you would take
   * it from is the one standing at the desk you were sent to: a new starter
   * did as it said in front of Priya and was named for theft. The room is
   * filed on its rounds, chairs and linen; the rest is the grade.
   */
  return NOBODY;
}
const NOBODY = 'nobody will cover it today: the room can still be filed without it';

/**
 * Who to see about a department's shortfall: its gatekeeper if they are on,
 * else anybody from that department who is on and will deal. Chef Danny is
 * not on the morning shift, and a new starter told to "see Chef Danny" on day
 * three walked the kitchen looking for him while the room's deadline went by.
 */
function holderFor(owner) {
  const gate = gateFor(owner);
  const at = gate && npcs.find((n) => n.data.id === gate.id && n.present);
  if (at) return { npc: at, name: gate.name };
  const other = npcs.find((n) => n.present && STAFF_BY_ID[n.data.id]?.department === owner && !STAFF_BY_ID[n.data.id]?.enforces && dealFor(n)?.options);
  return other ? { npc: other, name: STAFF_BY_ID[other.data.id].name } : null;
}

/** The objective's word on a shortfall: whom to see and the way that is open with them. */
function howForObjective(entry) {
  const holder = holderFor(SHORTFALLS[entry.id]?.owner);
  return holder ? howToGet(entry, holder.npc).replace(/^press \[E\] again to /, 'talk with [E], then [E] to ') : null;
}

/** The first thing the room is short of that is not covered yet, and whom to ask. */
function shortForTip() {
  const errand = new Set(tasks.list.filter((t) => t.shortfallId && !t.expired).map((t) => t.shortfallId));
  // Somebody already waiting on a job from you says only "go and do it".
  const waiting = new Set(tasks.list.filter((t) => !t.expired && t.staffId).map((t) => t.staffId));
  for (const entry of beos.active?.beo.shortfalls ?? []) {
    if (entry.resolved || errand.has(entry.id)) continue;
    const row = SHORTFALLS[entry.id];
    if (!row) continue;
    // Stock or a door: either way it is somebody's, and they are who to see.
    const holder = holderFor(row.owner);
    if (!holder) continue;
    const { npc } = holder;
    if (waiting.has(npc.data.id)) continue;
    const how = howToGet(entry, npc);
    // Nobody to see about it: not somewhere to send anybody.
    if (how === NOBODY) continue;
    // Nor onto the marble in blacks to ask: said instead (`withFront`).
    if (inFrontOfHouse(npc.position) && !frontOk()) continue;
    return { wants: row.wants ?? row.label.toLowerCase(), who: whom(holder.name), how, at: { x: npc.position.x, z: npc.position.z } };
  }
  return null;
}

function tutorialReading() {
  const status = shift.status(clock);
  const holding = hands();
  const spec = lessonSpec();
  const open = classBoard().find((slot) => slot.open);
  const classRow = open ? classSpot(open.entry) : null;
  return {
    day: clock.day,
    dayIndex: tutorial.dayIndex(clock.day),
    near: (id, feet) => feetTo(id) <= feet,
    uniform: clothing.isUniform,
    onTheClock: status.phase === 'working' || status.phase === 'over',
    boardOpen: beoPanel.open,
    active: beos.active?.beo.id ?? null,
    holding: (kind) => holding.kind === kind && holding.count > 0,
    counts: setup.counts,
    want: { tables: spec.tableCount, chairs: spec.tableCount * spec.chairsPerTable, each: spec.chairsPerTable },
    filed: beos.history.some((record) => record.id === FIRST_BEO.id) || gradePanel.open,
    gradeOpen: gradePanel.open,
    shiftDone: status.clockedOutAt !== null && status.clockedOutAt !== undefined,
    summaryOpen: summaryPanel.open,
    room: level.roomAt(player.position.x, player.position.z)?.id ?? null,
    sheetSeen: tutorialSeen.sheet,
    short: shortForTip(),
    errand: errandForTip(),
    moreHere: (onOffer.rest?.length ?? 0) > 0,
    fanned: tutorialSeen.fanned,
    pushing: Boolean(wheeled.active),
    canSnap: Boolean(snapping?.anchor),
    snapped: tutorialSeen.snapped,
    classOn: open ? { name: open.entry.name, at: classRow } : null,
    satClass: tutorialSeen.satClass,
    holdingSmall: holding.count > 0 && !holding.twoHanded && !wheeled.active,
    threw: tutorialSeen.threw,
    pocketFull: pocket.contents.length > 0,
    prankHere: prankOffersHere > 0,
    strikeOnBoard: beoPanel.open && beos.board(clock).some((card) => card.beo.teardown),
    jobUp: jobsUp().some((up) => up.person),
    sentFront: frontCover && clothing.isUniform,
    inFront: inFrontOfHouse(player.position),
  };
}

let prankOffersHere = 0;

/**
 * Whether a thing the key could do is part of the task on screen
 * (`taskFocus`): setting the room, the people it names, the kinds of thing it
 * wants, the places it sends you, the cart you have hold of.
 */
function onTask(c) {
  if (c.tier === TALKING || c.tier === PLACING) return true;
  if (c.id === 'errand' || String(c.id).startsWith('deal')) return true;
  if (c.prop) return taskFocus.props.has(c.prop);
  if (c.id === 'pickup') return taskFocus.kinds.has(c.entityKind);
  const person = /^npc:(.+)$/.exec(c.focus ?? '')?.[1];
  if (person) return taskFocus.anyone || taskFocus.people.has(person);
  if (String(c.id).startsWith('job-') || c.id === 'class') return !beos.active;
  if (String(c.id).startsWith('cart')) return Boolean(wheeled.active) || bodyReading().seated || taskFocus.carts;
  return false;
}

/** How close, and how squarely faced, a thing off the task has to be to offer itself. */
const QUIET = Object.freeze({ reach: 2.1, facing: 0.8 });

/**
 * The quiet floor. Something the task has nothing to do with does not put a
 * prompt up as you walk past it: only once you have stopped (no stick, no
 * keys) beside it and are facing it. Until then it is still a hold away, in
 * the fan, like everything else here. A new starter walking a chair room
 * through storage had a wet floor sign, a coaster, a mop and a colleague
 * each offer themselves in turn, and could not tell which one the screen
 * meant (`fullgame.mjs --careless`).
 */
function quieted(list) {
  const still = Math.hypot(keys.move.x, keys.move.y) < 0.15;
  const me = player.position;
  const cam = rig.forward;
  const body = { x: Math.sin(player.yaw ?? 0), z: Math.cos(player.yaw ?? 0) };
  return list.map((c) => {
    if (c.tier !== NEAR && c.tier !== FIXED) return c;
    if (onTask(c)) return c;
    if (still && faced(c)) return c;
    return { ...c, tier: POSSIBLE, quiet: true };
  });
  function faced(c) {
    if (!c.at) return true;
    const dx = c.at.x - me.x;
    const dz = c.at.z - me.z;
    const d = Math.hypot(dx, dz);
    // How far is to the thing's edge where the offer says (a cart is long).
    const reach = Number.isFinite(c.distance) && c.distance > 0 ? Math.min(c.distance, d) : d;
    if (reach > QUIET.reach + (c.tier === FIXED ? 0.6 : 0)) return false;
    if (d < 0.35) return true;
    const cos = (v) => (dx * v.x + dz * v.z) / (d * (Math.hypot(v.x, v.z) || 1));
    return cos(cam) >= QUIET.facing || cos(body) >= QUIET.facing;
  }
}

/**
 * Takes out of what the action key could do here everything the guided days
 * have not taught yet: the hold's verbs, talking, carts, things on the floor,
 * pranks and the pocket. With no tutorial it hands the list back untouched.
 */
function keptBack(candidates) {
  const list = candidates.filter(Boolean);
  prankOffersHere = list.filter((c) => String(c.id).startsWith('prank')).length;
  /*
   * Taking a thing back off a spot where the plan wants it is never the tap,
   * guided days or not; it is still on the hold, for the time you meant it.
   * Standing on the round you just set, "strike the round" was the top of the
   * list, and pressing the key to set the next one picked the table back up,
   * set it, picked it up, for as long as you stood there.
   */
  // The room you are setting, or the guided day's, never a teardown's: there
  // taking it all back is the job.
  const spec = beos.active ? (beos.active.beo.teardown ? null : beos.active.beo.setup) : tutorial.guiding ? FIRST_BEO.setup : null;
  const planned = spec ? plannedKeys(spec) : null;
  const kept = list.map((c) =>
    c.id === 'strike' && c.at?.key && planned?.has(c.at.key) && !c.at.keepClear ? { ...c, tier: POSSIBLE } : c,
  );
  if (!tutorial.active) return kept;
  return kept.filter((c) => {
    if (c.tier === POSSIBLE && !allows('wheel')) return false;
    if ((c.id === 'talk' || c.id === 'charm') && !allows('talk')) return false;
    if (c.kind === CART && !wheeled.active && !allows('carts')) return false;
    if (c.id === 'pickup' && !allows('pickups')) return false;
    if (String(c.id).startsWith('prank') && !allows('pranks')) return false;
    if (String(c.id).startsWith('consume') && !allows('consumables')) return false;
    return true;
  });
}

function runTutorial(dt) {
  if (readingSheet && beos.active) tutorialSeen.sheet = true;
  if (actionKey.fanning) tutorialSeen.fanned = true;
  lesson = tutorial.frame(tutorialReading(), dt);
  const place = lesson?.where?.anchor && lesson.where.anchor !== 'room' ? anchorFor(lesson.where.anchor) : null;
  lessonAnchor = place;
  /*
   * Over a panel only when the lesson is about that panel (the board, the
   * grade sheet, the summary); any other lesson waits behind a panel rather
   * than talking over it.
   */
  const aboutPanel =
    lesson?.id === 'open-board' || lesson?.id === 'accept' || lesson?.id === 'strikes'
      ? beoPanel.open
      : lesson?.id === 'grade'
        ? gradePanel.open
        : lesson?.id === 'summary'
          ? summaryPanel.open
          : false;
  lessonShown = Boolean(lesson && (!modalOpen() || aboutPanel));
}

/** Whether the lesson or tip is on screen this frame: not behind a panel it is not about. */
let lessonShown = false;

/** A lesson's "(2 of 6 set)", taken off its end to be the card's progress. */
function splitCount(words) {
  const m = /\s*\((\d+) of (\d+)[^)]*\)\s*$/.exec(words ?? '');
  return m ? { text: words.slice(0, m.index), progress: `${m[1]} of ${m[2]}` } : { text: words, progress: null };
}

/**
 * The task card (`hud.setTask`): the lesson on screen on the guided day, else
 * the objective's task, with a first-time tip as a note under it. One thing to
 * do, how far along it is, and why; never a second instruction beside it.
 */
function taskCard() {
  const pad = currentDevice() === 'gamepad';
  const words = (entry) => (pad && entry.pad ? entry.pad : entry.text);
  if (lessonShown && lesson.kind === 'lesson') {
    const { text, progress } = splitCount(words(lesson));
    return { kind: 'lesson', kicker: `First day · step ${lesson.step} of ${lesson.steps}`, do: text, progress };
  }
  const tip = lessonShown && lesson.kind === 'tip' ? words(lesson) : null;
  if (modalOpen() && !tip) return null;
  const objective = allows('objective') && !modalOpen() ? objectiveFor() : null;
  const task = objective ? objective.task ?? { do: objective.line, why: objective.next?.[0] ?? objective.progress } : null;
  if (!task) return tip ? { kind: 'tip', kicker: 'New', do: tip } : null;
  // A job out on the road with a clock on it, when nothing newer is being taught.
  const road = !tip ? (objective.errands ?? []).find((e) => e.tone === 'bad' || e.tone === 'warn') : null;
  return {
    kind: 'task',
    do: task.do,
    why: task.why,
    progress: task.progress ?? null,
    tone: objective.tone === 'bad' ? 'bad' : 'ready',
    note: tip ?? (road ? `${road.label} · ${road.left} left` : null),
  };
}

/**
 * Where a class is held, for its marker and its join prompt: the middle of its
 * room, or the prop it names.
 */
function classSpot(entry) {
  if (entry?.spot) {
    const p = propAt(entry.spot);
    if (p) return { x: p.x, z: p.z };
  }
  const room = level.rooms.find((r) => r.id === entry?.room);
  return room ? { x: (room.bounds.minX + room.bounds.maxX) / 2, z: (room.bounds.minZ + room.bounds.maxZ) / 2 } : null;
}

/*
 * Classes and missions you cannot miss (D: "classes need a trigger").
 *
 * A class open now is joined where it is held: walk up to its spot and the
 * prompt says "Class starting:" and A joins it. Before it opens, and while it
 * is open, it has a pin over its spot (pinned to the screen's edge when it is
 * off screen) and a mark on the map, and a notice says when it is about to
 * start and when it has. A mission that starts from a person has a "!" over
 * them whenever walking up to them would start it, and a mark on the map
 * where it plays. All drawing and words; nothing here changes what a class or
 * a beat is.
 */
const CLASS_REACH = ft(10);
/** Minutes before a class opens that its pin, its mark and its notice go up. */
const CLASS_SOON = 15;
const CLASS_NOTICE = 10;
const classSaid = new Set();

function offerClass() {
  const open = classBoard().find((slot) => slot.open);
  if (!open) return null;
  const spot = classSpot(open.entry);
  if (!spot) return null;
  const distance = Math.hypot(spot.x - player.position.x, spot.z - player.position.z);
  if (distance > CLASS_REACH) return null;
  return {
    id: `class-${open.entry.id}`,
    tier: NEAR,
    distance,
    at: spot,
    height: 1.6,
    focus: `class:${open.entry.id}`,
    lead: 'Class starting:',
    verb: 'join',
    what: open.entry.name,
    say: `join the class, ${open.entry.name}`,
    run: () => {
      tutorialSeen.satClass = true;
      sitClass(open.entry.id);
    },
  };
}

/**
 * Jobs waiting to be handed out (`data/jobs.js`): on today's board, posted,
 * not taken, with time left, and you with nothing in hand. Each with whoever
 * asks for it, if they are on the floor.
 */
let jobsUpAt = null;
let jobsUpWere = [];
function jobsUp() {
  // Asked several times a frame (the key, the pins, the tips), and the board
  // is worked out afresh each time it is read: once a frame is enough.
  const at = `${clock.day}|${clock.minutes}|${Boolean(beos.active)}`;
  if (at === jobsUpAt) return jobsUpWere;
  jobsUpAt = at;
  jobsUpWere = jobsUpNow();
  return jobsUpWere;
}

function jobsUpNow() {
  if (!allows('jobs') || beos.active || !shift.status(clock).onTheClock) return [];
  return beos
    .board(clock)
    .filter((card) => card.beo.job && card.state === 'posted' && card.live && card.remaining > 0)
    .map((card) => {
      const row = giverOf(card.beo.job);
      const person = row ? npcs.find((n) => n.present && n.data.id === row.id) : null;
      return { card, person };
    });
}

/** Walking up to whoever asks for a job: [E] takes it, the way the board's Accept does. */
const JOB_REACH = ft(8);
function offerJob() {
  for (const { card, person } of jobsUp()) {
    if (!person) continue;
    const distance = Math.hypot(person.position.x - player.position.x, person.position.z - player.position.z);
    if (distance > JOB_REACH) continue;
    return {
      id: `job-${card.beo.job}`,
      tier: NEAR,
      distance: distance - 0.05,
      at: person.position,
      height: 2.1,
      focus: `job:${card.beo.job}`,
      lead: `${person.data.name.replace(/ ".*"/, '')} has a job:`,
      verb: 'take',
      what: `${card.beo.title.toLowerCase()}, by ${formatTime(card.times.setupBy)}`,
      say: `take the job from ${person.data.name.replace(/ ".*"/, '')}: ${card.beo.title.toLowerCase()}`,
      blocked: heldBackFrom(card.beo),
      run: () => acceptCard(card.beo),
    };
  }
  return null;
}

/** The classes worth a pin and a mark now: open, or opening within `CLASS_SOON`. */
function classesUp() {
  return classBoard()
    .filter((slot) => slot.open || (slot.opensIn > 0 && slot.opensIn <= CLASS_SOON && slot.state !== 'closed'))
    .map((slot) => ({ slot, at: classSpot(slot.entry) }))
    .filter((c) => c.at);
}

/** The notices: about to start, and started. Once each, per class, per day. */
function runClassNotices() {
  for (const slot of classBoard()) {
    const soon = `${clock.day}:${slot.entry.id}:soon`;
    const now = `${clock.day}:${slot.entry.id}:open`;
    if (!slot.open && slot.opensIn > 0 && slot.opensIn <= CLASS_NOTICE && !classSaid.has(soon)) {
      classSaid.add(soon);
      hud.say(`Class in ${formatSpan(Math.ceil(slot.opensIn))}: ${slot.entry.name}, in ${slot.entry.where}. It is marked on your map.`, { tone: 'info', seconds: 6 });
    }
    if (slot.open && !classSaid.has(now)) {
      classSaid.add(now);
      hud.say(`Class starting now: ${slot.entry.name}, in ${slot.entry.where}. Walk up and press [E] to join.`, { tone: 'good', seconds: 7 });
    }
  }
}

/** The beats waiting for you, each with its person (if on the property) and its place. */
function missionsUp() {
  return chapters
    .waiting({
      shiftOver: shift.status(clock).phase === 'over',
      day: clock.day,
      minutes: clock.minutes,
      allow: (chapter) => allows(chapter.gives?.length ? 'consumables' : 'beats'),
    })
    .map((chapter) => {
      const person = chapter.speaker ? npcs.find((n) => n.present && (n.data.name === chapter.speaker || n.data.name.includes(chapter.speaker))) : null;
      return { chapter, person, place: placeOf({ room: chapter.where }) };
    });
}

/** The pins on the screen: classes and missions, projected over where they are. */
function runPins() {
  if (hud.root.hidden || modalOpen()) {
    hud.setPins([]);
    return;
  }
  const pins = [];
  // With a room in hand, a class or a beat is not what you are doing: their
  // pins wait until it is filed, unless the task itself names the person.
  const busy = Boolean(beos.active);
  for (const { slot, at } of busy ? [] : classesUp()) {
    const p = screenOf({ x: at.x, y: 2.1, z: at.z });
    const label = slot.open ? `Class: ${slot.entry.name}` : `Class at ${formatTime(clock.minutes + slot.opensIn)}`;
    pins.push(edgePin(p, { kind: 'class', label }));
  }
  for (const { card, person } of jobsUp()) {
    if (!person) continue;
    const p = screenOf({ x: person.position.x, y: 2.35, z: person.position.z });
    if (!p.inside || Math.hypot(person.position.x - player.position.x, person.position.z - player.position.z) > 40) continue;
    pins.push({ x: p.x, y: p.y, kind: 'mission', label: '!', title: `${person.data.name.replace(/ ".*"/, '')}: ${card.beo.title}`, edge: false });
  }
  for (const { chapter, person } of missionsUp()) {
    if (!person || (busy && !taskFocus.people.has(person.data.id))) continue;
    const p = screenOf({ x: person.position.x, y: 2.35, z: person.position.z });
    // Over the person while you can see where they are; not through walls.
    if (!p.inside || Math.hypot(person.position.x - player.position.x, person.position.z - player.position.z) > 40) continue;
    pins.push({ x: p.x, y: p.y, kind: 'mission', label: '!', title: chapter.speaker, edge: false });
  }
  hud.setPins(pins);
}

/** A pin over a point, or held at the screen's edge pointing at it. */
function edgePin(p, extra) {
  const margin = 50;
  if (p.inside && p.x > margin && p.x < p.width - margin && p.y > margin && p.y < p.height - margin) {
    return { ...extra, x: p.x, y: p.y, edge: false };
  }
  let dx = p.ndc.x;
  let dy = -p.ndc.y;
  if (p.behind) {
    dx = -dx;
    dy = -dy;
  }
  const length = Math.hypot(dx, dy) || 1;
  dx /= length;
  dy /= length;
  const t = Math.min(Math.abs(dx) > 1e-6 ? (p.width / 2 - margin) / Math.abs(dx) : Infinity, Math.abs(dy) > 1e-6 ? (p.height / 2 - margin) / Math.abs(dy) : Infinity);
  return {
    ...extra,
    x: Math.min(p.width - margin, Math.max(margin, p.width / 2 + dx * t)),
    y: Math.min(p.height - 110, Math.max(250, p.height / 2 + dy * t)),
    edge: true,
  };
}

/** The storage shelf each kind of thing a room wants comes from. */
const SHELF_OF = Object.freeze({ round: 'store-rounds', chair: 'store-chairs', linen: 'store-linen' });

/**
 * With a room on, the place the next useful thing is, rather than the room:
 * the person to ask while it is short of something, where covered stock was
 * left, the next spot while you carry something for it, and otherwise the
 * shelf the next missing thing is on. The objective's room only once nothing
 * else is wanted (filing). A new starter on day two followed the line to an
 * empty ballroom with nothing in their hands.
 */
/**
 * A cart left standing with some of what you are after on it: a new starter
 * loaded the shelf onto a cart, left it to run an errand, and was sent back
 * to the empty shelf for the rest of the afternoon.
 */
function loadedCartWith(kind) {
  let best = null;
  for (const cart of wheeled.all) {
    if (cart === wheeled.active || cart.tipped || cart.hold.kind !== kind || cart.hold.count <= 0) continue;
    const d = Math.hypot(cart.position.x - player.position.x, cart.position.z - player.position.z);
    if (!best || d < best.d) best = { cart, d };
  }
  return best?.cart ?? null;
}

/**
 * The nearest of a kind of thing lying loose indoors: put down, dropped or
 * thrown. Nine chairs set down on the ballroom floor were nine chairs the
 * floor guide never mentioned; with the shelf empty it sent a new starter
 * back to it until the doors opened (`fullgame.mjs`, day three).
 */
function looseOf(kind) {
  let best = null;
  for (const e of entities.all) {
    if (e.kind !== kind || e.state !== 'world' || !level.roomAt(e.position.x, e.position.z) || onShelf(e)) continue;
    const d = Math.hypot(e.position.x - player.position.x, e.position.z - player.position.z);
    if (!best || d < best.d) best = { e, d };
  }
  return best?.e ?? null;
}

/** How many of a kind are lying loose indoors, for the objective's line. */
function looseCount(kind) {
  return entities.all.filter((e) => e.kind === kind && e.state === 'world' && level.roomAt(e.position.x, e.position.z) && !onShelf(e)).length;
}

/** Whether a loose thing is lying on its own kind's storage shelf (and so is the shelf's). */
function onShelf(e) {
  const shelf = SHELF_OF[e.kind] ? propAt(SHELF_OF[e.kind]) : null;
  if (!shelf) return false;
  return Math.abs(e.position.x - shelf.x) <= (shelf.halfX ?? 0) + ft(2) && Math.abs(e.position.z - shelf.z) <= (shelf.halfZ ?? 0) + ft(2);
}

/** Loose things of a kind lying on its shelf, for the shelf to take back. */
function straysAt(kind) {
  return entities.all.filter((e) => e.kind === kind && e.state === 'world' && onShelf(e));
}

/**
 * The kinds of thing the room still wants that cannot be had today: none on
 * the shelf, in your hands, on the floor or on a cart, no round with one to
 * spare, nobody who will cover it, and nothing covered waiting to be fetched.
 * The room goes on without them and is filed as it stands. Told "No chairs
 * today: the room can still be filed without it" under a line that still said
 * to set the rounds, a new starter walked to the empty shelf until the doors
 * opened (`fullgame.mjs`, day eight).
 */
/**
 * Something in your hands the room has no use for: a salt shaker picked up off
 * the storage floor instead of the chairs beside it. Nothing else can be
 * picked up until it is put down, and nothing said so; a new starter stood at
 * the chair shelf with it until the doors opened (`fullgame.mjs`, day eight).
 */
function strayInHands() {
  const active = beos.active;
  if (!active?.beo.setup || active.beo.teardown) return null;
  const holding = hands();
  if (!holding.count || wheeled.active) return null;
  // A room's own kind is in hand for a reason while the room still wants it;
  // a spare cloth kept after the last round was dressed kept the coffee urn
  // out of a new starter's hands for two hours (`fullgame.mjs`, day five).
  if (['round', 'chair', 'linen'].includes(holding.kind)) {
    const needs = venueOf(active.beo) === 'ballroom' ? roomNeeds(setup, active.beo.setup) : null;
    const owed = !needs || (holding.kind === 'round' ? needs.missing.length : holding.kind === 'chair' ? needs.fewChairs.length : needs.bare.length + needs.wrongCloth.length) > 0;
    if (owed) return null;
  }
  if (active.beo.job && holding.claim === `job:${active.beo.job}`) return null;
  if ((active.beo.shortfalls ?? []).some((entry) => SHORTFALLS[entry.id]?.item === holding.kind)) return null;
  return (OBJECTS[holding.kind]?.name ?? holding.kind).toLowerCase();
}

function goneKinds() {
  const out = new Set();
  const active = beos.active;
  const spec = active?.beo.setup;
  if (!spec || active.beo.teardown || venueOf(active.beo) !== 'ballroom') return out;
  const holding = hands();
  for (const kind of ['round', 'chair', 'linen']) {
    if (stores.available(kind) > 0 || straysAt(kind).length) continue;
    if (holding.kind === kind && holding.count > 0) continue;
    if (looseCount(kind) > 0 || loadedCartWith(kind)) continue;
    if (kind === 'chair' && [...(plannedKeys(spec) ?? [])].some((key) => (setup.tableAt(key)?.seats ?? 0) > spec.chairsPerTable)) continue;
    const ofKind = (active.beo.shortfalls ?? []).filter((entry) => SHORTFALLS[entry.id]?.item === kind);
    const coverable = ofKind.some((entry) => {
      if (entry.resolved) return Object.values(SHORTFALLS[entry.id]?.at ?? {}).some((place) => stores.waitingAt(place));
      const how = howForObjective(entry);
      return how !== null && !/^nobody/.test(how);
    });
    if (!coverable) out.add(kind);
  }
  return out;
}

function nextStepPlace() {
  const spec = beos.active?.beo.setup;
  if (!spec || beos.active.beo.teardown) return null;
  // What is in your hands for this room goes down first: fetching anything
  // else with full arms is refused, and a new starter stood at the next pile
  // with an armful of rounds until the room's deadline went by.
  const holdingNow = hands();
  // A job's own thing in your hands goes where the job wants it first.
  const job = beos.active.beo.job ? jobs.next(holdingNow, player.position) : null;
  if (job?.drop) return { point: job.point, room: null };
  // Cloth of the wrong colour goes back to the rack, which swaps it.
  if (holdingNow.kind === 'linen' && holdingNow.count > 0 && spec.linen && holdingNow.variant && holdingNow.variant !== spec.linen) {
    const rack = propAt(SHELF_OF.linen);
    if (rack) return { point: { x: rack.x, z: rack.z }, half: { x: rack.halfX ?? 0, z: rack.halfZ ?? 0 }, room: null };
  }
  // A round off the plan is struck first, whatever you are holding, as the
  // objective says: with linen in hand the guide went to the nearest bare
  // round, the game refused a cloth on a round that is not on the plan, and a
  // room flip stood like that until its doors (`fullgame.mjs`, day nine).
  if (venueOf(beos.active.beo) === 'ballroom') {
    const early = roomNeeds(setup, spec);
    if (early?.offPlan.length && early.missing.length) {
      let best = null;
      for (const key of early.offPlan) {
        const a = setup.anchorAt(key);
        const d = a ? Math.hypot(a.x - player.position.x, a.z - player.position.z) : Infinity;
        if (a && (!best || d < best.d)) best = { a, d };
      }
      if (best) return { point: { x: best.a.x, z: best.a.z }, room: null };
    }
  }
  if (holdingNow.count > 0 && OBJECTS[holdingNow.kind]?.place && venueOf(beos.active.beo) === 'ballroom') {
    const role = OBJECTS[holdingNow.kind].place.role;
    const spot = anchorFor(role === 'base' ? 'round' : role);
    if (spot) return { point: { x: spot.x, z: spot.z }, room: null };
  }
  const errand = errandForTip();
  if (errand) return { point: errand.at, room: null };
  const short = shortForTip();
  if (short) return { point: short.at, room: null };
  // Rounds, then chairs, then linen, as `sim/objective.js` says them, read
  // spot by spot rather than off the totals (`sim/roomNeeds.js`).
  const stages = ['round', 'chair', 'linen'];
  const needs = roomNeeds(setup, spec, goneKinds());
  const stageNow = needs?.stage ?? 'linen';
  for (const entry of beos.active.beo.shortfalls ?? []) {
    if (!entry.resolved) continue;
    const item = SHORTFALLS[entry.id]?.item;
    if (stages.includes(item) && stages.indexOf(item) > stages.indexOf(stageNow)) continue;
    for (const place of Object.values(SHORTFALLS[entry.id]?.at ?? {})) {
      if (!stores.waitingAt(place)) continue;
      const p = propAt(place);
      // Not onto the marble in blacks: said under the objective instead (`withFront`).
      if (p && inFrontOfHouse(p) && !frontOk()) continue;
      if (p) return { point: { x: p.x, z: p.z }, half: { x: p.halfX ?? 0, z: p.halfZ ?? 0 }, room: null };
    }
  }
  if (venueOf(beos.active.beo) !== 'ballroom') return null;
  // A cloth of the wrong colour to take off: the nearest one.
  if (spec.linen) {
    let worst = null;
    for (const key of plannedKeys(spec) ?? []) {
      const cover = setup.tableAt(key)?.cover;
      if (!cover || cover.variant === spec.linen) continue;
      const a = setup.anchorAt(key);
      const d = a ? Math.hypot(a.x - player.position.x, a.z - player.position.z) : Infinity;
      if (a && (!worst || d < worst.d)) worst = { a, d };
    }
    if (worst) return { point: { x: worst.a.x, z: worst.a.z }, room: null };
  }
  const holding = hands();
  const wantKind = needs?.stage ?? null;
  // The room is as the sheet wants it: the job's own thing, if it has one left.
  if (!wantKind) return job ? { point: job.point, room: null } : null;
  const nearestOf = (keys) => {
    let best = null;
    for (const key of keys) {
      const a = setup.anchorAt(key);
      const d = a ? Math.hypot(a.x - player.position.x, a.z - player.position.z) : Infinity;
      if (a && (!best || d < best.d)) best = { a, d };
    }
    return best ? { point: { x: best.a.x, z: best.a.z }, room: null } : null;
  };
  // A ring is empty and a round stands off the plan: that round is the one
  // to move, and there may be none left on the shelf to fill it with.
  if (wantKind === 'round' && needs.offPlan.length && !(holding.kind === 'round' && holding.count > 0)) {
    const off = nearestOf(needs.offPlan);
    if (off) return off;
  }
  if (holding.count > 0 && OBJECTS[holding.kind]?.place) {
    const role = OBJECTS[holding.kind].place.role;
    const a = anchorFor(role === 'base' ? 'round' : role);
    if (a) return { point: { x: a.x, z: a.z }, room: null };
  }
  // The shelf is out of chairs and a round has more than it wants: pull one there.
  if (wantKind === 'chair' && stores.available('chair') <= 0 && !(holding.kind === 'chair' && holding.count > 0)) {
    const spare = [...(plannedKeys(spec) ?? [])].filter((key) => (setup.tableAt(key)?.seats ?? 0) > spec.chairsPerTable);
    const there = nearestOf(spare);
    if (there) return there;
  }
  // Some of it on the floor: picked up from there before the shelf when the
  // shelf is out of it, or when it is lying in the room itself.
  if (!(holding.kind === wantKind && holding.count > 0)) {
    const loose = looseOf(wantKind);
    const inRoom = loose && level.roomAt(loose.position.x, loose.position.z)?.id === venueOf(beos.active.beo);
    if (loose && (inRoom || stores.available(wantKind) <= 0)) return { point: { x: loose.position.x, z: loose.position.z }, room: null };
  }
  const parked = loadedCartWith(wantKind);
  if (parked) return { point: { x: parked.position.x, z: parked.position.z }, room: null };
  const shelf = propAt(SHELF_OF[wantKind]);
  return shelf ? { point: { x: shelf.x, z: shelf.z }, half: { x: shelf.halfX ?? 0, z: shelf.halfZ ?? 0 }, room: null } : null;
}

/**
 * What the task on screen is about: the people it names, the kinds of thing it
 * wants and the places (prop ids) it sends you to. Read ten times a second
 * with the route. Things it names stand out (name tags carry, an outline) and
 * things it does not keep quiet as you walk past (`quieted`).
 */
let taskFocus = { people: new Set(), kinds: new Set(), props: new Set(), anyone: false, carts: false };

function computeTaskFocus() {
  const people = new Set();
  const kinds = new Set();
  const props = new Set();
  let anyone = false;
  let carts = Boolean(wheeled.active) || Boolean(shuttles.active || blockades.active || deliveries.reading || rescues?.active || teardown?.active);
  const status = shift.status(clock);
  const objective = objectiveFor();
  if (objective?.where?.prop) props.add(objective.where.prop);
  if (lesson?.where?.prop) props.add(lesson.where.prop);
  if (lesson?.where?.anchor && SHELF_OF[lesson.where.anchor]) {
    props.add(SHELF_OF[lesson.where.anchor]);
    kinds.add(lesson.where.anchor);
  }
  const active = beos.active;
  const spec = active?.beo.setup;
  if (!active && tutorial.guiding) {
    for (const kind of ['round', 'chair', 'linen']) kinds.add(kind);
  }
  if (spec && !active.beo.teardown) {
    const needs = venueOf(active.beo) === 'ballroom' ? roomNeeds(setup, spec) : null;
    if (!needs || needs.missing.length || needs.offPlan.length) kinds.add('round');
    if (!needs || needs.missing.length || needs.fewChairs.length) kinds.add('chair');
    if (!needs || needs.missing.length || needs.bare.length || needs.wrongCloth.length) kinds.add('linen');
    for (const kind of kinds) if (SHELF_OF[kind]) props.add(SHELF_OF[kind]);
    if (loadedCartWith('round') || loadedCartWith('chair') || loadedCartWith('linen')) carts = true;
  }
  if (active?.beo.teardown) carts = true;
  if (active?.beo.job) {
    const row = JOBS.find((entry) => entry.id === active.beo.job);
    if (row?.before?.litter?.kind) kinds.add(row.before.litter.kind);
    if (row?.before?.bring?.kind) kinds.add(row.before.bring.kind);
  }
  for (const entry of active?.beo.shortfalls ?? []) {
    const row = SHORTFALLS[entry.id];
    if (!row) continue;
    if (row.item) kinds.add(row.item);
    if (!entry.resolved) {
      const holder = holderFor(row.owner);
      if (holder) people.add(holder.npc.data.id);
    } else {
      for (const place of Object.values(row.at ?? {})) if (stores.waitingAt(place)) props.add(place);
    }
  }
  if (!active && status.onTheClock) {
    props.add('beo-board');
    for (const up of jobsUp()) if (up.person) people.add(up.person.data.id);
  }
  for (const task of tasks.list) {
    if (task.expired) continue;
    if (task.staffId) people.add(task.staffId);
    if (task.target?.staffId) people.add(task.target.staffId);
  }
  for (const up of missionsUp()) if (up.person) people.add(up.person.data.id);
  // Looking for somebody: anybody might have seen them.
  if (searches?.active) anyone = true;
  if (conversation?.talkingTo) people.add(conversation.talkingTo.data.id);
  return { people, kinds, props, anyone, carts };
}

/** What the line goes to: the objective's place, or failing that a mission's. */
function routeTarget() {
  // The lesson on screen says where to go; the floor guide goes there.
  const taught = lessonPlace();
  if (taught) return taught;
  const step = nextStepPlace();
  if (step) return step;
  // No room in hand: an errand still has somewhere to be.
  const errand = shift.status(clock).onTheClock ? errandForTip() : null;
  if (errand) return { point: errand.at, room: null };
  const objective = objectiveFor();
  const point = placeOf(objective?.where);
  const prop = objective?.where?.prop ? propAt(objective.where.prop) : null;
  if (point) return { point, half: prop ? { x: prop.halfX ?? 0, z: prop.halfZ ?? 0 } : null, room: objective.where.room ?? null };
  const main = mapTargets().find((t) => t.main);
  return main ? { point: { x: main.x, z: main.z }, room: null } : null;
}

/*
 * The guide while you push a cart: the same floor, read with a cart's width
 * kept off every wall instead of a body's, over the building only. The person
 * line ran a table cart into a three-foot service door it could never pass,
 * and a new starter pushed at the jamb until the room's deadline went by
 * (`fullgame.mjs`); this one takes the main run, like everybody with a cart.
 */
const cartRouter = createRouter({
  world: level.world,
  bounds: { minX: ft(-64), minZ: ft(-56), maxX: ft(198), maxZ: ft(104) },
  cell: 0.25,
  margin: 0.62,
});

function runRoute(dt) {
  const shut = level.closedBarriers.join('|');
  if (shut !== routeBarriers) {
    routeBarriers = shut;
    router.invalidate();
    cartRouter.invalidate();
    // Breaks walk the building as it stands, chains and all, whatever it was
    // when their grid was first read.
    breakRouter.invalidate();
  }
  router.pump(ROUTE_BUDGET);
  const pushing = wheeled.active?.state === 'pushed';
  if (pushing) cartRouter.pump(ROUTE_BUDGET);
  const guide = pushing ? cartRouter : router;
  if (pressed('route') && !actionKey.fanning && !modalOpen() && !readingSheet) {
    const on = !settings.get('routeLine');
    settings.set('routeLine', on);
    hud.say(on ? 'Route line on. [V] to hide it.' : 'Route line off. [V] to show it.', { tone: 'info', seconds: 2 });
  }
  routeSince += dt;
  if (routeSince >= 0.1) {
    routeSince = 0;
    coverUnmanned();
    routeGoal = routeTarget();
    taskFocus = computeTaskFocus();
    updateFrontCover();
    router.target(routeGoal?.point ?? null, { half: routeGoal?.half ?? null });
    if (pushing) cartRouter.target(routeGoal?.point ?? null, { half: routeGoal?.half ?? null });
    const here = level.roomAt(player.position.x, player.position.z);
    // A lesson's target is a thing to stand at, so its line runs right up to it.
    // A person is stood beside, not on: a talking distance for them.
    const close = lesson?.where ? (lesson.where.point ? ft(6) : ft(3.5)) : ARRIVED;
    const arrived =
      !routeGoal ||
      (routeGoal.room && here?.id === routeGoal.room) ||
      Math.hypot(routeGoal.point.x - player.position.x, routeGoal.point.z - player.position.z) < close;
    routeGoal = routeGoal && { ...routeGoal, arrived };
    // A cart's line, while its grid is still being read, is the person's.
    routePath = arrived ? null : guide.path(player.position) ?? (guide === cartRouter ? router.path(player.position) : null);
  }
  const shown = !hud.root.hidden && !modalOpen();
  routeView.set(shown && (settings.get('routeLine') || helpCard.open) && routePath ? routePath : null);
  routeView.update(dt);
  runHighlights(dt);
  hud.setMarker(shown ? markerFor(routeGoal) : null);
}

/** Feet, said the way a person would round them. */
function sayFeet(metres) {
  const feet = toFeet(metres);
  if (feet < 100) return `${Math.max(1, Math.round(feet))} ft`;
  if (feet < 1000) return `${Math.round(feet / 10) * 10} ft`;
  return `${Math.round(feet / 100) * 100} ft`;
}

/** The objective's marker on the screen, over it or pinned to the edge. */
function markerFor(goal) {
  if (!goal || goal.arrived) return null;
  const metres = routePath ? pathLength(routePath) : Math.hypot(goal.point.x - player.position.x, goal.point.z - player.position.z);
  const floor = routePath?.[routePath.length - 1]?.y ?? 0;
  const at = screenOf({ x: goal.point.x, y: floor + 1.6, z: goal.point.z });
  const distance = sayFeet(metres);
  const margin = 46;
  if (at.inside && at.x > margin && at.x < at.width - margin && at.y > margin && at.y < at.height - margin) {
    // The prompt is on it already: one thing on the screen saying where, not two.
    if (promptPoint && Math.hypot(promptPoint.x - at.x, promptPoint.y - at.y) < 110) return null;
    return { x: at.x, y: at.y, edge: false, angle: 0, distance };
  }
  // Off the screen: toward it from the middle, flipped when it is behind.
  let dx = at.ndc.x;
  let dy = -at.ndc.y;
  if (at.behind) {
    dx = -dx;
    dy = -dy;
    if (Math.abs(dx) < 0.2) dy = Math.abs(dy) + 1;
  }
  const length = Math.hypot(dx, dy) || 1;
  dx /= length;
  dy /= length;
  const halfW = at.width / 2 - margin;
  const halfH = at.height / 2 - margin;
  const t = Math.min(Math.abs(dx) > 1e-6 ? halfW / Math.abs(dx) : Infinity, Math.abs(dy) > 1e-6 ? halfH / Math.abs(dy) : Infinity);
  // Pinned clear of the clock, the lesson and the map along the top, and of the
  // prompt along the bottom.
  const y = Math.min(at.height - 110, Math.max(250, at.height / 2 + dy * t));
  const x = Math.min(at.width - 46, Math.max(46, at.width / 2 + dx * t));
  return { x, y, edge: true, angle: Math.atan2(dy, dx), distance };
}

let mapSince = Infinity;
let mapReading = null;
function runMinimap(dt) {
  const visible = !hud.root.hidden && !modalOpen();
  mapSince += dt;
  if (!visible || mapSince < 0.1) return;
  mapSince = 0;
  const here = level.roomAt(player.position.x, player.position.z);
  mapReading = {
    // The way the body faces, which is the way it walks: not where the camera
    // looks. The map stays north-up; only the arrow turns.
    player: { x: player.position.x, z: player.position.z, yaw: player.yaw },
    room: here?.id ?? null,
    roomName: here?.name ?? null,
    outside: !here?.ceiling,
    people: perceivedPeople(),
    targets: mapTargets(),
    route: settings.get('routeLine') ? routePath : null,
  };
  minimap.draw(mapReading);
}

function finishFrame(dt) {
  const size = { width: window.innerWidth, height: window.innerHeight };
  for (const view of npcViews) view.sync(rig.camera, size, dt);
  panel.update({
    position: player.position,
    room: level.roomAt(player.position.x, player.position.z)?.name ?? null,
    zones: level.zonesAt(player.position.x, player.position.z).map((zone) => zone.kind),
    minutes: clock.minutes,
    scale: clock.scale,
    fps: dt > 0 ? 1 / dt : 0,
  });
  // A modal can open part-way through a frame — submitting the room does
  // exactly that — and the rest of the frame has already put the HUD back on
  // screen behind it. Last word wins.
  if (modalOpen()) {
    hud.setVisible(false);
    for (const view of npcViews) view.setLabelVisible(false);
  }
  runTutorial(dt);
  runRoute(dt);
  runClassNotices();
  runPins();
  runMinimap(dt);

  if (rendering) draw(dt);
  // Drained at the very end, so a scripted press queued between frames is not
  // thrown away before the frame that was meant to read it.
  keys.endFrame();
}

const resumed = boot();
if (resumed) {
  hud.say(`Day ${clock.day}. Picking up where you left off.`, { tone: 'info', seconds: 4 });
}

/*
 * The gesture the browser insists on.
 *
 * An `AudioContext` built before the player has touched anything starts
 * suspended and stays that way, so nothing here is created until the first key
 * or click. Left attached for the life of the page rather than removed after
 * the first hit: a tab that is backgrounded and comes back has a suspended
 * context again, and `unlock` on a running one costs nothing.
 */
for (const gesture of ['keydown', 'pointerdown']) {
  window.addEventListener(gesture, () => audio.unlock(), { passive: true });
}

/**
 * A frame with the title or the pause menu up: nothing in the world moves.
 *
 * The input is still read, because the pad has to get round the menu, and the
 * scene is still drawn, because the building is the backdrop. Headless runs
 * drive `step` themselves and never come through here.
 */
function menuFrame(dt) {
  input.padLive = false;
  input.update();
  padNav.step(menus.root, input.pad, { onBack: () => menus.back() });
  if (menus.screen === 'pause' && input.pad.pause()) resume();
  playScore();
  if (relabelDue()) relabel(ui);
  hud.setVisible(false);
  if (rendering) draw(dt);
}

const loop = createLoop((dt) => (menus.open ? menuFrame(dt) : step(dt)));
loop.start();
// The title, over whatever the save left: Continue, New Game, Settings.
menus.showTitle();

// Handle for the debug panel and for headless smoke tests.
window.__LIONCROWN__ = {
  /** Breaks and colleagues talking (`sim/breaks.js`, `sim/crewTalk.js`). */
  breaks,
  breakRouter,
  crewTalk,
  jobs,
  acceptCard,
  stage,
  /** The look of the building, for the headless check. Null under `?look=0`. */
  look,
  rig,
  /** The fader and the soundboard, for the headless audio check. */
  audio,
  sound,
  /** What the audio layer is handed each frame. Read-only, and it is a copy. */
  soundReading,
  loop,
  step,
  fader,
  /** One frame of the title or pause menu, for the pad check. */
  menuFrame,
  menus,
  settings,
  actionKey,
  player,
  input,
  level,
  avatar,
  veil,
  creation,
  identity,
  clothing,
  hud,
  clock,
  shift,
  interaction,
  standing,
  beos,
  stores,
  tasks,
  favors,
  procurement,
  ghosts,
  journal,
  chapters,
  chapterPanel,
  /** The acts, the visitor, the finale and the ending. See `sim/story.js`. */
  story,
  choicePanel,
  /** Weighs the finale as the deadline or the filing would. Headless only. */
  settleFinale,
  /** The classes, the perks they buy, and the screen you sit one on. */
  classes,
  perks,
  /** The nuisance drawer, and where each of them is stocked. */
  pranks,
  offerPranks,
  propAt,
  /**
   * Exactly what the action key is looking at this frame.
   *
   * The same object `runAction` reads, so a script can see what the player
   * sees: what a tap would do, what a hold would offer, and in what order. It
   * was held for the debug handle already and simply had no way out.
   */
  get offered() {
    return onOffer;
  },
  classPanel,
  classBoard,
  sitClass,
  summaryPanel,
  showSummary,
  carry,
  wheeled,
  climbing,
  perception,
  suspicion,
  noticed,
  concealment,
  impairment,
  chatter,
  claims,
  access,
  wildlife,
  bus,
  entities,
  throwing,
  cartView,
  setup,
  writeups,
  /** The card as a page, so a script can read what the player reads. */
  cardReadout,
  /** Serves one, for a script that would rather not collect three by hand. */
  suspend,
  save,
  autosave,
  snapshot,
  restore,
  newGame,
  hardReset,
  npcs,
  npcViews,
  dialogue,
  sheet,
  writeUp,
  setupView,
  gradePanel,
  fileSetup,
  cycleLinen,
  panel,
  beoPanel,
  openBoard,
  closeBoard,
  scripted,
  /** Queues an action press for the next frame, as if a key went down. */
  press: (action) => keys.queue(action),
  /** A cart by id, for the headless scripts. Defaults to the banquet one. */
  cartAt: (id = 'cart-banquet') => {
    const cart = wheeled.byId(id);
    return cart ? { x: cart.position.x, z: cart.position.z, state: cart.state } : null;
  },
  /** Puts the hand-written benchmark event on today's board. */
  postReference: () => beos.inject(REFERENCE_BEO),
  /**
   * Grades a scratch room, off to one side of the real one.
   *
   * Headless only, and it exists so a check can ask what the sheet makes of a
   * *fixed* mess without standing one in the ballroom and without touching the
   * event the player is running. Two rounds parked where the diagram did not
   * ask for them, everything else correct, graded through `gradeSetup` exactly
   * as a real room is. What comes back is the tidiness mark, the other five,
   * and the score, which is enough to say whether a perk moved one mark or all
   * of them.
   */
  gradeScratch: () => {
    const spec = REFERENCE_BEO.setup;
    const scratch = createSetup({ grid: LEVEL.ballroom });
    const wanted = [...wantedAnchors(spec)];
    const dress = (anchorKey) => {
      scratch.place(anchorKey, { role: 'base', kind: 'round' });
      for (let seat = 0; seat < spec.chairsPerTable; seat += 1) {
        scratch.place(anchorKey, { role: 'seat', kind: 'chair' });
      }
      scratch.place(anchorKey, { role: 'cover', kind: 'linen', variant: spec.linen });
    };
    for (const anchorKey of wanted.slice(0, spec.tableCount)) dress(anchorKey);
    /*
     * The mess: two rounds standing somewhere the sheet never mentioned.
     *
     * Taken from the far end of the grid rather than written down as keys, so
     * this keeps working if the ballroom's anchor grid is ever renumbered, and
     * so they are certain not to be anchors the diagram already wanted.
     */
    const strays = scratch.anchors
      .map((anchor) => anchor.key)
      .filter((anchorKey) => !wanted.includes(anchorKey) && !scratch.anchorAt(anchorKey)?.keepClear)
      .slice(-2);
    for (const anchorKey of strays) dress(anchorKey);
    const result = gradeSetup({
      spec,
      tables: scratch.tables,
      anchorAt: scratch.anchorAt,
      minutesLate: 0,
      shortfalls: [],
      tidyForgiveness: perks.tidy.forgiveness,
    });
    const { tidy, ...others } = result.parts;
    return { tidy, others, score: result.score, letter: result.letter, strays: strays.length };
  },
  /**
   * Stands the whole room the sheet asked for, in one call.
   *
   * Headless only, and it exists for the readouts rather than for the game: a
   * check that wants to know what the screen says once the room is set should
   * not have to walk ninety trips to find out. Nothing in the game calls it and
   * it grades exactly as a room set by hand would, because it goes through
   * `setup.place` like everything else.
   */
  setRoom: () => {
    const active = beos.active;
    if (!active) return { ok: false, reason: 'nothing accepted' };
    const spec = active.beo.setup;
    let stood = 0;
    for (const key of wantedAnchors(spec)) {
      if (stood >= spec.tableCount) break;
      if (!setup.place(key, { role: 'base', kind: 'round' }).ok) continue;
      for (let i = 0; i < spec.chairsPerTable; i += 1) {
        setup.place(key, { role: 'seat', kind: 'chair' });
      }
      // The colour the sheet asked for. Any other one grades as a bare table.
      setup.place(key, { role: 'cover', kind: 'linen', variant: spec.linen });
      stood += 1;
    }
    applyShortfalls();
    return { ok: true, set: setup.counts };
  },
  /** Which of the two is currently to hand. The pocket cycles with [3]. */
  pocket: () => pocket.current,
  /** Everything in it, which for a new starter is nothing until somebody gives them something. */
  pocketContents: () => pocket.contents,
  /** The first afternoon's state, or null for any week that did not start at the hiring screen. */
  opening: () => (theOpening.state ? { ...theOpening.state } : null),
  /**
   * Empties a stretch of the property of staff, for a measurement.
   *
   * The only instrument of its kind in the game and it exists because of one
   * honest question: how long is a corridor run with nobody in the corridor?
   * There is no hour at which the Banquet Manager is not walking up and down
   * it, so the answer cannot be obtained by waiting. Passing no rect puts
   * everybody back.
   */
  holdStaffBack: (rect) => {
    const held = [];
    for (const npc of npcs) {
      if (!rect) {
        npc.holdBack(false);
        continue;
      }
      const inside = npc.route.some(
        (point) =>
          point.x >= ft(rect[0]) && point.x <= ft(rect[2]) &&
          point.z >= ft(rect[1]) && point.z <= ft(rect[3])
      );
      if (inside) {
        npc.holdBack(true);
        held.push(npc.data.id);
      }
    }
    return held;
  },
  /**
   * What the key would do here, and everything holding it would offer.
   *
   * The same object the screen is drawn from, so a headless check that the
   * prompt matches the action is comparing the two things the player compares
   * rather than two parallel descriptions of them.
   */
  offered: () => ({
    top: onOffer.top
      ? { id: onOffer.top.id, says: line(onOffer.top, { key: 'E' }), blocked: onOffer.top.blocked ?? null }
      : null,
    rest: onOffer.rest.map((option) => ({
      id: option.id,
      says: line(option, { key: 'E' }),
      blocked: option.blocked ?? null,
    })),
  }),
  /**
   * The menu as it is actually drawn, row by row, with the key on each row.
   *
   * Read off the same call the HUD makes, so a check cannot pass against a list
   * the screen is not showing.
   */
  /** The lesson or tip on screen, and where it points, as the player is shown it. */
  lesson: () => (lesson ? { ...lesson, at: lessonPlace()?.point ?? null, ring: lessonAnchor ? { x: lessonAnchor.x, z: lessonAnchor.z } : null } : null),
  tutorial,
  /** The minimap's last reading. */
  get mapReading() {
    return mapReading;
  },
  /** The way to the objective, as the player is shown it. */
  /** What is outlined, and what the task on screen is about (`taskFocus`). */
  highlights: () => ({ count: highlightView.count, focus: { people: [...taskFocus.people], kinds: [...taskFocus.kinds], props: [...taskFocus.props], anyone: taskFocus.anyone, carts: taskFocus.carts } }),
  route: () => ({
    ready: router.ready,
    path: routePath ? routePath.map((p) => ({ x: p.x, y: p.y, z: p.z })) : null,
    goal: routeGoal,
    on: settings.get('routeLine'),
    drawn: routeView.drawn,
    end: routeView.end,
    length: routePath ? pathLength(routePath) : null,
  }),
  /** Settles the route at once, for a script that teleports: every cell, now. */
  settleRoute: () => {
    for (let i = 0; i < 400 && !router.ready; i += 1) router.pump(50000);
    routeSince = Infinity;
    return router.ready;
  },
  /** What the snap would use here, for a check. */
  snapping: () => (snapping ? { key: snapping.anchor?.key ?? null, why: snapping.why ?? null } : null),
  fan: () => (actionKey.fanning ? { rows: fanRows([onOffer.top, ...onOffer.rest].filter(Boolean)), at: actionKey.at } : null),
  /** Presses the number on a row, exactly as the keyboard does. */
  fanPress: (key) => {
    const index = fanRows([onOffer.top, ...onOffer.rest].filter(Boolean)).findIndex(
      (row) => row.key === String(key)
    );
    if (!actionKey.fanning || index === -1) return { ok: false, reason: 'no such row' };
    actionKey.pick(index);
    return { ok: true, key: String(key) };
  },
  /**
   * Chooses one of them by id, which is what holding the key and releasing
   * over it does. Refuses anything not on offer here, because the point of the
   * fan is that it is everything available at this spot and nothing else.
   */
  choose: (id) => {
    const found = onOffer.all.find((option) => option.id === id);
    if (!found) return { ok: false, reason: 'not on offer here' };
    return perform(found);
  },
  /**
   * Every fixed thing in the building, with somewhere to stand to use it.
   *
   * Read off the interaction system's own targets rather than written down, so
   * a prop added next week is covered by the controls check without anybody
   * remembering to add it.
   */
  interactables: () =>
    interaction.targets.map((target) => {
      const at = target.centre;
      // Stand a couple of feet off it, facing it, which is what a person does.
      const from = { x: toFeet(at.x), z: toFeet(at.z) - 3 };
      return {
        id: target.id,
        label: target.label,
        kind: target.kind,
        at: from,
        face: { x: toFeet(at.x), z: toFeet(at.z) },
      };
    }),
  /** Presses the action key once, and says which candidate it ran. */
  pressAction: () => {
    const top = onOffer.top;
    if (!top) return null;
    const done = perform(top);
    return { id: top.id, ok: done.ok, reason: done.reason ?? null };
  },
  /** The line the objective block shows about filing, or null. */
  objectiveLine: () => objectiveFor()?.submit ?? null,
  /** Whether a named candidate is on offer here at all. */
  canChoose: (id) => onOffer.all.some((option) => option.id === id),
  /** A person by id, for a script that got the id from `gate` rather than typed it. */
  staffById: (id) => STAFF_BY_ID[id] ?? null,
  /**
   * The section, as data, so a readout never types one of their names.
   *
   * Everything a script needs to tell thirteen people apart without knowing
   * which thirteen they are: who they are, when they are in, where they stand,
   * and what they do about what they see.
   */
  crew: () =>
    CREW.map((person) => ({
      id: person.id,
      name: person.name,
      department: person.department,
      reacts: reactionOf(person),
      after: person.after ?? null,
      window: { ...person.window },
      days: person.days ? [...person.days] : null,
      drifts: person.drifts ? { ...person.drifts } : null,
      post: { x: person.route[0][0], z: person.route[0][1] },
      stops: person.route.length,
      moves: person.route.length > 1,
      room: level.roomAt(ft(person.route[0][0]), ft(person.route[0][1]))?.name ?? null,
      sight: person.vision?.range ?? 0,
      /** Proof there is nothing here for a negotiation to read. */
      price: person.price ?? null,
    })),
  /**
   * Everybody with a voice, so a readout never types one of their names.
   *
   * Twenty of them, and the `ledger` field is the whole reason this is a list
   * rather than two: a colleague is read off what they personally think of you
   * and a gate off what their department does, and a check has to know which
   * number to move before it can ask what they would say.
   */
  voices: () =>
    STAFF.filter((person) => person.gate || person.crew).map((person) => ({
      id: person.id,
      name: person.name,
      department: person.department,
      gate: Boolean(person.gate),
      ledger: person.crew ? 'regard' : 'standing',
    })),
  /** Everybody a negotiation can be had with, for a check that walks them all. */
  staffWithPrices: () =>
    STAFF.filter((person) => person.price && !person.enforces).map((person) => ({
      id: person.id,
      department: person.department,
    })),
  /** Where one person's trade would send you, on a given draw. */
  errandSpot: (staffId, draw) => {
    const person = STAFF_BY_ID[staffId];
    if (!person) return null;
    return errandFor(person, createRng(hashSeed('errand-probe', staffId, draw)));
  },
  /** What is over somebody's head right now, and whether they are moving. */
  showing: (id) => {
    const npc = npcs.find((candidate) => candidate.data.id === id);
    if (!npc) return null;
    return {
      note: npc.note,
      searching: npc.searching,
      holding: npc.holding,
      at: { x: toFeet(npc.position.x), z: toFeet(npc.position.z) },
      yaw: npc.yaw,
      /** How square-on to the player they are: 1 facing, -1 turned away. */
      facing: facingOf(npc),
    };
  },
  /** The one line under the clock, as the screen has it. */
  objective: () => objectiveFor(),
  /** What the minimap last drew, for a check: who, and where it pointed. */
  controlsCard,
  minimap: () => mapReading && { ...mapReading, visible: !hud.root.hidden && !modalOpen() },
  /** Every tell there is, so a readout never types one of them. */
  tells: () => Object.fromEntries(Object.entries(TELLS).map(([shape, tell]) => [shape, tell.say])),
  /**
   * What this person would say to you right now, off whichever ladder they are
   * read on. The same call the box makes, so a check cannot pass against a
   * table the game is not using.
   */
  greeting: (id) => {
    const npc = npcs.find((candidate) => candidate.data.id === id);
    return npc ? greetingFor(npc) : null;
  },
  /** Has a word with somebody, exactly as pressing X does. */
  charm: (id) => charming.headless(id),
  /** Clears the charm cooldown, so a readout can ask more than one question. */
  uncharm: () => charming.uncharm(),
  /** Moves one person's own opinion of you. Headless only. */
  think: (id, delta, reason) => think(id, delta, reason ?? 'headless'),
  /** Whether somebody is on the floor right now, and why not if they are not. */
  onDutyNow: (id) => {
    const person = STAFF_BY_ID[id];
    if (!person) return null;
    return {
      onDuty: onDutyAt(person, clock),
      inWindow: windowCovers(person.window, clock.minutes),
      today: !person.days || person.days.includes(weekdayOf(clock.day)),
    };
  },
  /** The one person who answers for a department, so a script never names one. */
  gate: (department) => {
    const person = gateFor(department);
    return person ? { id: person.id, department: person.department, gates: person.gates ?? null } : null;
  },
  /** What the menu would say to you right now, without opening it. */
  dealOptions: (staffId, shortfallId) => {
    const person = STAFF_BY_ID[staffId];
    const entry = (beos.active?.beo.shortfalls ?? []).find((row) => row.id === shortfallId)
      ?? { id: shortfallId, amount: 1, resolved: false };
    return optionsFor(person, entry, { standing, strikes: writeups.count }).map((option) => ({
      kind: option.kind,
      allowed: option.allowed,
      reportsYou: Boolean(option.reportsYou),
      minutes: option.minutes ?? null,
    }));
  },
  /** Whether the supervisor is still carrying you. */
  supervisor: () => ({ stopped: escalation.supervisorStopped, rung: rungFor(seenValue('banquets')).id }),
  /** Helpers the headless scripts need so they never hardcode a staff id. */
  shortfallOwner: (id) => SHORTFALLS[id]?.owner ?? null,
  shortfallKind: (id) => SHORTFALLS[id]?.kind ?? null,
  /**
   * Where a shortfall's thing is right now, rung and all, so a script never
   * hardcodes a prop and never has to know the ladder.
   */
  shortfallPlace: (id, rung) =>
    placeFor(SHORTFALLS[id], rung ?? rungFor(seenValue(SHORTFALLS[id]?.owner)).id),
  /** Which rung you are on with whoever is holding it, your card included. */
  shortfallRung: (id) => rungFor(seenValue(SHORTFALLS[id]?.owner)).id,
  /** What a place is called, so a script can check the sheet against the world. */
  placeName: (place) => PLACES[place]?.spot ?? null,
  /** And what a shortfall is called, so a script can find its row on the sheet. */
  shortfallLabel: (id) => SHORTFALLS[id]?.label ?? null,
  /** Every shortfall there is, so a script never types one of their names. */
  shortfallIds: () => Object.keys(SHORTFALLS),
  /**
   * Has the department let go of it, exactly as a negotiation would.
   *
   * The same call the dialogue makes when you ask, trade or take, so a script
   * that wants to measure the fetch does not get to skip the part where the
   * rung decides where it lands. What rung that is comes from standing, which
   * the script has to move first, like everybody else.
   */
  releaseShortfall: (entry) => releaseFor(entry),
  gatekeepersOnDuty: (department) =>
    gatekeepersFor(department).filter((person) => windowCovers(person.window, clock.minutes)),
  /** Which rule-bearing zones a member of staff stands in. */
  postZone: (staffId) => {
    const person = STAFF_BY_ID[staffId];
    if (!person) return [];
    const [x, z] = person.route[0];
    return level.zonesAt(ft(x), ft(z)).map((zone) => zone.kind);
  },
  /** Everyone in a department, with whether they are in and when they next are. */
  holdersFor: (department) =>
    gatekeepersFor(department).map((person) => {
      const opens = parseTime(person.window.from);
      const wait = ((opens - clock.minutes) % 1440 + 1440) % 1440;
      return {
        id: person.id,
        present: windowCovers(person.window, clock.minutes),
        opensIn: windowCovers(person.window, clock.minutes) ? 0 : wait,
      };
    }),
  /** The board as data, so a script can choose an event the way a player would. */
  boardCards: () =>
    beos.board(clock).map((card) => ({
      code: card.beo.code,
      id: card.beo.id,
      tables: card.beo.setup?.tableCount ?? 0,
      chairs: (card.beo.setup?.tableCount ?? 0) * (card.beo.setup?.chairsPerTable ?? 0),
      estimate: card.beo.estimate ?? 0,
      remaining: Math.round(card.remaining),
      margin: Math.round(card.remaining - (card.beo.estimate ?? 0)),
      state: card.state,
      shortfalls: (card.beo.shortfalls ?? []).map((entry) => entry.id),
      /** Which lawn, for a strike. A room has none. */
      teardown: card.beo.teardown ?? null,
      /** Up on the board yet: a daily job posts partway into the shift. */
      live: card.live,
      job: card.beo.job ?? null,
    })),
  /** The animal that is one animal: where it is and what it remembers. */
  individuals: () => individuals.all,
  /** Somebody stranded: the rescue on now, and how the others went. */
  rescues: () => ({ active: rescues.active, records: rescues.records }),
  shuttles: () => ({ active: shuttles.active, records: shuttles.records, destination: shuttles.destination }),
  blockades: () => ({ active: blockades.active, records: blockades.records, where: blockades.where }),
  deliveries: () => ({ active: deliveries.active, records: deliveries.records, reading: deliveries.reading }),
  poolSets: () => ({ posted: poolSets.posted }),
  /** Who is being looked for, and who has seen them. */
  searches: () => ({
    active: searches.active,
    records: searches.records,
    sightings: Object.fromEntries(searches.rows.map((row) => [row.id, searches.sightings(row.id)])),
  }),
  /** Dinners: tonight's, every one so far, and which somebody wants over. */
  dinners: () => ({ running: dinners.running(), records: dinners.records, wanted: dinners.wanted }),
  /**
   * The strike you are on, as numbers: what is left on the lawn, what is on
   * the carts, what reached the dock and what went over. Null when it is not a
   * strike you are on.
   */
  teardown: () =>
    teardown.active
      ? {
          ...teardown.counts(),
          carts: teardown.active.row.carts.map((id) => {
            const cart = wheeled.byId(id);
            return {
              id,
              kind: cart.kind,
              x: toFeet(cart.position.x),
              z: toFeet(cart.position.z),
              yaw: cart.yaw,
              state: cart.state,
              tipped: cart.tipped,
              count: cart.hold.count,
              capacity: cart.hold.kind ? cart.hold.capacity : 0,
              room: level.roomAt(cart.position.x, cart.position.z)?.id ?? null,
            };
          }),
          crew: npcs
            .filter((npc) => npc.data.teardown)
            .map((npc) => ({
              id: npc.data.id,
              present: npc.present,
              posted: Boolean(npc.posted),
              room: level.roomAt(npc.position.x, npc.position.z)?.id ?? null,
            })),
        }
      : null,
  stationFor: (department) => {
    const station = interaction.targets.find(
      (target) => target.kind === 'station' && target.department === department
    );
    if (!station) return null;
    return { x: toFeet(station.centre.x), z: toFeet(station.centre.z), id: station.id };
  },
  get seed() {
    return runSeed;
  },
  /** Headless only: stop drawing, so a whole shift can be simulated quickly. */
  setRendering(on) {
    rendering = on;
    if (on) draw(0);
  },
  /** Held input for a headless test, bypassing pointer lock. */
  use: (id) => {
    const target = interaction.targets.find((candidate) => candidate.id === id);
    if (target) use(target, shift.status(clock));
  },
  report: () => ({
    position: { x: player.position.x, y: player.position.y, z: player.position.z },
    yaw: player.yaw,
    room: level.roomAt(player.position.x, player.position.z)?.id ?? null,
    clock: clock.toJSON(),
    time: formatTime(clock.minutes),
    shift: shift.status(clock),
    prompt: interaction.current?.id ?? null,
    standing: standing.toJSON(),
    /**
     * What each colleague thinks of you, and what that is doing.
     *
     * `bar` is the only thing the simulation reads it as: how much further this
     * person has to be pushed before they act. Kept beside the number so a
     * script can check that a step of the ladder is worth something rather than
     * being a figure on a panel.
     */
    regard: Object.fromEntries(
      regard.ids.map((id) => [
        id,
        {
          value: Math.round(regard.get(id) * 10) / 10,
          rung: rungFor(regard.get(id)).id,
          bar: Math.round(barFor(id) * 1000) / 1000,
          patience: patienceShift(id),
        },
      ])
    ),
    /** The same seven numbers read as steps of the ladder, which is what they do. */
    standingRungs: Object.fromEntries(
      standing.ids.map((id) => [id, rungFor(standing.get(id)).id])
    ),
    beo: beos.active && {
      id: beos.active.beo.id,
      remaining: beos.remaining(clock),
    },
    set: setup.counts,
    stock: stores.stock ? { ...stores.stock } : null,
    /** What a resolved shortfall has left somewhere, and where. */
    waiting: stores.waiting,
    shortfalls: (beos.active?.beo.shortfalls ?? []).map((entry) => ({ ...entry })),
    cartLocked,
    barriers: level.closedBarriers,
    tasks: tasks.list.map((task) => ({ id: task.id, source: task.source, label: task.label })),
    favors: favors.list.map((favor) => ({
      id: favor.id,
      staffId: favor.staffId,
      department: favor.department,
      state: favor.state,
      callDay: favor.callDay,
      callMinute: favor.callMinute,
    })),
    staffPresent: npcs.filter((npc) => npc.present).map((npc) => npc.data.id),
    ghosts: ghosts.forDay(clock.day).map((entry) => ({
      code: entry.code,
      owner: entry.owner,
      letter: entry.letter,
    })),
    chapter: chapters.pending?.id ?? null,
    act: story.act,
    summaryShown: summaryShownForDay === clock.day,
    history: beos.history.map((record) => ({
      code: record.code,
      day: record.day,
      ghost: Boolean(record.ghost),
      grade: record.grade,
    })),
    writeups: writeups.count,
    strikes: writeups.entries.map((entry) => entry.reason),
    /**
     * The card, and what it is doing to you.
     *
     * `penalty` and `asRead` are the two halves that make the escalation
     * checkable from outside: what each department's gate takes off you for the
     * write-ups against your name, and the step they therefore have you on.
     * `standing` above stays the ledger, untouched, which is the distinction
     * the whole design rests on.
     */
    record: {
      strikes: writeups.count,
      max: writeups.max,
      served: writeups.served,
      next: writeups.nextSuspension(),
      warningDue: escalation.warningDue,
      dismissed: escalation.dismissed,
      entries: writeups.entries.map((entry) => ({
        reason: entry.reason,
        detail: entry.detail ?? '',
        day: entry.day,
        clears: entry.clears,
      })),
      penalty: Object.fromEntries(standing.ids.map((id) => [id, recordPenalty(id)])),
    },
    asRead: Object.fromEntries(standing.ids.map((id) => [id, seenValue(id)])),
    asReadRungs: Object.fromEntries(
      standing.ids.map((id) => [id, rungFor(seenValue(id)).id])
    ),
    hands: carry.toJSON(),
    loose: entities.all.filter((e) => e.state === 'world').length,
    flying: throwing.flying.length,
    cart: wheeled.active
      ? {
          id: wheeled.active.id,
          state: wheeled.active.state,
          pushing: wheeled.active.state === 'pushed',
          hold: wheeled.active.hold.toJSON(),
          x: wheeled.active.position.x,
          z: wheeled.active.position.z,
        }
      : { id: null, state: 'parked', pushing: false, hold: { kind: null, count: 0, variant: null } },
    carts: wheeled.all.map((cart) => ({
      id: cart.id,
      state: cart.state,
      tipped: cart.tipped,
      held: cart.hold.count,
    })),
    linenChoice,
    /** Who you are and what you have got on, for the scripts and the panel. */
    who: {
      name: identity.name,
      body: identity.bodyId,
      created: identity.created,
      hiring: creation.open,
    },
    clothing: clothing.state,
    inUniform: clothing.isUniform,
    canClockIn: shift.status(clock).canClockIn && clothing.canClockIn,
    // Who believes what about you, strongest first. The only place anything
    // is allowed to ask whether you got away with it.
    witnesses: perception.witnesses.map((record) => ({
      by: record.by,
      department: record.department,
      what: record.what,
      detail: record.detail,
      mode: record.mode,
      confidence: Math.round(record.confidence * 1000) / 1000,
    })),
    watchers: perception.watchers,
    /*
     * The after-action record: everything anybody noticed this run and what
     * became of it, in the order the summary reads it. Outlives the witnesses,
     * which is the point, so a script can ask about a shift after everybody in
     * the building has stopped thinking about it.
     */
    noticed: noticed.entries.map((entry) => ({
      by: entry.by,
      who: entry.who,
      department: entry.department,
      what: entry.what,
      detail: entry.detail,
      mode: entry.mode,
      peak: Math.round(entry.peak * 1000) / 1000,
      worth: Math.round(entry.worth * 1000) / 1000,
      stirred: entry.stirred,
      outcome: entry.outcome,
      reason: entry.reason,
      day: entry.day,
    })),
    /** Today's, in the order the summary puts them on screen. */
    noticedToday: noticed.forDay(clock.day),
    sightingsToday: sightingsForDay(clock.day),
    chased: chased(),
    concealed: concealment.hidden ? concealment.hidden.label : null,
    wildlife: {
      about: wildlife.creatures.map((creature) => ({
        what: creature.what,
        state: creature.state,
        x: creature.position.x,
        z: creature.position.z,
      })),
      sightings: wildlife.sightings,
    },
    /*
     * What the placement resolver would do from here, right now.
     *
     * The same question the prompt asks, exposed so a script can walk until the
     * game says it can place rather than until it reaches an arbitrary distance
     * somebody typed into the harness. Without it, a rule that lets you set a
     * round from further away saves the player a step and a half and saves the
     * benchmark nothing at all, because the benchmark was never using the reach
     * it already had.
     */
    place: (() => {
      const holding = hands();
      const resolved = resolvePlacement({
        setup,
        carry: holding,
        position: player.position,
        spec: beos.active?.beo.setup ?? null,
        tolerance: impairment.tolerance,
      });
      return {
        action: resolved.action,
        key: resolved.anchor?.key ?? null,
        bad: Boolean(resolved.bad),
      };
    })(),
    access: Object.fromEntries(
      ACCESS_IDS.map((id) => [id, { allowed: access.allows(id), shortBy: access.shortBy(id) }])
    ),
    impairment: {
      level: Math.round(impairment.level * 1000) / 1000,
      scent: Math.round(impairment.scent * 1000) / 1000,
      belief: Math.round(impairment.belief * 1000) / 1000,
      reaction: Math.round(impairment.reaction * 1000) / 1000,
      tolerance: Math.round(impairment.tolerance * 1000) / 1000,
      earshot: impairment.earshot,
      ghosting: impairment.ghosting,
      chatter: impairment.chatter,
      tells: impairment.tells(),
      anchorsVisible: impairment.ghosting && sheetRead(),
      sheetRead: sheetRead(),
    },
    // Every state of mind past calm, worst first.
    suspicion: suspicion.watching,
    climb: {
      state: climbing.state,
      // What is in reach right now, as the game itself sees it. A script that
      // asks this is asking the same question the prompt does.
      edge: (() => {
        const found = climbing.busy
          ? climbing.edge
          : climbing.find(player.position, rig.forward, player.position.y);
        return found ? { kind: found.kind, top: found.top, rise: found.rise } : null;
      })(),
      feet: player.position.y,
    },
  }),
};
